import * as Chord from "@tonaljs/chord";
import * as Note from "@tonaljs/note";
import * as Scale from "@tonaljs/scale";
import * as Tone from "tone";
import {
  MIDI_DRUM_CHANNEL,
  addMidiNote,
  addMidiTrack,
  createMidiDocument,
  downloadMidi,
  rememberMidiExportNoticeDismissed,
  shouldShowMidiExportNotice,
} from "../js/music/midi-export.js";
import { playSoundfontPreview } from "../js/music/soundfont-previews.js";
import {
  DEFAULT_CHORD_PLAY_STYLE,
  chordPlayStyleDescription,
  chordPlayStyleLabel,
  chordPlayStyleOptions,
  chordPlayStyles,
  getChordPlayStyle,
  normalizeChordPlayStyle,
  noteIndexForPlayStyle,
  stepToBeatOffset,
} from "../playstyles.js";

const PPQ = 960;
const STORAGE_KEY = "bardsCorner.chordProgressions.v1";
const DEFAULT_SONG_BARS = 16;
const DEFAULT_SONG_TITLE = "Untitled Progression";
const BARS_PER_TIMELINE_ROW = 4;
const DEFAULT_CHORD_RENDER_MODE = "simple";
const PLAYBACK_START_DELAY_SECONDS = 0.08;
const GUITAR_TUNING_MIDI = [40, 45, 50, 55, 59, 64];
const GUITAR_PITCHES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const chromaticRoots = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const nonDiatonicChordColor = "#6f7680";
const majorSemitones = [0, 2, 4, 5, 7, 9, 11];
const minorSemitones = [0, 2, 3, 5, 7, 8, 10];
const dominantSemitones = [0, 2, 4, 5, 7, 9, 10];
const chromaticNoteColor = "#6f7680";
const chromaticNames = ["1", "b2", "2", "b3", "3", "4", "b5", "5", "b6", "6", "b7", "7"];
const DEFAULT_CLIP_STEPS_PER_BAR = 8;
const DEFAULT_TRACK_SUSTAIN = 0.9;
const DEFAULT_TRACK_VOLUME = 1;
const DEFAULT_TRACK_ADSR = {
  attack: 0.01,
  decay: 0.08,
  sustain: 0.85,
  release: 0.8,
};
const TRACK_ADSR_LIMITS = {
  attack: { min: 0, max: 0.75 },
  decay: { min: 0, max: 2 },
  sustain: { min: 0, max: 1 },
  release: { min: 0.01, max: 4 },
};
const DRUM_ACCENT_VELOCITIES = {
  ghost: 43,
  normal: 86,
  accent: 121,
};
const DEFAULT_CLIP_GRID_OCTAVES = 2;
const MAX_CLIP_GRID_OCTAVES = 3;
const CLIP_EDITOR_HANDOFF_KEY = "bardsCorner.clipEditor.handoff.v1";
const CLIP_EDITOR_RETURN_KEY = "bardsCorner.clipEditor.return.v1";
const chordRenderModes = ["simple", "guitar"];
const chordExtensionOptions = [
  { value: "", label: "Major" },
  { value: "m", label: "Minor" },
  { value: "5", label: "5" },
  { value: "6", label: "6" },
  { value: "m6", label: "m6" },
  { value: "6add9", label: "6add9" },
  { value: "7", label: "7" },
  { value: "M7", label: "M7" },
  { value: "m7", label: "m7" },
  { value: "mM7", label: "mM7" },
  { value: "dim", label: "dim" },
  { value: "dim7", label: "dim7" },
  { value: "m7b5", label: "m7b5" },
  { value: "aug", label: "aug" },
  { value: "sus2", label: "sus2" },
  { value: "sus4", label: "sus4" },
  { value: "7sus4", label: "7sus4" },
  { value: "sus24", label: "sus2sus4" },
  { value: "add9", label: "add9" },
  { value: "madd9", label: "madd9" },
  { value: "m69", label: "m6add9" },
  { value: "9", label: "9" },
  { value: "m9", label: "m9" },
  { value: "maj9", label: "M9" },
  { value: "maj9#11", label: "maj9#11" },
  { value: "mM9", label: "mM9" },
  { value: "11", label: "11" },
  { value: "m11", label: "m11" },
  { value: "13", label: "13" },
  { value: "m13", label: "m13" },
  { value: "maj13", label: "M13" },
  { value: "maj13#11", label: "maj13#11" },
  { value: "m7#5", label: "m7#5" },
  { value: "7b5", label: "7b5" },
  { value: "7#5", label: "7#5" },
  { value: "7b9", label: "7b9" },
  { value: "7#9", label: "7#9" },
  { value: "7b5b9", label: "7(b5,b9)" },
  { value: "7b5#9", label: "7(b5,#9)" },
  { value: "7#5b9", label: "7(#5,b9)" },
  { value: "7#5#9", label: "7(#5,#9)" },
  { value: "9b5", label: "9b5" },
  { value: "9#5", label: "9#5" },
  { value: "13#11", label: "13#11" },
  { value: "13b9", label: "13b9" },
  { value: "11b9", label: "11b9" },
  { value: "M7b5", label: "maj7b5" },
  { value: "maj7#5", label: "maj7#5" },
  { value: "b5", label: "b5" },
];
const keys = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];
const ADDITIONAL_FATBOY_SOUND_IDS = [
  "accordion",
  "acoustic_guitar_steel",
  "alto_sax",
  "applause",
  "bagpipe",
  "banjo",
  "baritone_sax",
  "bassoon",
  "blown_bottle",
  "brass_section",
  "bright_acoustic_piano",
  "celesta",
  "cello",
  "choir_aahs",
  "church_organ",
  "clarinet",
  "contrabass",
  "dulcimer",
  "electric_bass_finger",
  "electric_bass_pick",
  "electric_grand_piano",
  "english_horn",
  "fiddle",
  "flute",
  "french_horn",
  "fretless_bass",
  "glockenspiel",
  "harmonica",
  "honkytonk_piano",
  "kalimba",
  "koto",
  "lead_1_square",
  "lead_2_sawtooth",
  "lead_3_calliope",
  "lead_4_chiff",
  "lead_5_charang",
  "lead_6_voice",
  "lead_7_fifths",
  "lead_8_bass__lead",
  "marimba",
  "melodic_tom",
  "music_box",
  "muted_trumpet",
  "oboe",
  "ocarina",
  "orchestral_harp",
  "overdriven_guitar",
  "pad_1_new_age",
  "pad_2_warm",
  "pad_3_polysynth",
  "pad_4_choir",
  "pad_5_bowed",
  "pad_6_metallic",
  "pad_7_halo",
  "pad_8_sweep",
  "pan_flute",
  "percussive_organ",
  "piccolo",
  "recorder",
  "reverse_cymbal",
  "shakuhachi",
  "shamisen",
  "shanai",
  "sitar",
  "slap_bass_1",
  "slap_bass_2",
  "soprano_sax",
  "steel_drums",
  "string_ensemble_1",
  "string_ensemble_2",
  "synth_bass_1",
  "synth_bass_2",
  "synth_brass_1",
  "synth_brass_2",
  "synth_choir",
  "synth_drum",
  "synth_strings_1",
  "synth_strings_2",
  "tango_accordion",
  "tenor_sax",
  "trombone",
  "trumpet",
  "tuba",
  "tubular_bells",
  "viola",
  "violin",
];
const SOUND_LABEL_WORD_OVERRIDES = {
  aahs: "Aahs",
  bass: "Bass",
  sax: "Sax",
};

function soundfontLabelFromId(id) {
  return id
    .split("_")
    .filter(Boolean)
    .map((word) => SOUND_LABEL_WORD_OVERRIDES[word] || `${word[0].toUpperCase()}${word.slice(1)}`)
    .join(" ");
}

const FATBOY_SOUNDFONTS = [
  {
    id: "acoustic_grand_piano",
    label: "Acoustic Grand Piano",
    globalName: "acoustic_grand_piano",
    gain: 0.95,
    defaultVelocity: 0.65,
    singleNoteVelocityScale: 2.8,
    release: 1.2,
    duration: 1.45,
  },
  {
    id: "electric_piano_1",
    label: "Electric Piano 1",
    globalName: "electric_piano_1",
    gain: 0.95,
    defaultVelocity: 0.68,
    singleNoteVelocityScale: 2.6,
    release: 1.6,
    duration: 1.65,
  },
  {
    id: "electric_guitar_clean",
    label: "Electric Guitar Clean",
    globalName: "electric_guitar_clean",
    gain: 0.9,
    defaultVelocity: 0.72,
    singleNoteVelocityScale: 2.2,
    release: 1.1,
    duration: 1.25,
  },
  {
    id: "acoustic_guitar_nylon",
    label: "Acoustic Guitar Nylon",
    globalName: "acoustic_guitar_nylon",
    gain: 0.95,
    defaultVelocity: 0.72,
    singleNoteVelocityScale: 2.2,
    release: 1.2,
    duration: 1.3,
  },
  {
    id: "acoustic_bass",
    label: "Acoustic Bass",
    globalName: "acoustic_bass",
    gain: 0.95,
    defaultVelocity: 0.72,
    singleNoteVelocityScale: 2.1,
    release: 1.0,
    duration: 1.2,
  },
  {
    id: "clavinet",
    label: "Clavinet",
    globalName: "clavinet",
    gain: 0.9,
    defaultVelocity: 0.72,
    singleNoteVelocityScale: 2.1,
    release: 0.7,
    duration: 0.9,
  },
  {
    id: "distortion_guitar",
    label: "Distortion Guitar",
    globalName: "distortion_guitar",
    gain: 0.82,
    defaultVelocity: 0.66,
    singleNoteVelocityScale: 2.0,
    release: 1.2,
    duration: 1.3,
  },
  {
    id: "drawbar_organ",
    label: "Drawbar Organ",
    globalName: "drawbar_organ",
    gain: 0.75,
    defaultVelocity: 0.62,
    singleNoteVelocityScale: 1.8,
    release: 0.8,
    duration: 1.4,
  },
  {
    id: "electric_guitar_jazz",
    label: "Electric Guitar Jazz",
    globalName: "electric_guitar_jazz",
    gain: 0.9,
    defaultVelocity: 0.7,
    singleNoteVelocityScale: 2.2,
    release: 1.1,
    duration: 1.25,
  },
  {
    id: "electric_guitar_muted",
    label: "Electric Guitar Muted",
    globalName: "electric_guitar_muted",
    gain: 0.95,
    defaultVelocity: 0.72,
    singleNoteVelocityScale: 2.1,
    release: 0.6,
    duration: 0.75,
  },
  {
    id: "electric_piano_2",
    label: "Electric Piano 2",
    globalName: "electric_piano_2",
    gain: 0.95,
    defaultVelocity: 0.68,
    singleNoteVelocityScale: 2.6,
    release: 1.6,
    duration: 1.65,
  },
  {
    id: "harpsichord",
    label: "Harpsichord",
    globalName: "harpsichord",
    gain: 0.85,
    defaultVelocity: 0.72,
    singleNoteVelocityScale: 2.0,
    release: 0.7,
    duration: 1.0,
  },
  {
    id: "pizzicato_strings",
    label: "Pizzicato Strings",
    globalName: "pizzicato_strings",
    gain: 0.9,
    defaultVelocity: 0.7,
    singleNoteVelocityScale: 2.1,
    release: 0.8,
    duration: 0.95,
  },
  {
    id: "reed_organ",
    label: "Reed Organ",
    globalName: "reed_organ",
    gain: 0.78,
    defaultVelocity: 0.62,
    singleNoteVelocityScale: 1.8,
    release: 0.8,
    duration: 1.4,
  },
  {
    id: "rock_organ",
    label: "Rock Organ",
    globalName: "rock_organ",
    gain: 0.78,
    defaultVelocity: 0.62,
    singleNoteVelocityScale: 1.8,
    release: 0.8,
    duration: 1.4,
  },
  {
    id: "vibraphone",
    label: "Vibraphone",
    globalName: "vibraphone",
    gain: 0.9,
    defaultVelocity: 0.68,
    singleNoteVelocityScale: 2.2,
    release: 1.8,
    duration: 1.8,
  },
  {
    id: "woodblock",
    label: "Woodblock",
    globalName: "woodblock",
    gain: 0.9,
    defaultVelocity: 0.72,
    singleNoteVelocityScale: 2.0,
    release: 0.4,
    duration: 0.45,
  },
  {
    id: "xylophone",
    label: "Xylophone",
    globalName: "xylophone",
    gain: 0.9,
    defaultVelocity: 0.7,
    singleNoteVelocityScale: 2.1,
    release: 0.7,
    duration: 0.9,
  },
  ...ADDITIONAL_FATBOY_SOUND_IDS.map((id) => ({
    id,
    label: soundfontLabelFromId(id),
    globalName: id,
    gain: 0.9,
    defaultVelocity: 0.68,
    singleNoteVelocityScale: 2.2,
    release: 1.2,
    duration: 1.3,
  })),
  {
    id: "drums",
    label: "Drums",
    globalName: "drums",
    gain: 0.9,
    defaultVelocity: 0.8,
    singleNoteVelocityScale: 1.0,
    release: 0.2,
    duration: 0.3,
  },
  {
    id: "casio_vl_1",
    label: "Casio VL-1",
    globalName: "drums_casio_vl_1",
    gain: 0.9,
    defaultVelocity: 0.8,
    singleNoteVelocityScale: 1.0,
    release: 0.2,
    duration: 0.3,
  },
  {
    id: "linn_linndrum",
    label: "LinnDrum",
    globalName: "drums_linn_linndrum",
    gain: 0.9,
    defaultVelocity: 0.8,
    singleNoteVelocityScale: 1.0,
    release: 0.2,
    duration: 0.3,
  },
  {
    id: "roland_606",
    label: "Roland TR-606",
    globalName: "drums_roland_606",
    gain: 0.9,
    defaultVelocity: 0.8,
    singleNoteVelocityScale: 1.0,
    release: 0.2,
    duration: 0.3,
  },
  {
    id: "roland_707",
    label: "Roland TR-707",
    globalName: "drums_roland_707",
    gain: 0.9,
    defaultVelocity: 0.8,
    singleNoteVelocityScale: 1.0,
    release: 0.2,
    duration: 0.3,
  },
  {
    id: "roland_808",
    label: "Roland TR-808",
    globalName: "drums_roland_808",
    gain: 0.9,
    defaultVelocity: 0.8,
    singleNoteVelocityScale: 1.0,
    release: 0.2,
    duration: 0.3,
  },
  {
    id: "roland_909",
    label: "Roland TR-909",
    globalName: "drums_roland_909",
    gain: 0.9,
    defaultVelocity: 0.8,
    singleNoteVelocityScale: 1.0,
    release: 0.2,
    duration: 0.3,
  },
];
const DRUM_SOUNDFONTS = FATBOY_SOUNDFONTS.filter((config) => (
  config.id === "drums"
  || config.id === "casio_vl_1"
  || config.id === "linn_linndrum"
  || config.id.startsWith("roland_")
));
const playableSoundfonts = FATBOY_SOUNDFONTS.filter((config) => !DRUM_SOUNDFONTS.includes(config));
const programs = playableSoundfonts.map((config) => ({
  value: config.id,
  label: config.label,
  instrumentName: config.id,
}));
const LEGACY_PROGRAM_VALUE_MAP = {
  0: "piano",
  4: "electric piano",
  24: "nylon guitar",
  25: "steel guitar",
  32: "acoustic bass",
  40: "violin",
  48: "strings",
  52: "choir",
  73: "flute",
  80: "square lead",
  89: "warm pad",
};
const LEGACY_SOUNDFONT_ALIASES = {
  piano: "acoustic_grand_piano",
  "electric piano": "electric_piano_1",
  "nylon guitar": "acoustic_guitar_nylon",
  "steel guitar": "electric_guitar_clean",
  "acoustic bass": "acoustic_bass",
  violin: "pizzicato_strings",
  strings: "pizzicato_strings",
  choir: "reed_organ",
  flute: "vibraphone",
  "square lead": "xylophone",
  "warm pad": "drawbar_organ",
  drums: "drums",
  voice: "reed_organ",
};
const soundfontScriptPromises = new Map();
const stockPatterns = {
  melody: [],
  bass: [],
  drums: [],
};
const DRUM_PREVIEW_TOP_MIDI = 35;
const DRUM_PREVIEW_BOTTOM_MIDI = 81;
const chordColors = ["#c73528", "#c7771f", "#a3a226", "#4fad27", "#2d92a8", "#8c22b4", "#b821b8"];
const trackColors = {
  chords: "#c73528",
  melody: "#2d92a8",
  bass: "#4fad27",
  drums: "#c7771f",
  lyrics: "#8c22b4",
};
const hiddenTimelineTrackTypes = new Set(["lyrics"]);
const state = {
  song: createDefaultSong(),
  selectedEventId: null,
  selectedEventIds: [],
  serverSongId: null,
  serverSongSignature: null,
  serverSongLocked: false,
  loadedOriginBardName: "",
  savingSong: false,
  loadingSongs: false,
  loadingSaveSongs: false,
  loadModalSongs: [],
  loadModalMode: "owned",
  loadModalSortKey: "updatedAt",
  loadModalSortDirection: "desc",
  saveModalSongs: [],
  saveModalSelectedSongId: null,
  saveModalDuplicateSongId: null,
  saveModalSortKey: "updatedAt",
  saveModalSortDirection: "desc",
  clipSearch: {
    mode: "my",
    trackType: "melody",
    loading: false,
    clips: [],
    selectedClipId: null,
  },
  stockPatternsLoading: false,
  stockPatternsError: "",
  selectionAnchorEventId: null,
  lastSelectedChordId: null,
  lastInsertedClipId: null,
  previewClip: null,
  activePatternTrackId: null,
  activePanelTrackType: "chords",
  collapsedTrackIds: [],
  defaultChordPlayStyle: DEFAULT_CHORD_PLAY_STYLE,
  chordRenderMode: DEFAULT_CHORD_RENDER_MODE,
  playheadTick: 0,
  playbackPlayheadTick: null,
  snapTicks: 480,
  playing: false,
  preparingPlayback: false,
  loop: true,
  loopRegion: null,
  loopSelectionAnchorTick: null,
  loopDrag: null,
  selectedProgram: programForSoundfontId(defaultInstrumentForTrackType("chords")),
  editingTrackId: null,
  drag: null,
  clipboard: null,
  ignoreNextClipClick: false,
  scheduledIds: [],
  noteTimerIds: new Set(),
  auditionRequestId: 0,
  pendingTrackUnmutes: new Map(),
  playheadTimer: null,
  playheadAnimationFrame: null,
  lastPlaybackScrollMeasure: null,
  audioLoadPromise: null,
  patternGridResize: null,
  patternPreviewPlaying: false,
  patternPreviewStartedAt: 0,
  patternPreviewTrackId: null,
  patternPreviewEventId: null,
  patternPreviewAnimationFrame: null,
  saveTimer: null,
  history: {
    undoStack: [],
    redoStack: [],
    lastSongSnapshot: null,
    maxEntries: 100,
    restoring: false,
  },
  tapTempoTimes: [],
  audio: {
    ready: false,
    loading: false,
    error: "",
    samplers: new Map(),
  },
};

const els = {
  playButton: document.getElementById("playButton"),
  stopButton: document.getElementById("stopButton"),
  loopButton: document.getElementById("loopButton"),
  songTitleInput: document.getElementById("songTitleInput"),
  tempoButton: document.getElementById("tempoButton"),
  keyButton: document.getElementById("keyButton"),
  meterSelect: document.getElementById("meterSelect"),
  snapSelect: document.getElementById("snapSelect"),
  timelineScroller: document.getElementById("timelineScroller"),
  timeline: document.getElementById("timeline"),
  barRuler: document.getElementById("barRuler"),
  playhead: document.getElementById("playhead"),
  trackRows: document.getElementById("trackRows"),
  chordPalette: document.getElementById("chordPalette"),
  transportSettingsButton: document.getElementById("transportSettingsButton"),
  fileNewButton: document.getElementById("fileNewButton"),
  fileLoadButton: document.getElementById("fileLoadButton"),
  fileSaveButton: document.getElementById("fileSaveButton"),
  fileSaveAsButton: document.getElementById("fileSaveAsButton"),
  fileShareButton: document.getElementById("fileShareButton"),
  fileExportMidiButton: document.getElementById("fileExportMidiButton"),
  unsavedChangesIndicator: document.getElementById("unsavedChangesIndicator"),
  originBardIndicator: document.getElementById("originBardIndicator"),
  loadSongModal: document.getElementById("loadSongModal"),
  loadSongMessage: document.getElementById("loadSongMessage"),
  loadSongList: document.getElementById("loadSongList"),
  loadSongOwnedTab: document.getElementById("loadSongOwnedTab"),
  loadSongSharedTab: document.getElementById("loadSongSharedTab"),
  loadSongOwnerSortButton: document.getElementById("loadSongOwnerSortButton"),
  saveSongModal: document.getElementById("saveSongModal"),
  saveSongNameInput: document.getElementById("saveSongNameInput"),
  saveSongSharedCheckbox: document.getElementById("saveSongSharedCheckbox"),
  saveSongSharedInfo: document.getElementById("saveSongSharedInfo"),
  saveSongDuplicateMessage: document.getElementById("saveSongDuplicateMessage"),
  saveSongMessage: document.getElementById("saveSongMessage"),
  saveExistingSongList: document.getElementById("saveExistingSongList"),
  confirmSaveSongButton: document.getElementById("confirmSaveSongButton"),
  shareModal: document.getElementById("shareModal"),
  shareLinkHelp: document.getElementById("shareLinkHelp"),
  shareLinkInput: document.getElementById("shareLinkInput"),
  shareLinkMessage: document.getElementById("shareLinkMessage"),
  copyShareLinkButton: document.getElementById("copyShareLinkButton"),
  midiExportModal: document.getElementById("midiExportModal"),
  confirmMidiExportButton: document.getElementById("confirmMidiExportButton"),
  midiExportNeverShowButton: document.getElementById("midiExportNeverShowButton"),
  clipSearchModal: document.getElementById("clipSearchModal"),
  clipSearchTitle: document.getElementById("clipSearchTitle"),
  clipSearchMyButton: document.getElementById("clipSearchMyButton"),
  clipSearchSharedButton: document.getElementById("clipSearchSharedButton"),
  clipSearchFilters: document.getElementById("clipSearchFilters"),
  clipSearchBardInput: document.getElementById("clipSearchBardInput"),
  clipSearchTitleInput: document.getElementById("clipSearchTitleInput"),
  clipSearchDurationSelect: document.getElementById("clipSearchDurationSelect"),
  clipSearchMessage: document.getElementById("clipSearchMessage"),
  clipSearchList: document.getElementById("clipSearchList"),
  confirmClipSearchButton: document.getElementById("confirmClipSearchButton"),
  tempoModal: document.getElementById("tempoModal"),
  tempoDisplay: document.getElementById("tempoDisplay"),
  tapTempoButton: document.getElementById("tapTempoButton"),
  transposeButton: document.getElementById("transposeButton"),
  transposeModal: document.getElementById("transposeModal"),
  keyModal: document.getElementById("keyModal"),
  keyDisplay: document.getElementById("keyDisplay"),
  transportModal: document.getElementById("transportModal"),
  chordRenderModeSelect: document.getElementById("chordRenderModeSelect"),
  chordClipModal: document.getElementById("chordClipModal"),
  chordExtensionSelect: document.getElementById("chordExtensionSelect"),
  chordPlayStyleSelect: document.getElementById("chordPlayStyleSelect"),
  chordPlayStylePreviewButton: document.getElementById("chordPlayStylePreviewButton"),
  patternClipModal: document.getElementById("patternClipModal"),
  patternClipModalTitle: document.getElementById("patternClipModalTitle"),
  patternPlayButton: document.getElementById("patternPlayButton"),
  patternOpenFullEditorButton: document.getElementById("patternOpenFullEditorButton"),
  patternFullEditorTextLink: document.getElementById("patternFullEditorTextLink"),
  patternOctaveDownButton: document.getElementById("patternOctaveDownButton"),
  patternOctaveUpButton: document.getElementById("patternOctaveUpButton"),
  patternOctaveDisplay: document.getElementById("patternOctaveDisplay"),
  patternClipGridSettings: document.getElementById("patternClipGridSettings"),
  patternClipGrid: document.getElementById("patternClipGrid"),
  patternGridOctaves1Radio: document.getElementById("patternGridOctaves1Radio"),
  patternGridOctaves2Radio: document.getElementById("patternGridOctaves2Radio"),
  patternGridOctaves3Radio: document.getElementById("patternGridOctaves3Radio"),
  patternGridResolution4Radio: document.getElementById("patternGridResolution4Radio"),
  patternGridResolution8Radio: document.getElementById("patternGridResolution8Radio"),
  patternGridResolution12Radio: document.getElementById("patternGridResolution12Radio"),
  patternGridResolution16Radio: document.getElementById("patternGridResolution16Radio"),
  newChordSettingsModal: document.getElementById("newChordSettingsModal"),
  defaultChordPlayStyleSelect: document.getElementById("defaultChordPlayStyleSelect"),
  defaultChordPlayStylePreviewButton: document.getElementById("defaultChordPlayStylePreviewButton"),
  trackSoundModal: document.getElementById("trackSoundModal"),
  trackSoundTrackName: document.getElementById("trackSoundTrackName"),
  trackSoundSelect: document.getElementById("trackSoundSelect"),
  trackChordStyleField: document.getElementById("trackChordStyleField"),
  trackVolumeInput: document.getElementById("trackVolumeInput"),
  trackAttackInput: document.getElementById("trackAttackInput"),
  trackDecayInput: document.getElementById("trackDecayInput"),
  trackSustainField: document.getElementById("trackSustainField"),
  trackSustainInput: document.getElementById("trackSustainInput"),
  trackReleaseInput: document.getElementById("trackReleaseInput"),
  trackOctaveField: document.getElementById("trackOctaveField"),
  trackOctaveDownButton: document.getElementById("trackOctaveDownButton"),
  trackOctaveUpButton: document.getElementById("trackOctaveUpButton"),
  trackOctaveDisplay: document.getElementById("trackOctaveDisplay"),
  saveTrackSoundButton: document.getElementById("saveTrackSoundButton"),
};

function createDefaultSong() {
  const now = new Date().toISOString();
  const songId = makeId("song");
  const sectionId = makeId("sec");
  const sectionDuration = barsToTicks(DEFAULT_SONG_BARS, 4, 4);

  return {
    schema: "ChordSongJSON",
    schemaVersion: "0.1.0",
    id: songId,
    title: DEFAULT_SONG_TITLE,
    createdAt: now,
    updatedAt: now,
    ppq: PPQ,
    metadata: {
      composer: "",
      lyricist: "",
      notes: "",
    },
    timeline: {
      defaultTempoBpm: 120,
      tempoEvents: [
        { tick: 0, bpm: 120 },
      ],
      timeSignatureEvents: [
        { tick: 0, numerator: 4, denominator: 4 },
      ],
      keySignatureEvents: [
        { tick: 0, key: "C", mode: "major" },
      ],
    },
    form: {
      sections: [
        { id: sectionId, name: "Verse 1", startTick: 0, durationTicks: sectionDuration },
      ],
      playOrder: [
        { sectionId },
      ],
    },
    tracks: [
      makeTrack("track_chords", "Chords", "chords", defaultInstrumentForTrackType("chords")),
      makeTrack("track_melody", "Melody", "melody", "acoustic_grand_piano"),
      makeTrack("track_bass", "Bass", "bass", "acoustic_bass"),
      makeTrack("track_drums", "Drums", "drums", "drums", 10),
      makeTrack("track_lyrics", "Lyrics", "lyrics", "voice"),
    ],
    markers: [
      { id: makeId("marker"), tick: 0, name: "Verse 1", markerType: "section" },
    ],
  };
}

function makeTrack(id, name, type, instrumentName, midiChannel) {
  return {
    id,
    name,
    type,
    instrumentName,
    ...(midiChannel ? { midiChannel } : {}),
    muted: false,
    solo: false,
    volume: DEFAULT_TRACK_VOLUME,
    pan: 0,
    sustain: DEFAULT_TRACK_SUSTAIN,
    adsr: { ...DEFAULT_TRACK_ADSR },
    octaveShift: 0,
    source: {
      originalTempoBpm: 120,
      originalPpq: PPQ,
      importedFrom: "user",
    },
    events: [],
  };
}

function makeId(prefix) {
  if (crypto.randomUUID) {
    return `${prefix}_${crypto.randomUUID().slice(0, 8)}`;
  }

  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function barsToTicks(bars, numerator, denominator) {
  return bars * numerator * ticksPerBeat(denominator);
}

function ticksPerBeat(denominator = getMeter().denominator) {
  return PPQ * (4 / denominator);
}

function getMeter() {
  return state.song.timeline.timeSignatureEvents[0] || { tick: 0, numerator: 4, denominator: 4 };
}

function getKeySignature() {
  return state.song.timeline.keySignatureEvents?.[0] || { tick: 0, key: "C", mode: "major" };
}

function getTempo() {
  return state.song.timeline.tempoEvents[0]?.bpm || state.song.timeline.defaultTempoBpm || 120;
}

function getChordTrack() {
  return state.song.tracks.find((track) => track.type === "chords");
}

function getSelectedChordEvent() {
  return getChordTrack()?.events.find((event) => event.id === state.selectedEventId) || null;
}

function selectedEventIdSet() {
  const ids = new Set(state.selectedEventIds);
  if (state.selectedEventId) {
    ids.add(state.selectedEventId);
  }
  return ids;
}

function getSelectedTimelineEvents() {
  const ids = selectedEventIdSet();
  if (!ids.size) {
    return [];
  }

  return state.song.tracks.flatMap((track) => (
    track.events
      .filter((event) => ids.has(event.id))
      .map((event) => ({ track, event }))
  )).sort((a, b) => a.event.tick - b.event.tick);
}

function getSelectedChordEvents() {
  return getSelectedTimelineEvents().filter(({ event }) => event.eventType === "chord");
}

function getSelectedEventsForTrackType(trackType) {
  return getSelectedTimelineEvents().filter(({ track, event }) => (
    track.type === trackType && stockClipTypes().includes(event.eventType)
  ));
}

function getEditableTimelineEventsInOrder() {
  const trackOrder = new Map(state.song.tracks.map((track, index) => [track.id, index]));
  return state.song.tracks.flatMap((track) => (
    track.events
      .filter(isEditableTimelineClip)
      .map((event) => ({ track, event }))
  )).sort((a, b) => (
    ((trackOrder.get(a.track.id) || 0) - (trackOrder.get(b.track.id) || 0))
    || (a.event.tick - b.event.tick)
    || ((a.event.durationTicks || 0) - (b.event.durationTicks || 0))
  ));
}

function findEventById(eventId) {
  for (const track of state.song.tracks) {
    const event = track.events.find((item) => item.id === eventId);
    if (event) {
      return { track, event };
    }
  }

  return null;
}

function getSelectedTimelineEvent() {
  return findEventById(state.selectedEventId);
}

function clearSelection() {
  state.selectedEventId = null;
  state.selectedEventIds = [];
  state.selectionAnchorEventId = null;
}

function totalTicks() {
  const meter = getMeter();
  return Math.max(sectionEndTicks(), eventEndTicks(), barsToTicks(DEFAULT_SONG_BARS, meter.numerator, meter.denominator));
}

function totalBars() {
  const meter = getMeter();
  return Math.max(1, Math.ceil(totalTicks() / ticksPerBar(meter)));
}

function ticksPerBar(meter = getMeter()) {
  return meter.numerator * ticksPerBeat(meter.denominator);
}

function ticksPerTimelineRow() {
  return ticksPerBar() * BARS_PER_TIMELINE_ROW;
}

function timelineRowCount() {
  return Math.max(1, Math.ceil(totalTicks() / ticksPerTimelineRow()));
}

function sectionEndTicks() {
  return Math.max(0, ...(state.song.form?.sections || []).map((section) => section.startTick + section.durationTicks));
}

function eventEndTicks() {
  return Math.max(0, ...(state.song.tracks || []).flatMap((track) => (
    (track.events || []).map((event) => (Number(event.tick) || 0) + (Number(event.durationTicks) || 0))
  )));
}

function ensureSongDuration(requiredTicks) {
  const meter = getMeter();
  const nextEndTick = Math.ceil(Math.max(requiredTicks, 0) / ticksPerBar(meter)) * ticksPerBar(meter);
  if (nextEndTick <= sectionEndTicks()) {
    return;
  }

  state.song.form = state.song.form || {};
  state.song.form.sections = Array.isArray(state.song.form.sections) ? state.song.form.sections : [];

  let section = state.song.form.sections
    .slice()
    .sort((a, b) => (a.startTick + a.durationTicks) - (b.startTick + b.durationTicks))
    .at(-1);

  if (!section) {
    section = { id: makeId("sec"), name: "Verse 1", startTick: 0, durationTicks: 0 };
    state.song.form.sections.push(section);
  }

  section.durationTicks = Math.max(section.durationTicks || 0, nextEndTick - section.startTick);
  state.song.markers = state.song.markers || [];
  if (!state.song.markers.length) {
    state.song.markers.push({ id: makeId("marker"), tick: section.startTick, name: section.name, markerType: "section" });
  }
}

function snapTick(tick) {
  return Math.max(0, Math.min(totalTicks(), snapGrid(tick)));
}

function snapGrid(tick) {
  return Math.round(tick / state.snapTicks) * state.snapTicks;
}

function snapDelta(ticks) {
  return Math.round(ticks / state.snapTicks) * state.snapTicks;
}

function barStartTick(tick) {
  return Math.floor(Math.max(0, tick) / ticksPerBar()) * ticksPerBar();
}

function tickToBeats(tick) {
  return tick / PPQ;
}

function beatsToTick(beats) {
  return Math.round(beats * PPQ);
}

function beatWidth() {
  return Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--beat-width"));
}

function tickToPx(tick) {
  return tickToBeats(tick) * beatWidth();
}

function pxToTick(px) {
  return beatsToTick(px / beatWidth());
}

function songHistorySnapshot() {
  return JSON.stringify(state.song);
}

function initializeSongHistory() {
  state.history.undoStack = [];
  state.history.redoStack = [];
  state.history.lastSongSnapshot = songHistorySnapshot();
}

function pushUndoSnapshot(snapshot) {
  if (!snapshot || state.history.undoStack.at(-1) === snapshot) {
    return;
  }

  state.history.undoStack.push(snapshot);
  if (state.history.undoStack.length > state.history.maxEntries) {
    state.history.undoStack.shift();
  }
}

function commitSongHistoryChange(previousSnapshot, currentSnapshot = songHistorySnapshot()) {
  if (!previousSnapshot || previousSnapshot === currentSnapshot) {
    state.history.lastSongSnapshot = currentSnapshot;
    return false;
  }

  pushUndoSnapshot(previousSnapshot);
  state.history.redoStack = [];
  state.history.lastSongSnapshot = currentSnapshot;
  return true;
}

function recordSongHistoryChange(currentSnapshot = songHistorySnapshot()) {
  const previousSnapshot = state.history.lastSongSnapshot || currentSnapshot;
  commitSongHistoryChange(previousSnapshot, currentSnapshot);
}

function restoreSongSnapshot(snapshot) {
  try {
    state.history.restoring = true;
    state.song = normalizeSong(JSON.parse(snapshot));
    state.history.lastSongSnapshot = songHistorySnapshot();
    clearPreviewClip();
    clearSelection();
    state.drag = null;
    state.loopDrag = null;
    state.loopRegion = normalizeLoopRegion(state.loopRegion);
    state.loopSelectionAnchorTick = null;
    state.playheadTick = Math.min(totalTicks(), state.playheadTick);
    state.selectedProgram = programFromChordTrack() || state.selectedProgram;
    scheduleAppStateSave();
    render();
    return true;
  } catch {
    return false;
  } finally {
    state.history.restoring = false;
  }
}

function undoSongChange() {
  const snapshot = state.history.undoStack.pop();
  if (!snapshot) {
    return false;
  }

  state.history.redoStack.push(songHistorySnapshot());
  return restoreSongSnapshot(snapshot);
}

function redoSongChange() {
  const snapshot = state.history.redoStack.pop();
  if (!snapshot) {
    return false;
  }

  pushUndoSnapshot(songHistorySnapshot());
  return restoreSongSnapshot(snapshot);
}

function touchSong({ history = true, updateBaseline = true } = {}) {
  state.song.updatedAt = new Date().toISOString();
  if (!state.history.restoring) {
    const currentSnapshot = songHistorySnapshot();
    if (history) {
      recordSongHistoryChange(currentSnapshot);
    } else if (updateBaseline) {
      state.history.lastSongSnapshot = currentSnapshot;
    }
  }
  scheduleAppStateSave();
}

function loadStoredAppState() {
  const stored = readStoredAppState();
  if (!stored) {
    return;
  }

  const settings = stored.settings || {};
  if (stored.song) {
    state.song = normalizeSong(stored.song);
  }

  state.serverSongId = settings.serverSongId ? String(settings.serverSongId) : null;
  state.serverSongSignature = settings.serverSongSignature || null;
  state.serverSongLocked = Boolean(settings.serverSongLocked);
  state.snapTicks = validSnapTicks(settings.snapTicks) ? Number(settings.snapTicks) : state.snapTicks;
  state.loop = typeof settings.loop === "boolean" ? settings.loop : state.loop;
  state.loopRegion = normalizeLoopRegion(settings.loopRegion);
  state.loopSelectionAnchorTick = null;
  state.selectedProgram = programFromStoredValue(settings.selectedProgramValue)
    || programFromChordTrack()
    || state.selectedProgram;
  state.playheadTick = Math.min(totalTicks(), Math.max(0, Number(settings.playheadTick) || 0));
  state.selectedEventId = findEventById(settings.selectedEventId)
    ? settings.selectedEventId
    : null;
  state.selectedEventIds = state.selectedEventId ? [state.selectedEventId] : [];
  state.selectionAnchorEventId = state.selectedEventId;
  state.lastSelectedChordId = getChordTrack()?.events.some((event) => event.id === settings.lastSelectedChordId)
    ? settings.lastSelectedChordId
    : null;
  state.collapsedTrackIds = Array.isArray(settings.collapsedTrackIds)
    ? settings.collapsedTrackIds.filter((trackId) => state.song.tracks.some((track) => track.id === trackId && track.type !== "chords"))
    : [];
  state.defaultChordPlayStyle = normalizeChordPlayStyle(settings.defaultChordPlayStyle);
  state.chordRenderMode = normalizeChordRenderMode(settings.chordRenderMode);
}

function readStoredAppState() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function scheduleAppStateSave() {
  if (state.saveTimer) {
    window.clearTimeout(state.saveTimer);
  }
  state.saveTimer = window.setTimeout(saveAppState, 150);
}

function saveAppState() {
  if (state.saveTimer) {
    window.clearTimeout(state.saveTimer);
    state.saveTimer = null;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: 1,
      savedAt: new Date().toISOString(),
      song: state.song,
      settings: {
        snapTicks: state.snapTicks,
        loop: state.loop,
        loopRegion: state.loopRegion,
        selectedProgramValue: state.selectedProgram.value,
        playheadTick: Math.min(totalTicks(), state.playheadTick),
        selectedEventId: state.selectedEventId,
        lastSelectedChordId: state.lastSelectedChordId,
        collapsedTrackIds: state.collapsedTrackIds,
        defaultChordPlayStyle: state.defaultChordPlayStyle,
        chordRenderMode: state.chordRenderMode,
        serverSongId: state.serverSongId,
        serverSongSignature: state.serverSongSignature,
        serverSongLocked: state.serverSongLocked,
      },
    }));
  } catch {
    // Storage can be unavailable or full; the editor should keep working.
  }
}

async function apiRequest(path, options = {}) {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });
  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(body.error || "The server could not complete that request.");
    error.status = response.status;
    throw error;
  }

  return body;
}

async function loadStockPatterns() {
  state.stockPatternsLoading = true;
  state.stockPatternsError = "";
  renderToolPanel();

  try {
    const loadedPatterns = await Promise.all(["melody", "bass", "drums"].map(async (trackType) => {
      const params = new window.URLSearchParams({
        mode: "stock",
        type: clipSearchApiType(trackType),
        includeClip: "1",
      });
      const body = await apiRequest(`/api/clips?${params}`);
      return [trackType, (body.clips || []).map((serverClip) => stockPatternFromServerClip(trackType, serverClip)).filter(Boolean)];
    }));

    loadedPatterns.forEach(([trackType, patterns]) => {
      stockPatterns[trackType] = patterns;
    });
  } catch (error) {
    state.stockPatternsError = error.message || "Could not load stock clips.";
  } finally {
    state.stockPatternsLoading = false;
    renderToolPanel();
  }
}

function stockPatternFromServerClip(trackType, serverClip) {
  const clip = normalizeSavedClip(serverClip?.clip, serverClip);
  if (!clip) {
    return null;
  }

  return {
    id: String(serverClip.id || clip.id),
    name: clip.title || serverClip.title || "Untitled Clip",
    durationTicks: clipDurationTicks(clip),
    octaveShift: trackType === "drums" ? 0 : octaveForSavedClip(trackType, clip),
    serverClipId: serverClip?.id ? String(serverClip.id) : "",
    bars: validClipBars(clip?.bars),
    stepsPerBar: validClipStepsPerBar(clip?.stepsPerBar, DEFAULT_CLIP_STEPS_PER_BAR),
    gridOctaves: validClipGridOctaves(clip?.gridOctaves),
    events: savedClipEventsForTrackType(trackType, clip).map(tickedEventToStockPatternEvent),
  };
}

function tickedEventToStockPatternEvent(event) {
  return {
    ...event,
    offsetBeats: event.offsetBeats ?? tickToBeats(event.offsetTicks || 0),
    durationBeats: event.durationBeats ?? tickToBeats(event.durationTicks || state.snapTicks),
  };
}

async function saveSongToServer() {
  if (!state.serverSongId) {
    await showSaveSongModal();
    return;
  }

  if (state.serverSongLocked) {
    await showSaveSongModal();
    return;
  }

  await saveSongToServerWithOptions({
    title: state.song.title,
    shared: state.song.shared,
    serverSongId: state.serverSongId,
  });
}

async function showSaveSongModal() {
  const modal = window.bootstrap.Modal.getOrCreateInstance(els.saveSongModal);
  state.saveModalSelectedSongId = state.serverSongLocked ? null : state.serverSongId;
  const currentTitle = state.song.title || DEFAULT_SONG_TITLE;
  els.saveSongNameInput.value = state.serverSongLocked ? `${currentTitle} Copy`.slice(0, 160) : currentTitle;
  els.saveSongSharedCheckbox.checked = Boolean(state.song.shared);
  renderSaveSongSharedInfo();
  state.loadingSaveSongs = true;
  renderSaveSongList([], "Loading saved progressions...");
  renderSaveSongDuplicateState();
  renderSaveSongModalState();
  modal.show();

  try {
    const body = await apiRequest("/api/songs?mode=my&includeSong=1");
    state.saveModalSongs = body.songs || [];
    renderSaveSongList(state.saveModalSongs);
    renderSaveSongDuplicateState();
  } catch (error) {
    state.saveModalSongs = [];
    renderSaveSongList([], error.message || "Could not load saved progressions.");
  } finally {
    state.loadingSaveSongs = false;
    renderSaveSongModalState();
  }
}

async function saveSongFromModal() {
  const duplicateSong = currentSaveDuplicateSong();
  let serverSongId = state.saveModalSelectedSongId;

  if (duplicateSong) {
    const overwrite = window.confirm(`Are you sure you want to save over "${duplicateSong.title || DEFAULT_SONG_TITLE}"?`);
    if (!overwrite) {
      return;
    }
    serverSongId = duplicateSong.id;
  }

  await saveSongToServerWithOptions({
    title: els.saveSongNameInput.value,
    shared: els.saveSongSharedCheckbox.checked,
    serverSongId,
  });
}

async function saveSongToServerWithOptions(options = {}) {
  if (state.savingSong) {
    return;
  }

  state.savingSong = true;
  renderFileMenuState();

  try {
    const selectedSong = options.serverSongId
      ? state.saveModalSongs.find((song) => song.id === options.serverSongId)
      : null;
    if (selectedSong?.songId) {
      state.song.id = selectedSong.songId;
    } else if (!options.serverSongId && state.serverSongId) {
      state.song.id = makeId("song");
    }
    state.song.title = String(options.title ?? state.song.title).trim().slice(0, 160) || DEFAULT_SONG_TITLE;
    state.song.shared = Boolean(options.shared ?? state.song.shared);
    const body = await apiRequest("/api/songs", {
      method: "POST",
      body: JSON.stringify({
        songId: state.song.id,
        title: state.song.title,
        shared: state.song.shared,
        song: state.song,
      }),
    });

    state.serverSongId = body.song?.id ? String(body.song.id) : state.serverSongId;
    state.serverSongLocked = Boolean(body.song?.locked);
    state.loadedOriginBardName = "";
    if (body.song?.songId) {
      state.song.id = body.song.songId;
    }
    state.serverSongSignature = songServerSignature();
    scheduleAppStateSave();
    render();
    window.bootstrap.Modal.getOrCreateInstance(els.saveSongModal).hide();
  } catch (error) {
    window.alert(error.message || "Save failed.");
  } finally {
    state.savingSong = false;
    renderFileMenuState();
  }
}

function renderSaveSongList(songs, message = "") {
  const sortedSongs = sortSongsForList(songs, state.saveModalSortKey, state.saveModalSortDirection);
  els.saveSongMessage.textContent = message || (sortedSongs.length ? "" : "No saved progressions yet.");
  els.saveExistingSongList.replaceChildren(...sortedSongs.map((song) => {
    const active = song.id === state.saveModalSelectedSongId;
    const button = document.createElement("button");
    button.className = `list-group-item list-group-item-action bg-dark text-light border-secondary song-list-row has-preview${active ? " active" : ""}`;
    button.type = "button";
    button.dataset.songId = song.id;
    button.innerHTML = `
      <span class="song-list-preview-cell">${renderSongMiniPreview(song)}</span>
      <span class="song-list-title-wrap">
        <strong class="song-list-cell song-list-title-cell">${escapeHtml(song.title || DEFAULT_SONG_TITLE)}</strong>
        ${renderSongListStats(song)}
      </span>
      <span class="song-list-cell text-secondary">${escapeHtml(formatSongDate(song.updatedAt))}</span>
      <span class="song-list-cell text-secondary">${renderSongAccess(song)}</span>
    `;
    button.addEventListener("click", () => selectSaveModalSong(song.id));
    return button;
  }));
  renderSaveSongSortHeaders();
}

function selectSaveModalSong(songId) {
  const song = state.saveModalSongs.find((candidate) => candidate.id === songId);
  if (!song) {
    return;
  }

  state.saveModalSelectedSongId = song.id;
  els.saveSongNameInput.value = song.title || DEFAULT_SONG_TITLE;
  els.saveSongSharedCheckbox.checked = Boolean(song.shared);
  renderSaveSongSharedInfo();
  renderSaveSongList(state.saveModalSongs);
  renderSaveSongDuplicateState();
}

function clearSaveSongModalSelectionIfEdited() {
  const selectedSong = state.saveModalSongs.find((song) => song.id === state.saveModalSelectedSongId);
  if (!selectedSong) {
    return;
  }

  if (normalizeComparableTitle(selectedSong.title || DEFAULT_SONG_TITLE) !== normalizeComparableTitle(els.saveSongNameInput.value || DEFAULT_SONG_TITLE)) {
    state.saveModalSelectedSongId = null;
    renderSaveSongList(state.saveModalSongs);
  }
}

function renderSaveSongDuplicateState() {
  const duplicateSong = currentSaveDuplicateSong();
  state.saveModalDuplicateSongId = duplicateSong?.id || null;
  els.saveSongNameInput.classList.toggle("is-duplicate", Boolean(duplicateSong));
  els.saveSongDuplicateMessage.textContent = duplicateSong ? "A progression with this name already exists." : "";
}

function renderSaveSongSharedInfo() {
  els.saveSongSharedInfo.classList.toggle("d-none", !els.saveSongSharedCheckbox.checked);
}

function renderSaveSongSortHeaders() {
  document.querySelectorAll("[data-save-song-sort]").forEach((button) => {
    const active = button.dataset.saveSongSort === state.saveModalSortKey;
    button.classList.toggle("is-active", active);
    button.setAttribute("aria-sort", active ? (state.saveModalSortDirection === "asc" ? "ascending" : "descending") : "none");
  });
}

function currentSaveDuplicateSong() {
  const title = normalizeComparableTitle(els.saveSongNameInput.value);
  if (!title) {
    return null;
  }

  return state.saveModalSongs.find((song) => normalizeComparableTitle(song.title) === title) || null;
}

function renderSaveSongModalState() {
  els.confirmSaveSongButton.disabled = state.savingSong || state.loadingSaveSongs;
  els.confirmSaveSongButton.textContent = state.savingSong ? "Saving..." : "OK";
}

async function showLoadSongModal() {
  const modal = window.bootstrap.Modal.getOrCreateInstance(els.loadSongModal);
  state.loadingSongs = true;
  renderLoadSongList([], "Loading saved songs...");
  modal.show();

  try {
    const [ownedBody, sharedBody] = await Promise.all([
      apiRequest("/api/songs?mode=my&includeSong=1"),
      apiRequest("/api/songs?mode=shared&includeSong=1"),
    ]);
    state.loadModalSongs = [
      ...(ownedBody.songs || []),
      ...(sharedBody.songs || []).filter((song) => !song.owned),
    ];
    renderLoadSongList(state.loadModalSongs);
  } catch (error) {
    state.loadModalSongs = [];
    renderLoadSongList([], error.message || "Could not load songs.");
  } finally {
    state.loadingSongs = false;
  }
}

function renderLoadSongList(songs, message = "") {
  renderLoadSongModeTabs();
  const sharedMode = state.loadModalMode === "shared";
  if (sharedMode && state.loadModalSortKey === "access") {
    state.loadModalSortKey = "bardName";
  } else if (!sharedMode && state.loadModalSortKey === "bardName") {
    state.loadModalSortKey = "access";
  }
  if (els.loadSongOwnerSortButton) {
    els.loadSongOwnerSortButton.textContent = sharedMode ? "Bard" : "Access";
    els.loadSongOwnerSortButton.dataset.loadSongSort = sharedMode ? "bardName" : "access";
  }
  const sortedSongs = sortSongsForList(
    songs.filter((song) => (sharedMode ? !song.owned : song.owned)),
    state.loadModalSortKey,
    state.loadModalSortDirection,
  );
  els.loadSongMessage.textContent = message || (sortedSongs.length ? "" : `No ${sharedMode ? "shared" : "owned"} progressions yet.`);
  els.loadSongList.replaceChildren(...sortedSongs.map((song) => {
    const row = document.createElement("div");
    row.className = "list-group-item list-group-item-action bg-dark text-light border-secondary song-list-row has-preview";
    row.setAttribute("role", "button");
    row.setAttribute("tabindex", "0");
    row.dataset.songId = song.id;
    const ownerCell = sharedMode
      ? `<a class="song-list-cell text-secondary" href="${escapeHtml(leaderboardBardUrl(song.bardName))}">${escapeHtml(song.bardName || "Shared")}</a>`
      : `<span class="song-list-cell text-secondary">${renderSongAccess(song)}</span>`;
    row.innerHTML = `
      <span class="song-list-preview-cell">${renderSongMiniPreview(song)}</span>
      <span class="song-list-title-wrap">
        <strong class="song-list-cell song-list-title-cell">${escapeHtml(song.title || DEFAULT_SONG_TITLE)}</strong>
        ${renderSongListStats(song)}
      </span>
      <span class="song-list-cell text-secondary">${escapeHtml(formatSongDate(song.updatedAt))}</span>
      ${ownerCell}
    `;
    row.querySelector("a")?.addEventListener("click", (event) => event.stopPropagation());
    row.addEventListener("click", () => loadSongFromServer(song.id));
    row.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        loadSongFromServer(song.id);
      }
    });
    return row;
  }));
  renderLoadSongSortHeaders();
}

function renderLoadSongModeTabs() {
  const shared = state.loadModalMode === "shared";
  els.loadSongOwnedTab.classList.toggle("active", !shared);
  els.loadSongOwnedTab.setAttribute("aria-selected", shared ? "false" : "true");
  els.loadSongSharedTab.classList.toggle("active", shared);
  els.loadSongSharedTab.setAttribute("aria-selected", shared ? "true" : "false");
}

function sortSongsForList(songs, sortKey, sortDirection) {
  const direction = sortDirection === "asc" ? 1 : -1;
  const valueForSong = (song) => {
    if (sortKey === "bardName") {
      return song.owned ? "You" : song.bardName || "Shared";
    }
    if (sortKey === "updatedAt") {
      return new Date(song.updatedAt).getTime() || 0;
    }
    if (sortKey === "access") {
      return formatSongAccess(song);
    }
    return song.title || DEFAULT_SONG_TITLE;
  };

  return [...songs].sort((first, second) => {
    const firstValue = valueForSong(first);
    const secondValue = valueForSong(second);
    if (typeof firstValue === "number" || typeof secondValue === "number") {
      return ((firstValue || 0) - (secondValue || 0)) * direction;
    }

    const compared = String(firstValue).localeCompare(String(secondValue), undefined, { sensitivity: "base" });
    return compared * direction || String(first.id).localeCompare(String(second.id));
  });
}

function renderLoadSongSortHeaders() {
  document.querySelectorAll("[data-load-song-sort]").forEach((button) => {
    const active = button.dataset.loadSongSort === state.loadModalSortKey;
    button.classList.toggle("is-active", active);
    button.setAttribute("aria-sort", active ? (state.loadModalSortDirection === "asc" ? "ascending" : "descending") : "none");
  });
}

function formatSongAccess(song) {
  return [
    song.shared ? "Public" : "",
  ].filter(Boolean).join(", ") || "Private";
}

function isReadOnlySong(song) {
  return Boolean(song.locked) || Number(song.usedCount) > 0 || Number(song.thumbsUp) > 0;
}

function renderSongAccess(song) {
  const labels = [formatSongAccess(song)];
  if (isReadOnlySong(song)) {
    labels.push("Read-only");
  }
  return labels.map((label) => `<span class="access-chip${label === "Read-only" ? " is-read-only" : ""}">${escapeHtml(label)}</span>`).join("");
}

function renderSongListStats(song) {
  const thumbs = Math.max(0, Math.trunc(Number(song.thumbsUp) || 0));
  const uses = Math.max(0, Math.trunc(Number(song.usedCount) || 0));
  const score = Math.max(0, Math.trunc(Number(song.score) || thumbs + uses));
  return `
    <span class="song-list-stats" aria-label="${thumbs} thumbs, ${uses} uses, score ${score}">
      <span title="Thumbs">${thumbs} thumbs</span>
      <span title="Uses">${uses} uses</span>
      <span title="Score">${score} score</span>
    </span>
  `;
}

function renderSongMiniPreview(serverSong) {
  const song = normalizeSong(serverSong.song || serverSong, { preserveProvidedTracks: true });
  const meter = song.timeline?.timeSignatureEvents?.[0] || { numerator: 4, denominator: 4 };
  const ticksPerSongBar = meter.numerator * (PPQ * (4 / meter.denominator));
  const sectionEnd = Math.max(0, ...(song.form?.sections || []).map((section) => (Number(section.startTick) || 0) + (Number(section.durationTicks) || 0)));
  const eventEnd = Math.max(0, ...song.tracks.flatMap((track) => (track.events || []).map((event) => (Number(event.tick) || 0) + (Number(event.durationTicks) || ticksPerSongBar))));
  const total = Math.max(ticksPerSongBar, sectionEnd, eventEnd);
  const previewTracks = song.tracks.filter((track) => !hiddenTimelineTrackTypes.has(track.type));
  const previewEvents = previewTracks.flatMap((track) => (track.events || []).map((event) => ({ track, event }))).slice(0, 80);
  const events = previewEvents.map(({ track, event }) => {
    const left = ((Number(event.tick) || 0) / total) * 100;
    const width = (Math.max(PPQ / 8, Number(event.durationTicks) || ticksPerSongBar) / total) * 100;
    const trackOrder = ["chords", "melody", "bass", "drums"];
    const top = Math.max(0, trackOrder.indexOf(track.type)) / trackOrder.length * 100;
    const height = Math.max(12, 100 / trackOrder.length);
    const color = trackColors[track.type] || "#6f7680";
    return `<span class="song-list-preview-event" style="left:${left}%;width:${width}%;top:${top}%;height:${height}%;--event-color:${color};"></span>`;
  }).join("");

  return `<span class="song-list-preview" aria-hidden="true">${events}</span>`;
}

function leaderboardBardUrl(bardName) {
  const params = new URLSearchParams({ bardName: String(bardName || "") });
  return `/leaderboard/?${params}`;
}

function formatSongDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function clipSearchApiType(trackType = state.clipSearch.trackType) {
  return trackType === "drums" ? "drum" : trackType;
}

function clipSearchDisplayType(trackType = state.clipSearch.trackType) {
  return panelTitleForTrackType(trackType).toLowerCase();
}

function setClipSearchMode(mode) {
  state.clipSearch.mode = mode === "shared" ? "shared" : "my";
  renderClipSearchMode();
  loadClipSearchResults();
}

function renderClipSearchMode() {
  const shared = state.clipSearch.mode === "shared";
  els.clipSearchMyButton.className = `btn btn-sm ${shared ? "btn-outline-light" : "btn-primary"}`;
  els.clipSearchSharedButton.className = `btn btn-sm ${shared ? "btn-primary" : "btn-outline-light"}`;
  els.clipSearchFilters.hidden = !shared;
}

function showClipSearchModal(trackType) {
  state.clipSearch.trackType = trackType;
  state.clipSearch.mode = "my";
  state.clipSearch.selectedClipId = null;
  state.clipSearch.clips = [];
  els.clipSearchTitle.textContent = `${panelTitleForTrackType(trackType)} Clip Search`;
  els.clipSearchBardInput.value = "";
  els.clipSearchTitleInput.value = "";
  els.clipSearchDurationSelect.value = "";
  renderClipSearchMode();
  renderClipSearchList([], `Loading ${clipSearchDisplayType()} clips...`);
  window.bootstrap.Modal.getOrCreateInstance(els.clipSearchModal).show();
  loadClipSearchResults();
}

async function loadClipSearchResults() {
  const requestId = makeId("clip_search");
  state.clipSearch.requestId = requestId;
  state.clipSearch.loading = true;
  state.clipSearch.selectedClipId = null;
  renderClipSearchList([], `Loading ${clipSearchDisplayType()} clips...`);

  const params = new window.URLSearchParams({
    mode: state.clipSearch.mode,
    type: clipSearchApiType(),
    includeClip: "1",
  });

  if (state.clipSearch.mode === "shared") {
    const bardName = els.clipSearchBardInput.value.trim();
    const title = els.clipSearchTitleInput.value.trim();
    const duration = els.clipSearchDurationSelect.value;
    if (bardName) {
      params.set("bardName", bardName);
    }
    if (title) {
      params.set("title", title);
    }
    if (duration) {
      params.set("duration", duration);
    }
  }

  try {
    const body = await apiRequest(`/api/clips?${params}`);
    if (state.clipSearch.requestId === requestId) {
      state.clipSearch.clips = body.clips || [];
      renderClipSearchList(state.clipSearch.clips);
    }
  } catch (error) {
    if (state.clipSearch.requestId === requestId) {
      renderClipSearchList([], error.message || "Could not load clips.");
    }
  } finally {
    if (state.clipSearch.requestId === requestId) {
      state.clipSearch.loading = false;
      renderClipSearchState();
    }
  }
}

function renderClipSearchList(clips, message = "") {
  if (!clips.some((clip) => clip.id === state.clipSearch.selectedClipId)) {
    state.clipSearch.selectedClipId = null;
  }
  els.clipSearchMessage.textContent = message || (clips.length ? "" : `No ${clipSearchDisplayType()} clips found.`);
  els.clipSearchList.replaceChildren(...clips.map((clip) => {
    const item = document.createElement("div");
    const active = clip.id === state.clipSearch.selectedClipId;
    item.className = `list-group-item list-group-item-action bg-dark text-light border-secondary clip-search-item${active ? " is-selected" : ""}`;
    item.dataset.clipId = clip.id;
    item.setAttribute("role", "button");
    item.setAttribute("tabindex", "0");
    item.setAttribute("aria-pressed", active ? "true" : "false");
    const preview = renderClipSearchMiniPreview(clip);
    item.innerHTML = `
      <div class="clip-search-item-main">
        <span class="clip-search-preview-shell" aria-hidden="true">${preview}</span>
        <div class="clip-search-item-text">
          <div class="d-flex w-100 justify-content-between gap-3">
            <strong>${escapeHtml(clip.title || "Untitled Clip")}</strong>
            <small class="text-secondary">${escapeHtml(formatSongDate(clip.updatedAt))}</small>
          </div>
          <div class="clip-search-meta">
            <span>${escapeHtml(clip.owned ? "Mine" : clip.bardName || "Shared")}</span>
            <span>${escapeHtml(durationBarsLabel(clip.clipDuration))}</span>
            ${renderClipSearchStats(clip)}
          </div>
        </div>
      </div>
    `;
    item.addEventListener("click", () => selectClipSearchResult(clip.id));
    item.addEventListener("dblclick", () => importClipSearchResult(clip.id));
    item.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        selectClipSearchResult(clip.id);
      }
    });
    return item;
  }));
  renderClipSearchState();
}

function renderClipSearchState() {
  if (!els.confirmClipSearchButton) {
    return;
  }

  els.confirmClipSearchButton.disabled = state.clipSearch.loading || !state.clipSearch.selectedClipId;
}

function selectClipSearchResult(serverClipId) {
  if (state.clipSearch.loading) {
    return;
  }

  const clip = state.clipSearch.clips.find((candidate) => candidate.id === serverClipId);
  if (!clip) {
    return;
  }

  state.clipSearch.selectedClipId = clip.id;
  els.clipSearchList.querySelectorAll(".clip-search-item[data-clip-id]").forEach((item) => {
    const active = item.dataset.clipId === clip.id;
    item.classList.toggle("is-selected", active);
    item.setAttribute("aria-pressed", active ? "true" : "false");
  });
  renderClipSearchState();
  previewClipSearchResult(clip.id);
}

function importSelectedClipSearchResult() {
  if (!state.clipSearch.selectedClipId || state.clipSearch.loading) {
    return;
  }

  importClipSearchResult(state.clipSearch.selectedClipId);
}

function renderClipSearchStats(clip) {
  const thumbs = Math.max(0, Math.trunc(Number(clip.thumbsUp) || 0));
  const uses = Math.max(0, Math.trunc(Number(clip.usedCount) || 0));
  const score = Math.max(0, Math.trunc(Number(clip.score) || thumbs + uses));
  return `
    <span title="Thumbs">${thumbs} thumbs</span>
    <span title="Uses">${uses} uses</span>
    <span title="Score">${score} score</span>
  `;
}

function renderClipSearchMiniPreview(serverClip) {
  if (!serverClip?.clip) {
    return "";
  }

  try {
    const clip = normalizeSavedClip(serverClip.clip, serverClip);
    const trackType = normalizePanelTrackType(serverClip.clipType || clip.clipType || state.clipSearch.trackType);
    const event = createSavedClipEvent(trackType, clip, 0, { type: trackType }, serverClip);
    return renderTimelineClipBody({ type: trackType }, event, {
      visibleStartTick: 0,
      durationTicks: event.durationTicks,
    }, true);
  } catch {
    return "";
  }
}

function durationBarsLabel(value) {
  const bars = Number(value) || 1;
  return `${bars} bar${bars === 1 ? "" : "s"}`;
}

async function importClipSearchResult(serverClipId) {
  if (state.clipSearch.loading) {
    return;
  }

  state.clipSearch.loading = true;
  state.clipSearch.previewRequestId = makeId("clip_preview_cancel");
  cancelClipAudition();
  renderClipSearchState();
  renderClipSearchList([], "Loading clip...");

  try {
    const body = await apiRequest(`/api/clips/${encodeURIComponent(serverClipId)}`);
    const clip = body.clip?.clip;
    if (!clip) {
      throw new Error("The server did not return clip JSON.");
    }

    applySavedClipToTimeline(normalizeSavedClip(clip, body.clip), body.clip);
    window.bootstrap.Modal.getOrCreateInstance(els.clipSearchModal).hide();
  } catch (error) {
    renderClipSearchList([], error.message || "Could not load that clip.");
  } finally {
    state.clipSearch.loading = false;
    renderClipSearchState();
  }
}

async function previewClipSearchResult(serverClipId) {
  if (state.clipSearch.loading) {
    return;
  }

  const previewRequestId = makeId("clip_preview");
  const auditionRequestId = beginClipAudition();
  state.clipSearch.previewRequestId = previewRequestId;

  try {
    await ensureAudio();
    if (
      state.clipSearch.previewRequestId !== previewRequestId
      || !isCurrentClipAudition(auditionRequestId)
    ) {
      return;
    }

    const body = await apiRequest(`/api/clips/${encodeURIComponent(serverClipId)}`);
    if (
      state.clipSearch.previewRequestId !== previewRequestId
      || !isCurrentClipAudition(auditionRequestId)
    ) {
      return;
    }

    const clip = body.clip?.clip;
    if (!clip) {
      throw new Error("The server did not return clip JSON.");
    }

    const { track, event } = createClipSearchPreviewEvent(normalizeSavedClip(clip, body.clip), body.clip);
    await ensureSamplerForTrack(track);
    if (
      state.clipSearch.previewRequestId !== previewRequestId
      || !isCurrentClipAudition(auditionRequestId)
    ) {
      return;
    }

    playStockPatternEvent(event, ticksToSeconds(event.durationTicks || ticksPerBar()) * 1000, track);
  } catch (error) {
    if (state.clipSearch.previewRequestId === previewRequestId) {
      els.clipSearchMessage.textContent = error.message || "Could not preview that clip.";
    }
  }
}

function normalizeSavedClip(value, serverClip = null) {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const bars = validClipBars(serverClip?.clipDuration ?? serverClip?.clip_duration ?? source.clipDuration ?? source.clip_duration ?? source.bars);
  const stepsPerBar = validClipStepsPerBar(source.stepsPerBar ?? source.steps_per_bar ?? source.gridResolution ?? source.grid_resolution);
  return {
    ...source,
    title: String(source.title || "Untitled Clip").slice(0, 160),
    bars,
    stepsPerBar,
    notes: Array.isArray(source.notes) ? source.notes : [],
    events: Array.isArray(source.events) ? source.events : [],
  };
}

function validClipBars(value, fallback = 1) {
  const bars = Number(value);
  return [1, 2, 4, 8, 16].includes(bars) ? bars : fallback;
}

function validClipStepsPerBar(value, fallback = 8) {
  const stepsPerBar = Number(value);
  return [4, 8, 12, 16, 24, 32, 48].includes(stepsPerBar) ? stepsPerBar : fallback;
}

function validClipGridOctaves(value, fallback = DEFAULT_CLIP_GRID_OCTAVES) {
  const gridOctaves = Number(value);
  return [1, 2, 3].includes(gridOctaves) ? Math.trunc(gridOctaves) : fallback;
}

function applySavedClipToTimeline(clip, serverClip) {
  const { trackType, track, event } = createClipSearchPreviewEvent(clip, serverClip);
  track.events.push(event);
  track.events.sort((a, b) => a.tick - b.tick);
  state.playheadTick = Math.min(totalTicks(), event.tick + event.durationTicks);
  ensureSongDuration(state.playheadTick + ticksPerBar());
  clearPreviewClip();
  state.activePatternTrackId = track.id;
  state.activePanelTrackType = trackType;
  clearSelection();
  touchSong();
  render();
  auditionTimelineEvent(event.id);
}

function createClipSearchPreviewEvent(clip, serverClip) {
  const trackType = normalizePanelTrackType(serverClip?.clipType || clip.clipType || state.clipSearch.trackType);
  const track = getPatternTargetTrack(trackType);
  if (!track) {
    throw new Error("That clip type does not have a track.");
  }

  const preview = state.previewClip?.trackType === trackType ? state.previewClip : null;
  const tick = preview?.tick ?? barStartTick(state.playheadTick);
  return {
    trackType,
    track,
    event: createSavedClipEvent(trackType, clip, tick, track, serverClip),
  };
}

function normalizePanelTrackType(value) {
  const type = String(value || "").trim().toLowerCase();
  if (type === "drum") {
    return "drums";
  }
  return ["melody", "bass", "drums"].includes(type) ? type : state.clipSearch.trackType;
}

function createSavedClipEvent(trackType, clip, tick, track = null, serverClip = null) {
  const durationTicks = clipDurationTicks(clip);
  return {
    id: makeId(trackType),
    eventType: `${trackType}-clip`,
    patternId: String(serverClip?.id || clip.id || makeId("clip")),
    serverClipId: serverClip?.id ? String(serverClip.id) : "",
    patternName: clip.title,
    tick,
    durationTicks,
    rootMode: trackType === "drums" ? "fixed" : "chord-root",
    octaveShift: trackType === "drums"
      ? 0
      : normalizePatternTransform(octaveForSavedClip(trackType, clip) - normalizePatternTransform(track?.octaveShift)),
    bars: validClipBars(clip?.bars),
    stepsPerBar: validClipStepsPerBar(clip?.stepsPerBar),
    gridOctaves: validClipGridOctaves(clip?.gridOctaves),
    events: savedClipEventsForTrackType(trackType, clip),
  };
}

function clipDurationTicks(clip) {
  return Math.max(ticksPerBar(), barsToTicks(validClipBars(clip?.bars), getMeter().numerator, getMeter().denominator));
}

function savedClipEventsForTrackType(trackType, clip) {
  if (Array.isArray(clip.events) && clip.events.length) {
    return clip.events.map((event) => ({
      ...event,
      offsetTicks: event.offsetTicks ?? beatsToTick(event.offsetBeats || 0),
      durationTicks: event.durationTicks ?? beatsToTick(event.durationBeats || 0.5),
    }));
  }

  if (trackType === "drums") {
    return clip.notes.map((event) => ({
      midi: Number(event.midi) || 36 + (Number(event.semitone) || 0),
      drumName: event.drumName || "",
      accent: normalizeDrumAccent(event.accent),
      velocity: normalizedDrumVelocity(event),
      offsetTicks: clipStepToTicks(event.step || 0, clip),
      durationTicks: clipStepToTicks(event.durationSteps || 1, clip),
    }));
  }

  return clip.notes.map((note) => ({
    relativeSemitone: Number(note.semitone) || 0,
    octave: 0,
    offsetTicks: clipStepToTicks(note.step || 0, clip),
    durationTicks: clipStepToTicks(note.durationSteps || 1, clip),
  }));
}

function octaveForSavedClip(trackType, clip) {
  const baseOctave = Number.isFinite(Number(clip.baseOctave))
    ? Math.trunc(Number(clip.baseOctave))
    : 2;
  const progressionBaseOctave = trackType === "bass" ? 3 : 4;
  return baseOctave - progressionBaseOctave;
}

function clipStepToTicks(step, clip) {
  const stepsPerBar = validClipStepsPerBar(clip?.stepsPerBar);
  return Math.round(((Number(step) || 0) / stepsPerBar) * ticksPerBar());
}

function normalizeDrumAccent(value) {
  const accent = String(value || "").trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(DRUM_ACCENT_VELOCITIES, accent) ? accent : "normal";
}

function normalizedDrumVelocity(event) {
  const velocity = Number(event?.velocity);
  if (Number.isFinite(velocity)) {
    return Math.max(0.05, Math.min(1, velocity <= 1 ? velocity : velocity / 127));
  }

  return DRUM_ACCENT_VELOCITIES[normalizeDrumAccent(event?.accent)] / 127;
}

async function loadSongFromServer(songId) {
  if (state.loadingSongs) {
    return;
  }

  state.loadingSongs = true;
  renderLoadSongList([], "Loading song...");

  try {
    const body = await apiRequest(`/api/songs/${encodeURIComponent(songId)}`);
    const loadedSong = body.song?.song;

    if (!loadedSong) {
      throw new Error("The server did not return song JSON.");
    }

    stopPlayback(true, state.playing);
    state.song = normalizeSong(loadedSong);
    state.serverSongId = body.song.id ? String(body.song.id) : null;
    state.serverSongLocked = Boolean(body.song.locked || !body.song.owned);
    state.loadedOriginBardName = body.song.owned ? "" : String(body.song.bardName || "").trim();
    state.serverSongSignature = state.serverSongId ? songServerSignature() : null;
    clearSelection();
    clearPreviewClip();
    state.activePanelTrackType = "chords";
    state.collapsedTrackIds = [];
    state.playheadTick = 0;
    state.loopRegion = null;
    state.loopSelectionAnchorTick = null;
    state.loopDrag = null;
    state.selectedProgram = programFromChordTrack() || programs[0];
    initializeSongHistory();
    scheduleAppStateSave();
    render();
    window.bootstrap.Modal.getOrCreateInstance(els.loadSongModal).hide();
  } catch (error) {
    renderLoadSongList([], error.message || "Could not load that song.");
  } finally {
    state.loadingSongs = false;
  }
}

function renderFileMenuState() {
  els.fileSaveButton.disabled = state.savingSong;
  els.fileSaveButton.textContent = state.savingSong ? "Saving..." : "Save";
  els.fileSaveAsButton.disabled = state.savingSong;
  els.fileSaveAsButton.textContent = state.savingSong ? "Saving..." : "Save As";
  els.unsavedChangesIndicator.classList.toggle("d-none", !hasUnsavedSongChanges());
  renderSaveSongModalState();
}

function normalizeComparableTitle(value) {
  return String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
}

function serverComparableSong(song = state.song) {
  return stripVolatileSongFields({
    ...song,
    title: String(song.title || DEFAULT_SONG_TITLE).trim() || DEFAULT_SONG_TITLE,
  });
}

function stripVolatileSongFields(value) {
  if (Array.isArray(value)) {
    return value.map(stripVolatileSongFields);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value)
      .filter(([key]) => !["id", "createdAt", "updatedAt"].includes(key))
      .map(([key, entryValue]) => [key, stripVolatileSongFields(entryValue)]));
  }

  return value;
}

function songServerSignature(song = state.song) {
  return JSON.stringify(serverComparableSong(song));
}

function isSavedServerSongDirty() {
  return Boolean(state.serverSongId && state.serverSongSignature && songServerSignature() !== state.serverSongSignature);
}

function isUnsavedNewSongModified() {
  return songServerSignature() !== songServerSignature(createDefaultSong());
}

function hasUnsavedSongChanges() {
  return state.serverSongId ? isSavedServerSongDirty() : isUnsavedNewSongModified();
}

function shareLinkForCurrentSong() {
  if (!state.serverSongId) {
    return "";
  }

  const url = new window.URL(window.location.href);
  url.pathname = "/songs/";
  url.search = "";
  url.hash = "";
  url.searchParams.set("loadSongId", state.serverSongId);
  return url.toString();
}

function showShareModal() {
  const link = shareLinkForCurrentSong();
  const isDirty = hasUnsavedSongChanges();
  els.shareLinkInput.value = link;
  els.copyShareLinkButton.disabled = !link;
  els.shareLinkMessage.textContent = "";
  els.shareLinkHelp.textContent = link
    ? `This deep link opens the saved progression. ${isDirty ? "Save again before sharing if you want this link to include your latest edits. " : ""}For social media, mark the progression shared when you save so other people can open it.`
    : "Save this progression first to create a deep link. For social media, mark it shared when you save so other people can open it.";
  window.bootstrap.Modal.getOrCreateInstance(els.shareModal).show();
}

async function copyShareLink() {
  const link = els.shareLinkInput.value;
  if (!link) {
    els.shareLinkMessage.textContent = "Save this progression first to create a link.";
    return;
  }

  try {
    await navigator.clipboard.writeText(link);
  } catch {
    els.shareLinkInput.select();
    document.execCommand("copy");
  }
  els.shareLinkMessage.textContent = "Link copied.";
}

async function loadLeaderboardSongFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const songId = params.get("loadSongId");
  if (!songId) {
    return;
  }

  try {
    const body = await apiRequest(`/api/leaderboard/songs/${encodeURIComponent(songId)}/use`, {
      method: "POST",
      body: JSON.stringify({}),
    });
    const loadedSong = body.song?.song;
    if (!loadedSong) {
      throw new Error("The server did not return song JSON.");
    }

    stopPlayback(true, state.playing);
    state.song = normalizeSong(loadedSong);
    state.serverSongId = body.song.id ? String(body.song.id) : null;
    state.serverSongLocked = Boolean(body.song.locked || !body.song.owned);
    state.serverSongSignature = state.serverSongId ? songServerSignature() : null;
    state.loadedOriginBardName = body.song.owned ? "" : String(body.song.bardName || "").trim();
    clearSelection();
    clearPreviewClip();
    state.activePanelTrackType = "chords";
    state.collapsedTrackIds = [];
    state.playheadTick = 0;
    state.loopRegion = null;
    state.loopSelectionAnchorTick = null;
    state.loopDrag = null;
    state.selectedProgram = programFromChordTrack() || programs[0];
    initializeSongHistory();
    scheduleAppStateSave();
    render();
    window.history.replaceState({}, "", window.location.pathname);
  } catch (error) {
    window.alert(error.message || "Could not load that progression.");
  }
}

function validSnapTicks(value) {
  return [3840, 1920, 960, 640, 480, 320, 240].includes(Number(value));
}

function normalizeChordRenderMode(value) {
  const mode = String(value || "").trim();
  return chordRenderModes.includes(mode) ? mode : DEFAULT_CHORD_RENDER_MODE;
}

function normalizeLoopRegion(region) {
  const startTick = snapTick(region?.startTick);
  const endTick = snapTick(region?.endTick);
  if (!Number.isFinite(startTick) || !Number.isFinite(endTick) || endTick <= startTick) {
    return null;
  }

  return {
    startTick,
    endTick: Math.min(totalTicks(), endTick),
  };
}

function contentLoopRegion() {
  const contentEvents = (state.song.tracks || []).flatMap((track) => (
    (track.events || []).filter(isEditableTimelineClip)
  ));
  if (!contentEvents.length) {
    return null;
  }

  const startTick = Math.min(...contentEvents.map((event) => Number(event.tick) || 0));
  const endTick = Math.max(...contentEvents.map((event) => (
    (Number(event.tick) || 0) + (Number(event.durationTicks) || state.snapTicks)
  )));
  const barTicks = ticksPerBar();
  return normalizeLoopRegion({
    startTick: Math.floor(startTick / barTicks) * barTicks,
    endTick: Math.ceil(endTick / barTicks) * barTicks,
  });
}

function defaultInstrumentForTrackType(type) {
  return {
    chords: "acoustic_guitar_nylon",
    melody: "acoustic_grand_piano",
    bass: "acoustic_bass",
    drums: "drums",
    lyrics: "voice",
  }[type] || "acoustic_grand_piano";
}

function normalizeSoundfontId(value, fallback = "acoustic_grand_piano") {
  const raw = String(value || "").trim();
  if (!raw) {
    return fallback;
  }
  if (FATBOY_SOUNDFONTS.some((config) => config.id === raw)) {
    return raw;
  }
  return LEGACY_SOUNDFONT_ALIASES[raw] || fallback;
}

function programForSoundfontId(value) {
  const soundfontId = normalizeSoundfontId(value);
  return programs.find((program) => program.instrumentName === soundfontId) || programs[0];
}

function labelForInstrumentName(value, fallback = "acoustic_grand_piano") {
  return soundfontConfig(normalizeSoundfontId(value, fallback)).label;
}

function labelForTrackInstrument(track) {
  if (track?.type === "lyrics") {
    return "Voice";
  }
  return labelForInstrumentName(track?.instrumentName, defaultInstrumentForTrackType(track?.type));
}

function programFromStoredValue(value) {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }
  const legacyName = LEGACY_PROGRAM_VALUE_MAP[Number(value)];
  if (legacyName) {
    return programForSoundfontId(legacyName);
  }
  return programForSoundfontId(value);
}

function programFromChordTrack() {
  return programForSoundfontId(getChordTrack()?.instrumentName);
}

function normalizeSong(input, options = {}) {
  const fallback = createDefaultSong();
  const song = {
    ...fallback,
    ...input,
    schema: "ChordSongJSON",
    schemaVersion: input.schemaVersion || "0.1.0",
    ppq: Number(input.ppq) || PPQ,
    timeline: {
      ...fallback.timeline,
      ...(input.timeline || {}),
    },
    form: {
      ...fallback.form,
      ...(input.form || {}),
    },
    metadata: {
      ...fallback.metadata,
      ...(input.metadata || {}),
    },
    tracks: Array.isArray(input.tracks) && input.tracks.length ? input.tracks : fallback.tracks,
    markers: Array.isArray(input.markers) ? input.markers : fallback.markers,
  };

  song.timeline.tempoEvents = Array.isArray(song.timeline.tempoEvents) && song.timeline.tempoEvents.length
    ? song.timeline.tempoEvents
    : fallback.timeline.tempoEvents;
  song.timeline.timeSignatureEvents = Array.isArray(song.timeline.timeSignatureEvents) && song.timeline.timeSignatureEvents.length
    ? song.timeline.timeSignatureEvents
    : fallback.timeline.timeSignatureEvents;
  song.timeline.keySignatureEvents = Array.isArray(song.timeline.keySignatureEvents) && song.timeline.keySignatureEvents.length
    ? song.timeline.keySignatureEvents
    : fallback.timeline.keySignatureEvents;
  song.form.sections = Array.isArray(song.form.sections) && song.form.sections.length ? song.form.sections : fallback.form.sections;
  song.form.playOrder = Array.isArray(song.form.playOrder) ? song.form.playOrder : [];
  song.tracks = options.preserveProvidedTracks
    ? song.tracks.map((track) => normalizeTrack(track))
    : ensureDefaultTracks(song.tracks);

  return song;
}

function ensureDefaultTracks(tracks) {
  const defaults = createDefaultSong().tracks;
  const result = tracks.map((track) => normalizeTrack(track));

  defaults.forEach((defaultTrack) => {
    if (!result.some((track) => track.name === defaultTrack.name || track.id === defaultTrack.id)) {
      result.push(defaultTrack);
    }
  });

  return result;
}

function normalizeTrack(track) {
  const migratedTrack = { ...track };
  if (migratedTrack.id === "track_rhythm" || migratedTrack.name === "Rhythm" || migratedTrack.type === "other") {
    migratedTrack.id = "track_bass";
    migratedTrack.name = "Bass";
    migratedTrack.type = "bass";
    migratedTrack.instrumentName = migratedTrack.instrumentName === "piano" || !migratedTrack.instrumentName
      ? "acoustic_bass"
      : migratedTrack.instrumentName;
  }
  if (migratedTrack.type !== "lyrics") {
    migratedTrack.instrumentName = normalizeSoundfontId(
      migratedTrack.instrumentName,
      defaultInstrumentForTrackType(migratedTrack.type),
    );
  }

  return {
    ...migratedTrack,
    muted: typeof migratedTrack.muted === "boolean" ? migratedTrack.muted : false,
    volume: clampValue(migratedTrack.volume, 0, 1.25, DEFAULT_TRACK_VOLUME),
    sustain: clampValue(migratedTrack.sustain, 0, 1, DEFAULT_TRACK_SUSTAIN),
    adsr: normalizeTrackAdsr(migratedTrack.adsr, migratedTrack.sustain),
    octaveShift: trackSupportsOctaveShift(migratedTrack) ? normalizePatternTransform(migratedTrack.octaveShift) : 0,
    events: Array.isArray(migratedTrack.events) ? migratedTrack.events.map(normalizeEvent) : [],
  };
}

function normalizeTrackAdsr(adsr, legacySustain) {
  const input = adsr || {};
  const fallbackSustain = Number.isFinite(Number(legacySustain))
    ? clampValue(legacySustain, 0, 1, DEFAULT_TRACK_ADSR.sustain)
    : DEFAULT_TRACK_ADSR.sustain;

  return {
    attack: clampTrackAdsrValue("attack", input.attack),
    decay: clampTrackAdsrValue("decay", input.decay),
    sustain: clampTrackAdsrValue("sustain", input.sustain, fallbackSustain),
    release: clampTrackAdsrValue("release", input.release),
  };
}

function clampTrackAdsrValue(key, value, fallback = DEFAULT_TRACK_ADSR[key]) {
  const limit = TRACK_ADSR_LIMITS[key];
  return clampValue(value, limit.min, limit.max, fallback);
}

function normalizeEvent(event) {
  if (event?.eventType === "chord") {
    return {
      ...event,
      playStyle: normalizeChordPlayStyle(event.playStyle),
      guitarVoicingIndex: Math.max(0, Number(event.guitarVoicingIndex) || 0),
    };
  }

  if (isTransformablePatternClip(event)) {
    const patternEvent = { ...event };
    delete patternEvent.inversion;
    return {
      ...patternEvent,
      octaveShift: normalizePatternTransform(event.octaveShift),
      gridOctaves: validClipGridOctaves(event.gridOctaves),
      stepsPerBar: validClipStepsPerBar(event.stepsPerBar, DEFAULT_CLIP_STEPS_PER_BAR),
    };
  }

  return event;
}

function getScaleChords() {
  const keySignature = getKeySignature();
  const mode = keySignature.mode === "minor" ? "minor" : "major";
  const scale = Scale.get(`${keySignature.key} ${mode}`).notes;
  const roots = scale.length ? scale : Scale.get(`${keySignature.key} major`).notes;
  const qualities = mode === "minor"
    ? [
        { roman: "i", suffix: "m" },
        { roman: "ii dim", suffix: "dim" },
        { roman: "III", suffix: "" },
        { roman: "iv", suffix: "m" },
        { roman: "v", suffix: "m" },
        { roman: "VI", suffix: "" },
        { roman: "VII", suffix: "" },
      ]
    : [
        { roman: "I", suffix: "" },
        { roman: "ii", suffix: "m" },
        { roman: "iii", suffix: "m" },
        { roman: "IV", suffix: "" },
        { roman: "V", suffix: "" },
        { roman: "vi", suffix: "m" },
        { roman: "vii dim", suffix: "dim" },
      ];

  return roots.map((root, index) => ({
    roman: qualities[index].roman,
    symbol: `${root}${qualities[index].suffix}`,
    color: chordColors[index],
  }));
}

function createChordEvent(tick, durationTicks, symbol) {
  const details = parseChord(symbol);
  return {
    id: makeId("chord"),
    eventType: "chord",
    tick,
    durationTicks,
    symbol: details.symbol,
    root: details.root,
    ...(details.bass ? { bass: details.bass } : {}),
    quality: details.quality,
    extensions: details.extensions,
    voicing: details.voicing,
    playStyle: normalizeChordPlayStyle(state.defaultChordPlayStyle),
    guitarVoicingIndex: 0,
  };
}

function parseChord(symbol) {
  const trimmed = String(symbol || "C").trim() || "C";
  const chord = Chord.get(trimmed);
  const notes = chord.notes.length ? chord.notes : fallbackChordNotes(trimmed);
  const root = chord.tonic || notes[0]?.replace(/\d/g, "") || trimmed.replace(/[^A-G#b]/g, "") || "C";
  const bass = chord.bass || "";
  const voicing = midiVoicing(notes, root);
  const aliases = Array.isArray(chord.aliases) ? chord.aliases.filter(Boolean) : [];
  const quality = aliases[0] || inferQuality(trimmed);

  return {
    symbol: trimmed,
    root,
    bass,
    quality,
    extensions: aliases.slice(1, 4),
    voicing,
  };
}

function fallbackChordNotes(symbol) {
  const root = symbol.match(/[A-G](?:#|b)?/)?.[0] || "C";
  const minor = /m(?!aj)/.test(symbol);
  return [
    root,
    Note.transpose(root, minor ? "3m" : "3M"),
    Note.transpose(root, "5P"),
  ];
}

function inferQuality(symbol) {
  if (symbol.includes("dim")) {
    return "dim";
  }
  if (/m(?!aj)/.test(symbol)) {
    return "minor";
  }
  return "major";
}

function midiVoicing(notes, root) {
  const rootMidi = Note.midi(`${root}4`) || 60;
  let previous = rootMidi - 1;

  return notes.map((note) => {
    let octave = 4;
    let midi = Note.midi(`${note}${octave}`);
    while (Number.isFinite(midi) && midi <= previous) {
      octave += 1;
      midi = Note.midi(`${note}${octave}`);
    }
    previous = Number.isFinite(midi) ? midi : previous + 4;
    return Number.isFinite(midi) ? midi : previous;
  });
}

function render() {
  renderControls();
  renderToolPanel();
  renderTimeline();
}

function renderControls() {
  const meter = getMeter();
  renderSongTitleWithOrigin();
  populateChordExtensionSelect(els.chordExtensionSelect, getSelectedChordEvent()?.symbol);
  populateChordPlayStyleSelect(els.chordPlayStyleSelect, getSelectedChordEvent()?.playStyle);
  populateChordPlayStyleSelect(els.defaultChordPlayStyleSelect, state.defaultChordPlayStyle);
  renderTempo();
  renderKeySignature();
  els.meterSelect.value = `${meter.numerator}/${meter.denominator}`;
  els.snapSelect.value = String(state.snapTicks);
  els.chordRenderModeSelect.value = state.chordRenderMode;
  els.loopButton.classList.toggle("btn-primary", state.loop);
  els.loopButton.classList.toggle("btn-outline-light", !state.loop);
  els.loopButton.setAttribute("aria-pressed", String(state.loop));
  els.playButton.innerHTML = "<span class=\"play-icon\" aria-hidden=\"true\"></span>";
  els.playButton.classList.toggle("active", state.playing);
  els.playButton.setAttribute("aria-label", state.playing ? "Pause" : "Play");
  els.playButton.setAttribute("title", state.playing ? "Pause" : "Play");
  els.playButton.disabled = Boolean(state.audio.error || state.preparingPlayback);
  renderFileMenuState();
}

function renderSongTitleWithOrigin() {
  els.songTitleInput.textContent = state.song.title || DEFAULT_SONG_TITLE;
  els.originBardIndicator.replaceChildren();
  els.originBardIndicator.classList.toggle("d-none", !state.loadedOriginBardName);
  if (!state.loadedOriginBardName) {
    return;
  }

  els.originBardIndicator.append("original by ");
  const link = document.createElement("a");
  link.href = leaderboardBardUrl(state.loadedOriginBardName);
  link.textContent = state.loadedOriginBardName;
  els.originBardIndicator.append(link);
}

function renderTempo() {
  const tempoText = `${getTempo()} BPM`;
  els.tempoButton.textContent = tempoText;
  els.tempoButton.setAttribute("aria-label", `Tempo ${tempoText}`);
  els.tempoDisplay.textContent = tempoText;
}

function renderKeySignature() {
  const keySignature = getKeySignature();
  const mode = keySignature.mode === "minor" ? "minor" : "major";
  const keyText = `${keySignature.key} ${mode}`;
  els.keyButton.textContent = keyText;
  els.keyButton.setAttribute("aria-label", `Key ${keyText}`);
  els.keyDisplay.textContent = keyText;
  els.keyModal.querySelectorAll("[data-key-value]").forEach((button) => {
    button.classList.toggle("btn-primary", button.dataset.keyValue === keySignature.key);
    button.classList.toggle("btn-outline-light", button.dataset.keyValue !== keySignature.key);
  });
  els.keyModal.querySelectorAll("[data-quality-value]").forEach((button) => {
    button.classList.toggle("btn-primary", button.dataset.qualityValue === mode);
    button.classList.toggle("btn-outline-light", button.dataset.qualityValue !== mode);
  });
}

function openTrackSoundModal(trackId) {
  const track = state.song.tracks.find((item) => item.id === trackId);
  if (!track) {
    return;
  }

  state.editingTrackId = track.id;
  els.trackSoundTrackName.textContent = `${track.name} sound bank`;
  els.trackChordStyleField.hidden = track.type !== "chords";
  els.trackSustainField.hidden = false;
  const adsr = normalizeTrackAdsr(track.adsr, track.sustain);
  els.trackVolumeInput.value = `${clampValue(track.volume, 0, 1.25, DEFAULT_TRACK_VOLUME)}`;
  els.trackAttackInput.value = `${adsr.attack}`;
  els.trackDecayInput.value = `${adsr.decay}`;
  els.trackSustainInput.value = `${adsr.sustain}`;
  els.trackReleaseInput.value = `${adsr.release}`;
  els.trackOctaveField.hidden = !trackSupportsOctaveShift(track);
  els.chordRenderModeSelect.value = state.chordRenderMode;
  renderTrackOctaveControls(track);
  const trackPrograms = track.type === "drums"
    ? DRUM_SOUNDFONTS.map((config) => ({ value: config.id, label: config.label, instrumentName: config.id }))
    : programs;
  const selectedInstrument = normalizeSoundfontId(
    track.instrumentName,
    defaultInstrumentForTrackType(track.type),
  );

  els.trackSoundSelect.replaceChildren(...trackPrograms.map((program) => (
    new Option(program.label, program.instrumentName, false, program.instrumentName === selectedInstrument)
  )));

  if (!trackPrograms.some((program) => program.instrumentName === selectedInstrument)) {
    els.trackSoundSelect.value = trackPrograms[0].instrumentName;
  }

  window.bootstrap.Modal.getOrCreateInstance(els.trackSoundModal).show();
}

function renderTrackOctaveControls(track = state.song.tracks.find((item) => item.id === state.editingTrackId)) {
  if (!track || !trackSupportsOctaveShift(track)) {
    els.trackOctaveDisplay.textContent = "0";
    return;
  }

  els.trackOctaveDisplay.textContent = signedValueLabel(normalizePatternTransform(track.octaveShift));
}

function adjustEditingTrackOctave(delta) {
  const track = state.song.tracks.find((item) => item.id === state.editingTrackId);
  if (!track || !trackSupportsOctaveShift(track)) {
    return;
  }

  track.octaveShift = normalizePatternTransform((Number(track.octaveShift) || 0) + delta);
  renderTrackOctaveControls(track);
  touchSong();
  renderTimeline();
}

function saveTrackSound() {
  const track = state.song.tracks.find((item) => item.id === state.editingTrackId);
  const program = track?.type === "drums"
    ? DRUM_SOUNDFONTS.find((item) => item.id === els.trackSoundSelect.value)
    : programs.find((item) => item.instrumentName === els.trackSoundSelect.value);
  if (!track || !program) {
    return;
  }

  track.instrumentName = program.instrumentName || program.id;
  track.volume = clampValue(els.trackVolumeInput.value, 0, 1.25, DEFAULT_TRACK_VOLUME);
  track.adsr = {
    attack: clampTrackAdsrValue("attack", els.trackAttackInput.value),
    decay: clampTrackAdsrValue("decay", els.trackDecayInput.value),
    sustain: clampTrackAdsrValue("sustain", els.trackSustainInput.value),
    release: clampTrackAdsrValue("release", els.trackReleaseInput.value),
  };
  track.sustain = track.adsr.sustain;
  if (track.type === "chords") {
    state.selectedProgram = program;
  }
  updateSamplerForTrack(track);

  touchSong();
  window.bootstrap.Modal.getOrCreateInstance(els.trackSoundModal).hide();
  render();
}

function previewTrackSoundSelection() {
  const track = state.song.tracks.find((item) => item.id === state.editingTrackId);
  const family = track?.type === "drums" ? "drums" : "fatboy";
  playSoundfontPreview(family, els.trackSoundSelect.value);
}

function openChordClipModal() {
  const selectedChords = getSelectedChordEvents();
  const event = selectedChords.find((item) => item.event.id === state.selectedEventId)?.event || selectedChords[0]?.event;
  if (!event) {
    return;
  }

  populateChordExtensionSelect(els.chordExtensionSelect, event.symbol);
  populateChordPlayStyleSelect(els.chordPlayStyleSelect, event.playStyle);
  els.chordExtensionSelect.value = chordExtensionValueForSymbol(event.symbol);
  els.chordPlayStyleSelect.value = normalizeChordPlayStyle(event.playStyle);
  window.bootstrap.Modal.getOrCreateInstance(els.chordClipModal).show();
}

function openPatternClipModal() {
  const selected = getSelectedTimelineEvents().find(({ event }) => isTransformablePatternClip(event));
  if (!selected || !isTransformablePatternClip(selected.event)) {
    return;
  }

  renderPatternClipModal(selected.track, selected.event);
  window.bootstrap.Modal.getOrCreateInstance(els.patternClipModal).show();
}

function renderPatternClipModal(track, event) {
  const count = getSelectedTimelineEvents().filter(({ event: selectedEvent }) => isTransformablePatternClip(selectedEvent)).length;
  els.patternClipModalTitle.textContent = count > 1 ? `${count} Clips` : `${track.name} Clip`;
  els.patternOctaveDisplay.textContent = signedValueLabel(normalizePatternTransform(event.octaveShift));
  const isDrumClip = event.eventType === "drums-clip";
  const editorHref = isDrumClip ? "/drums" : "/clips";
  els.patternOpenFullEditorButton.href = editorHref;
  els.patternFullEditorTextLink.href = editorHref;
  els.patternOctaveDownButton.closest(".clip-transform-controls").hidden = isDrumClip;
  els.patternClipGridSettings.querySelector("[aria-label='Visible grid octaves']").hidden = isDrumClip;
  renderPatternPlaybackControls(track, event);
  renderPatternGridSettings(event);
  renderPatternClipGrid(track, event);
}

function signedValueLabel(value) {
  return value > 0 ? `+${value}` : String(value);
}

function renderPatternGridSettings(event) {
  const gridOctaves = patternGridOctaves(event);
  const stepsPerBar = patternStepsPerBar(event);
  els.patternGridOctaves1Radio.checked = gridOctaves === 1;
  els.patternGridOctaves2Radio.checked = gridOctaves === 2;
  els.patternGridOctaves3Radio.checked = gridOctaves === 3;
  els.patternGridResolution4Radio.checked = stepsPerBar === 4;
  els.patternGridResolution8Radio.checked = stepsPerBar === 8;
  els.patternGridResolution12Radio.checked = stepsPerBar === 12;
  els.patternGridResolution16Radio.checked = stepsPerBar === 16;
}

function renderPatternClipGrid(track, event) {
  const lanes = patternGridLanes(event);
  const stepsPerBar = patternStepsPerBar(event);
  const totalSteps = patternTotalSteps(event);
  const notes = patternGridNotes(event);
  const fragment = document.createDocumentFragment();
  const minimumLaneHeightRem = patternGridOctaves(event) >= 3 && event.eventType !== "drums-clip" ? 1.02 : 1.25;
  const laneHeightRem = Math.max(minimumLaneHeightRem, Math.min(2.15, 22 / Math.max(lanes.length, 1)));

  els.patternClipGrid.style.setProperty("--step-count", totalSteps);
  els.patternClipGrid.style.setProperty("--clip-lane-height", `${laneHeightRem.toFixed(2)}rem`);

  const corner = document.createElement("div");
  corner.className = "pattern-grid-corner";
  fragment.append(corner);

  for (let step = 0; step < totalSteps; step += 1) {
    const header = document.createElement("div");
    header.className = `pattern-step-header${step % stepsPerBar === 0 ? " is-bar-start" : ""}`;
    header.textContent = step % stepsPerBar === 0 ? `Bar ${Math.floor(step / stepsPerBar) + 1}` : `${(step % stepsPerBar) + 1}`;
    fragment.append(header);
  }

  lanes.forEach((lane, laneIndex) => {
    const label = document.createElement("div");
    const octaveBoundaryClass = isPatternOctaveBoundaryAbove(lane.semitone, event) ? " is-octave-boundary" : "";
    const bottomRowClass = laneIndex === lanes.length - 1 ? " is-grid-bottom" : "";
    label.className = `pattern-lane-label${octaveBoundaryClass}${bottomRowClass}`;
    label.style.setProperty("--note-color", lane.color);
    label.textContent = lane.label;
    label.title = `${lane.label} (${chromaticNames[positiveMod(lane.semitone, 12)]})`;
    fragment.append(label);

    for (let step = 0; step < totalSteps; step += 1) {
      const note = patternNoteCoveringCell(notes, step, lane.semitone);
      const isStart = note?.step === step;
      const isEnd = note && note.step + note.durationSteps - 1 === step;
      const cell = document.createElement("button");
      cell.className = [
        "pattern-note-cell",
        note ? "is-active" : "",
        isStart ? "is-note-start" : "",
        isEnd ? "is-note-end" : "",
        step % stepsPerBar === 0 ? "is-bar-start" : "",
        octaveBoundaryClass ? "is-octave-boundary" : "",
        bottomRowClass ? "is-grid-bottom" : "",
      ].filter(Boolean).join(" ");
      cell.type = "button";
      cell.style.setProperty("--note-color", lane.color);
      cell.setAttribute("aria-label", `${note ? "Remove" : "Add"} ${lane.label} at step ${step + 1}`);
      cell.addEventListener("click", () => togglePatternGridNote(track, event, step, lane));

      if (isStart) {
        cell.append(createPatternResizeHandle(note, "left"));
      }
      if (isEnd) {
        cell.append(createPatternResizeHandle(note, "right"));
      }

      fragment.append(cell);
    }
  });

  const playhead = document.createElement("div");
  playhead.className = `pattern-clip-playhead${isPatternEditorPlaying(track, event) ? " is-visible" : ""}`;
  playhead.setAttribute("aria-hidden", "true");
  fragment.append(playhead);

  els.patternClipGrid.replaceChildren(fragment);
  updatePatternEditorPlayhead();
}

function renderPatternPlaybackControls(track, event) {
  els.patternPlayButton.textContent = isPatternEditorPlaying(track, event) ? "Pause" : "Play";
  els.patternPlayButton.classList.toggle("active", isPatternEditorPlaying(track, event));
}

function isPatternEditorPlaying(track, event) {
  return Boolean(
    state.patternPreviewPlaying
    && state.patternPreviewTrackId === track?.id
    && state.patternPreviewEventId === event?.id,
  );
}

async function togglePatternEditorPlayback() {
  const selected = getSelectedTimelineEvents().find(({ event }) => isTransformablePatternClip(event));
  if (!selected) {
    return;
  }

  if (isPatternEditorPlaying(selected.track, selected.event)) {
    stopPatternEditorPlayback();
    renderPatternClipModal(selected.track, selected.event);
    return;
  }

  await playPatternEditorClip(selected.track, selected.event);
}

async function playPatternEditorClip(track, event) {
  stopPatternEditorPlayback();
  const auditionRequestId = beginClipAudition();

  try {
    await ensureAudio();
    await ensureSamplerForTrack(track);
  } catch (error) {
    window.alert(error.message || "Could not load that sound.");
    return;
  }

  if (!isCurrentClipAudition(auditionRequestId)) {
    return;
  }

  state.patternPreviewPlaying = true;
  state.patternPreviewTrackId = track.id;
  state.patternPreviewEventId = event.id;
  state.patternPreviewStartedAt = Tone.now() + 0.05;
  renderPatternClipModal(track, event);
  schedulePatternEditorCycle(track, event, state.patternPreviewStartedAt, auditionRequestId);
  startPatternEditorPlayhead();
}

function schedulePatternEditorCycle(track, event, startedAt, auditionRequestId) {
  if (!state.patternPreviewPlaying || !isCurrentClipAudition(auditionRequestId)) {
    return;
  }

  if (event.eventType === "drums-clip") {
    (event.events || []).forEach((drumEvent) => {
      const midi = Number(drumEvent.midi);
      if (!Number.isFinite(midi)) {
        return;
      }

      const noteStartAt = startedAt + ticksToSeconds(drumEvent.offsetTicks || 0);
      const noteDurationMs = ticksToSeconds(drumEvent.durationTicks || 120) * 1000;
      scheduleToneNote(midi, noteDurationMs, track, drumVelocityForEvent(drumEvent), Math.max(0, (noteStartAt - Tone.now()) * 1000));
    });

    const nextCycleAt = startedAt + ticksToSeconds(event.durationTicks || ticksPerBar());
    scheduleNoteTimer(() => {
      schedulePatternEditorCycle(track, event, nextCycleAt, auditionRequestId);
    }, Math.max(80, (nextCycleAt - Tone.now()) * 1000));
    return;
  }

  const chord = chordAtTick(event.tick) || lastSelectedChordEvent();
  const rootMidi = rootMidiForPatternEvent(event, chord);
  patternRelativeSemitones(event, chord, track).forEach((relativeSemitone, index) => {
    const noteEvent = event.events[index];
    const midi = rootMidi + relativeSemitone;
    const noteStartAt = startedAt + ticksToSeconds(noteEvent.offsetTicks || 0);
    const noteDurationMs = ticksToSeconds(noteEvent.durationTicks || state.snapTicks) * 920;
    scheduleToneNote(midi, noteDurationMs, track, 88, Math.max(0, (noteStartAt - Tone.now()) * 1000));
  });

  const nextCycleAt = startedAt + ticksToSeconds(event.durationTicks || ticksPerBar());
  scheduleNoteTimer(() => {
    schedulePatternEditorCycle(track, event, nextCycleAt, auditionRequestId);
  }, Math.max(80, (nextCycleAt - Tone.now()) * 1000));
}

function stopPatternEditorPlayback() {
  if (!state.patternPreviewPlaying && !state.patternPreviewAnimationFrame) {
    return;
  }

  state.patternPreviewPlaying = false;
  state.patternPreviewStartedAt = 0;
  state.patternPreviewTrackId = null;
  state.patternPreviewEventId = null;
  stopPatternEditorPlayhead();
  cancelClipAudition();
  els.patternPlayButton.textContent = "Play";
  els.patternPlayButton.classList.remove("active");
  updatePatternEditorPlayhead();
}

function startPatternEditorPlayhead() {
  stopPatternEditorPlayhead();
  const animate = () => {
    updatePatternEditorPlayhead();
    if (state.patternPreviewPlaying) {
      state.patternPreviewAnimationFrame = window.requestAnimationFrame(animate);
    }
  };
  state.patternPreviewAnimationFrame = window.requestAnimationFrame(animate);
}

function stopPatternEditorPlayhead() {
  if (!state.patternPreviewAnimationFrame) {
    return;
  }

  window.cancelAnimationFrame(state.patternPreviewAnimationFrame);
  state.patternPreviewAnimationFrame = null;
}

function updatePatternEditorPlayhead() {
  const playhead = els.patternClipGrid.querySelector(".pattern-clip-playhead");
  if (!playhead) {
    return;
  }

  playhead.classList.toggle("is-visible", state.patternPreviewPlaying);
  if (!state.patternPreviewPlaying || !state.patternPreviewStartedAt) {
    els.patternClipGrid.style.setProperty("--pattern-playhead-left", "var(--clip-lane-label-width)");
    return;
  }

  const selected = getSelectedTimelineEvents().find(({ event }) => event.id === state.patternPreviewEventId);
  const totalSeconds = ticksToSeconds(selected?.event.durationTicks || ticksPerBar());
  const steps = selected ? patternTotalSteps(selected.event) : DEFAULT_CLIP_STEPS_PER_BAR;
  const elapsedSeconds = Math.max(0, Tone.now() - state.patternPreviewStartedAt);
  const cycleSeconds = totalSeconds > 0 ? elapsedSeconds % totalSeconds : 0;
  const stepPosition = (cycleSeconds / totalSeconds) * steps;

  els.patternClipGrid.style.setProperty(
    "--pattern-playhead-left",
    `calc(var(--clip-lane-label-width) + (${stepPosition} * var(--clip-step-width)))`,
  );
}

function patternGridLanes(event) {
  if (event.eventType === "drums-clip") {
    const midiValues = [...new Set((event.events || [])
      .map((noteEvent) => Math.trunc(Number(noteEvent.midi)))
      .filter(Number.isFinite))];
    const lanes = (midiValues.length ? midiValues : [36, 38, 42, 46, 49, 51]).sort((a, b) => a - b);
    return lanes.map((midi) => ({
      semitone: midi,
      degree: positiveMod(midi, 12),
      label: drumNameForMidi(midi, event),
      color: chordColors[positiveMod(midi, chordColors.length)],
    })).reverse();
  }

  const chord = chordAtTick(event.tick) || lastSelectedChordEvent();
  const scaleSemitones = scaleSemitonesForChord(chord);
  const root = chord?.root || getKeySignature().key || "C";
  const rootIndex = Note.chroma(root) ?? 0;
  const baseOctave = patternGridBaseOctave(event);
  const visibleSemitones = patternVisibleLaneSemitones(event, scaleSemitones);

  return visibleSemitones.map((relativeSemitone) => {
    const absoluteIndex = rootIndex + relativeSemitone;
    const noteName = keys[positiveMod(absoluteIndex, 12)];
    const scaleDegree = scaleSemitones.indexOf(positiveMod(relativeSemitone, 12));
    return {
      semitone: relativeSemitone,
      degree: scaleDegree,
      label: `${noteName}${baseOctave + Math.floor(absoluteIndex / 12)}`,
      color: chordColors[scaleDegree] || chromaticNoteColor,
    };
  }).reverse();
}

function patternVisibleLaneSemitones(event, scaleSemitones) {
  const targetLaneCount = patternGridOctaves(event) * scaleSemitones.length;
  const defaultSemitones = patternScaleLaneSemitonesFrom(0, targetLaneCount, scaleSemitones);
  const noteSemitones = patternRawNoteSemitones(event);
  if (!noteSemitones.length) {
    return defaultSemitones;
  }

  const visible = new Set(noteSemitones);
  const lowestNote = Math.min(...noteSemitones);
  const highestNote = Math.max(...noteSemitones);

  patternScaleLaneSemitonesBetween(lowestNote, highestNote, scaleSemitones)
    .filter((semitone) => semitone >= lowestNote && semitone <= highestNote)
    .forEach((semitone) => visible.add(semitone));

  let lower = previousPatternScaleLane(lowestNote, scaleSemitones);
  let upper = nextPatternScaleLane(highestNote, scaleSemitones);
  const expandedTarget = Math.max(targetLaneCount, visible.size, noteSemitones.length);
  for (let pad = 0; visible.size < expandedTarget && pad < MAX_CLIP_GRID_OCTAVES * 14; pad += 1) {
    if (pad % 2 === 0) {
      visible.add(lower);
      lower = previousPatternScaleLane(lower, scaleSemitones);
    } else {
      visible.add(upper);
      upper = nextPatternScaleLane(upper, scaleSemitones);
    }
  }

  return [...visible].sort((a, b) => a - b);
}

function drumNameForMidi(midi, event = null) {
  const midiNumber = Math.trunc(Number(midi));
  const matchingEvent = (event?.events || []).find((noteEvent) => Math.trunc(Number(noteEvent.midi)) === midiNumber);
  return matchingEvent?.drumName || `Drum ${midiNumber}`;
}

function patternScaleLaneSemitonesFrom(startSemitone, laneCount, scaleSemitones) {
  const lanes = [];
  for (let semitone = startSemitone; lanes.length < laneCount; semitone += 1) {
    if (scaleSemitones.includes(positiveMod(semitone, 12))) {
      lanes.push(semitone);
    }
  }
  return lanes;
}

function patternScaleLaneSemitonesBetween(startSemitone, endSemitone, scaleSemitones) {
  const lanes = [];
  for (let semitone = startSemitone; semitone <= endSemitone; semitone += 1) {
    if (scaleSemitones.includes(positiveMod(semitone, 12))) {
      lanes.push(semitone);
    }
  }
  return lanes;
}

function previousPatternScaleLane(relativeSemitone, scaleSemitones) {
  let candidate = relativeSemitone - 1;
  while (!scaleSemitones.includes(positiveMod(candidate, 12))) {
    candidate -= 1;
  }
  return candidate;
}

function nextPatternScaleLane(relativeSemitone, scaleSemitones) {
  let candidate = relativeSemitone + 1;
  while (!scaleSemitones.includes(positiveMod(candidate, 12))) {
    candidate += 1;
  }
  return candidate;
}

function patternRawNoteSemitones(event) {
  if (event.eventType === "drums-clip") {
    return [...new Set((event.events || [])
      .map((noteEvent) => Math.trunc(Number(noteEvent.midi)))
      .filter(Number.isFinite))];
  }

  const chord = chordAtTick(event.tick) || lastSelectedChordEvent();
  return [...new Set((event.events || [])
    .map((noteEvent) => (
      relativeSemitoneForChord(Math.trunc(Number(noteEvent.relativeSemitone) || 0), chord)
      + (Math.trunc(Number(noteEvent.octave) || 0) * 12)
    ))
    .filter(Number.isFinite))];
}

function patternGridBaseOctave(event) {
  const track = getSelectedTimelineEvents().find(({ event: selectedEvent }) => selectedEvent.id === event.id)?.track;
  const trackShift = normalizePatternTransform(track?.octaveShift);
  return (event.eventType === "bass-clip" ? 3 : 4) + trackShift + normalizePatternTransform(event.octaveShift);
}

function isPatternOctaveBoundaryAbove(relativeSemitone, event) {
  if (event.eventType === "drums-clip") {
    return false;
  }

  const chord = chordAtTick(event.tick) || lastSelectedChordEvent();
  const root = chord?.root || getKeySignature().key || "C";
  const rootIndex = Note.chroma(root) ?? 0;
  const absoluteIndex = rootIndex + relativeSemitone;
  const nextAbsoluteIndex = absoluteIndex + 1;
  return Math.floor(nextAbsoluteIndex / 12) > Math.floor(absoluteIndex / 12);
}

function positiveMod(value, divisor) {
  return ((value % divisor) + divisor) % divisor;
}

function patternGridOctaves(event) {
  return validClipGridOctaves(event.gridOctaves);
}

function patternStepsPerBar(event) {
  return validClipStepsPerBar(event.stepsPerBar, DEFAULT_CLIP_STEPS_PER_BAR);
}

function patternBars(event) {
  return validClipBars(event.bars, Math.max(1, Math.round((Number(event.durationTicks) || ticksPerBar()) / ticksPerBar())));
}

function patternTotalSteps(event) {
  return patternBars(event) * patternStepsPerBar(event);
}

function patternGridNotes(event) {
  const stepsPerBar = patternStepsPerBar(event);
  const totalSteps = patternTotalSteps(event);
  const seen = new Set();
  const chord = chordAtTick(event.tick) || lastSelectedChordEvent();

  return (event.events || []).reduce((notes, noteEvent, index) => {
    const semitone = event.eventType === "drums-clip"
      ? Math.trunc(Number(noteEvent.midi))
      : relativeSemitoneForChord(Math.trunc(Number(noteEvent.relativeSemitone) || 0), chord)
        + (Math.trunc(Number(noteEvent.octave) || 0) * 12);
    const step = Math.trunc(Math.round(((Number(noteEvent.offsetTicks) || 0) / ticksPerBar()) * stepsPerBar));
    const durationSteps = Math.max(1, Math.trunc(Math.round(((Number(noteEvent.durationTicks) || state.snapTicks) / ticksPerBar()) * stepsPerBar)));
    if (!Number.isFinite(semitone) || step < 0 || step >= totalSteps) {
      return notes;
    }

    const duration = Math.min(durationSteps, totalSteps - step);
    const collides = Array.from({ length: duration }, (_unused, offset) => `${step + offset}:${semitone}`)
      .some((key) => seen.has(key));
    if (collides) {
      return notes;
    }

    for (let offset = 0; offset < duration; offset += 1) {
      seen.add(`${step + offset}:${semitone}`);
    }
    notes.push({
      id: noteEvent.id || `${event.id || "clip"}_note_${index}`,
      eventIndex: index,
      step,
      semitone,
      durationSteps: duration,
      drumName: noteEvent.drumName || "",
      accent: normalizeDrumAccent(noteEvent.accent),
      velocity: normalizedDrumVelocity(noteEvent),
    });
    return notes;
  }, []).sort((a, b) => a.step - b.step || a.semitone - b.semitone);
}

function patternNoteCoveringCell(notes, step, semitone) {
  return notes.find((note) => (
    note.semitone === semitone
    && note.step <= step
    && step < note.step + note.durationSteps
  ));
}

function togglePatternGridNote(track, event, step, lane) {
  const notes = patternGridNotes(event);
  const existing = patternNoteCoveringCell(notes, step, lane.semitone);
  let createdNote = null;

  if (existing) {
    event.events.splice(existing.eventIndex, 1);
  } else {
    createdNote = patternEventFromGridNote(event, {
      id: makeId("note"),
      step,
      semitone: lane.semitone,
      durationSteps: 1,
    });
    event.events.push(createdNote);
  }

  sortPatternEvents(event);
  touchSong();
  renderTimeline();
  renderPatternClipModal(track, event);

  if (createdNote) {
    previewPatternGridNote(track, event, createdNote);
  }
}

function patternEventFromGridNote(event, note) {
  const stepsPerBar = patternStepsPerBar(event);
  if (event.eventType === "drums-clip") {
    return {
      id: note.id || makeId("note"),
      midi: Math.trunc(Number(note.semitone) || 36),
      drumName: note.drumName || drumNameForMidi(note.semitone, event),
      accent: normalizeDrumAccent(note.accent),
      velocity: normalizedDrumVelocity(note),
      offsetTicks: Math.round((note.step / stepsPerBar) * ticksPerBar()),
      durationTicks: Math.max(1, Math.round((note.durationSteps / stepsPerBar) * ticksPerBar())),
    };
  }

  return {
    id: note.id || makeId("note"),
    relativeSemitone: note.semitone,
    octave: 0,
    offsetTicks: Math.round((note.step / stepsPerBar) * ticksPerBar()),
    durationTicks: Math.max(1, Math.round((note.durationSteps / stepsPerBar) * ticksPerBar())),
  };
}

function sortPatternEvents(event) {
  event.events.sort((a, b) => (
    (Number(a.offsetTicks) || 0) - (Number(b.offsetTicks) || 0)
    || patternSortPitch(a, event) - patternSortPitch(b, event)
  ));
}

function patternSortPitch(noteEvent, event) {
  if (event.eventType === "drums-clip") {
    return Number(noteEvent.midi) || 0;
  }

  return (Number(noteEvent.relativeSemitone) || 0) + ((Number(noteEvent.octave) || 0) * 12);
}

async function previewPatternGridNote(track, event, noteEvent) {
  try {
    await ensureSamplerForTrack(track);
  } catch (error) {
    window.alert(error.message || "Could not load that sound.");
    return;
  }

  if (event.eventType === "drums-clip") {
    scheduleToneNote(noteEvent.midi, ticksToSeconds(noteEvent.durationTicks || 120) * 1000, track, drumVelocityForEvent(noteEvent), 0);
    return;
  }

  const chord = chordAtTick(event.tick) || lastSelectedChordEvent();
  const rootMidi = rootMidiForPatternEvent(event, chord);
  const relativeSemitone = relativeSemitoneForChord(noteEvent.relativeSemitone, chord)
    + ((Number(noteEvent.octave) || 0) * 12)
    + ((normalizePatternTransform(track?.octaveShift) + normalizePatternTransform(event.octaveShift)) * 12);
  scheduleToneNote(rootMidi + relativeSemitone, ticksToSeconds(noteEvent.durationTicks || state.snapTicks) * 920, track, 88, 0);
}

function createPatternResizeHandle(note, edge) {
  const handle = document.createElement("span");
  handle.className = `pattern-note-resize-handle is-${edge}`;
  handle.role = "separator";
  handle.setAttribute("aria-label", `${edge === "left" ? "Start" : "End"} of note`);
  handle.addEventListener("click", (event) => event.stopPropagation());
  handle.addEventListener("pointerdown", (event) => startPatternNoteResize(event, note, edge));
  return handle;
}

function startPatternNoteResize(pointerEvent, note, edge) {
  pointerEvent.preventDefault();
  pointerEvent.stopPropagation();
  state.patternGridResize = {
    noteId: note.id,
    eventIndex: note.eventIndex,
    edge,
    moved: false,
  };
  window.addEventListener("pointermove", resizePatternNoteFromPointer);
  window.addEventListener("pointerup", finishPatternNoteResize, { once: true });
}

function resizePatternNoteFromPointer(pointerEvent) {
  if (!state.patternGridResize) {
    return;
  }

  const selected = getSelectedTimelineEvents().find(({ event }) => isTransformablePatternClip(event));
  if (!selected) {
    finishPatternNoteResize();
    return;
  }

  const notes = patternGridNotes(selected.event);
  const note = notes.find((candidate) => candidate.id === state.patternGridResize.noteId);
  if (!note) {
    finishPatternNoteResize();
    return;
  }

  const originalStep = note.step;
  const originalDuration = note.durationSteps;
  resizePatternGridNoteToStep(selected.event, notes, note, state.patternGridResize.edge, patternGridStepFromClientX(pointerEvent.clientX));

  if (note.step !== originalStep || note.durationSteps !== originalDuration) {
    selected.event.events[note.eventIndex] = patternEventFromGridNote(selected.event, note);
    sortPatternEvents(selected.event);
    state.patternGridResize.moved = true;
    renderPatternClipModal(selected.track, selected.event);
  }
}

function finishPatternNoteResize() {
  if (!state.patternGridResize) {
    return;
  }

  const changed = state.patternGridResize.moved;
  state.patternGridResize = null;
  window.removeEventListener("pointermove", resizePatternNoteFromPointer);

  if (changed) {
    const selected = getSelectedTimelineEvents().find(({ event }) => isTransformablePatternClip(event));
    if (selected) {
      touchSong();
      renderTimeline();
      renderPatternClipModal(selected.track, selected.event);
    }
  }
}

function patternGridStepFromClientX(clientX) {
  const gridRect = els.patternClipGrid.getBoundingClientRect();
  const labelWidth = els.patternClipGrid.querySelector(".pattern-grid-corner")?.getBoundingClientRect().width || 0;
  const stepWidth = els.patternClipGrid.querySelector(".pattern-step-header")?.getBoundingClientRect().width || 1;
  const selected = getSelectedTimelineEvents().find(({ event }) => isTransformablePatternClip(event));
  const totalSteps = selected ? patternTotalSteps(selected.event) : DEFAULT_CLIP_STEPS_PER_BAR;
  const rawStep = Math.floor((clientX - gridRect.left - labelWidth) / stepWidth);
  return Math.trunc(Math.max(0, Math.min(totalSteps - 1, rawStep)));
}

function resizePatternGridNoteToStep(event, notes, note, edge, step) {
  const laneNotes = notes
    .filter((candidate) => candidate.semitone === note.semitone && candidate.id !== note.id)
    .sort((a, b) => a.step - b.step);
  const noteEnd = note.step + note.durationSteps;

  if (edge === "left") {
    const previousNote = laneNotes.filter((candidate) => candidate.step < note.step).at(-1);
    const minStart = previousNote ? previousNote.step + previousNote.durationSteps : 0;
    const maxStart = noteEnd - 1;
    const nextStart = Math.trunc(Math.max(minStart, Math.min(maxStart, step)));
    note.durationSteps = noteEnd - nextStart;
    note.step = nextStart;
    return;
  }

  const nextNote = laneNotes.find((candidate) => candidate.step > note.step);
  const maxEnd = nextNote ? nextNote.step : patternTotalSteps(event);
  const minEnd = note.step + 1;
  const nextEnd = Math.trunc(Math.max(minEnd, Math.min(maxEnd, step + 1)));
  note.durationSteps = nextEnd - note.step;
}

function openSelectedPatternInFullClipEditor(event = null) {
  event?.preventDefault?.();
  const selected = getSelectedTimelineEvents().find(({ event }) => isTransformablePatternClip(event));
  if (!selected) {
    return;
  }

  stopPatternEditorPlayback();
  const clip = clipJsonForPatternEvent(selected.track, selected.event);
  try {
    window.localStorage.setItem(CLIP_EDITOR_HANDOFF_KEY, JSON.stringify({
      version: 1,
      savedAt: new Date().toISOString(),
      returnUrl: "/songs",
      trackId: selected.track.id,
      eventId: selected.event.id,
      clip,
    }));
  } catch {
    window.alert("Could not open the full clip editor because browser storage is unavailable.");
    return;
  }

  saveAppState();
  window.location.href = selected.event.eventType === "drums-clip" ? "/drums" : "/clips";
}

function clipJsonForPatternEvent(track, event) {
  const chord = chordAtTick(event.tick) || lastSelectedChordEvent();
  const root = chord?.root || getKeySignature().key || "C";
  const now = new Date().toISOString();
  const stepsPerBar = patternStepsPerBar(event);

  if (event.eventType === "drums-clip") {
    return {
      schema: "ClipJSON",
      version: 1,
      id: String(event.patternId || event.id || makeId("clip")),
      title: event.patternName || `${track.name} Clip`,
      bars: patternBars(event),
      stepsPerBar,
      bpm: getTempo(),
      soundBank: soundfontIdForTrack(track),
      key: "C",
      baseOctave: 0,
      gridOctaves: DEFAULT_CLIP_GRID_OCTAVES,
      quality: "major",
      showChromatic: true,
      shared: false,
      stock: false,
      clipType: "drum",
      notes: patternGridNotes(event).map((note) => ({
        id: note.id || makeId("note"),
        step: note.step,
        semitone: Math.max(0, Math.trunc(Number(note.semitone) || 36) - 35),
        degree: positiveMod(note.semitone, 12),
        durationSteps: note.durationSteps,
        accent: normalizeDrumAccent(note.accent),
      })),
      events: (event.events || []).map((noteEvent) => ({
        ...noteEvent,
        accent: normalizeDrumAccent(noteEvent.accent),
        velocity: normalizedDrumVelocity(noteEvent),
      })),
      createdAt: event.createdAt || now,
      updatedAt: now,
    };
  }

  return {
    schema: "ClipJSON",
    version: 1,
    id: String(event.patternId || event.id || makeId("clip")),
    title: event.patternName || `${track.name} Clip`,
    bars: patternBars(event),
    stepsPerBar,
    bpm: getTempo(),
    soundBank: soundfontIdForTrack(track),
    key: keys.includes(root) ? root : "C",
    baseOctave: patternGridBaseOctave(event),
    gridOctaves: patternGridOctaves(event),
    quality: isMinorChord(chord) ? "minor" : "major",
    showChromatic: false,
    shared: false,
    stock: false,
    clipType: track.type === "bass" ? "bass" : "melody",
    notes: patternGridNotes(event).map((note) => ({
      id: note.id || makeId("note"),
      step: note.step,
      semitone: note.semitone,
      degree: scaleSemitonesForChord(chord).indexOf(positiveMod(note.semitone, 12)),
      durationSteps: note.durationSteps,
    })),
    createdAt: event.createdAt || now,
    updatedAt: now,
  };
}

function applyReturnedClipEditorHandoff() {
  let returned = null;
  try {
    const raw = window.localStorage.getItem(CLIP_EDITOR_RETURN_KEY);
    returned = raw ? JSON.parse(raw) : null;
  } catch {
    returned = null;
  }

  if (!returned?.clip || !returned.eventId) {
    return false;
  }

  try {
    window.localStorage.removeItem(CLIP_EDITOR_RETURN_KEY);
  } catch {
    // Storage cleanup is best-effort.
  }

  const selected = getTimelineEventById(returned.eventId);
  if (!selected || !isTransformablePatternClip(selected.event)) {
    return false;
  }

  applyClipJsonToPatternEvent(selected.track, selected.event, returned.clip);
  state.selectedEventId = selected.event.id;
  state.selectedEventIds = [selected.event.id];
  state.selectionAnchorEventId = selected.event.id;
  state.activePatternTrackId = selected.track.id;
  state.activePanelTrackType = selected.track.type;
  touchSong();
  render();
  return true;
}

function applyClipJsonToPatternEvent(track, event, clip) {
  const normalizedClip = normalizeSavedClip(clip);
  event.patternId = String(normalizedClip.id || event.patternId || event.id);
  event.patternName = normalizedClip.title || event.patternName || `${track.name} Clip`;
  delete event.inversion;
  event.bars = validClipBars(normalizedClip.bars);
  event.stepsPerBar = validClipStepsPerBar(normalizedClip.stepsPerBar, DEFAULT_CLIP_STEPS_PER_BAR);
  event.gridOctaves = validClipGridOctaves(normalizedClip.gridOctaves);
  event.octaveShift = event.eventType === "drums-clip" ? 0 : normalizePatternTransform(
    (Number(normalizedClip.baseOctave) || (track.type === "bass" ? 3 : 4))
      - (track.type === "bass" ? 3 : 4)
      - normalizePatternTransform(track.octaveShift),
  );
  delete event.soundBank;
  event.durationTicks = clipDurationTicks(normalizedClip);
  event.events = patternEventsFromClipNotes(normalizedClip);
  ensureSongDuration(event.tick + event.durationTicks + ticksPerBar());
}

function patternEventsFromClipNotes(clip) {
  if (normalizePanelTrackType(clip.clipType) === "drums") {
    return savedClipEventsForTrackType("drums", clip)
      .sort((a, b) => (a.offsetTicks || 0) - (b.offsetTicks || 0) || (a.midi || 0) - (b.midi || 0));
  }

  return (clip.notes || []).map((note) => patternEventFromGridNote({
    bars: validClipBars(clip.bars),
    stepsPerBar: validClipStepsPerBar(clip.stepsPerBar, DEFAULT_CLIP_STEPS_PER_BAR),
  }, {
    id: note.id || makeId("note"),
    step: Math.trunc(Number(note.step) || 0),
    semitone: Math.trunc(Number(note.semitone) || 0),
    durationSteps: Math.max(1, Math.trunc(Number(note.durationSteps) || 1)),
  })).sort((a, b) => (a.offsetTicks || 0) - (b.offsetTicks || 0) || (a.relativeSemitone || 0) - (b.relativeSemitone || 0));
}

function getTimelineEventById(eventId) {
  for (const track of state.song.tracks) {
    const event = track.events.find((candidate) => candidate.id === eventId);
    if (event) {
      return { track, event };
    }
  }
  return null;
}

function openNewChordSettingsModal() {
  populateChordPlayStyleSelect(els.defaultChordPlayStyleSelect, state.defaultChordPlayStyle);
  els.defaultChordPlayStyleSelect.value = normalizeChordPlayStyle(state.defaultChordPlayStyle);
  window.bootstrap.Modal.getOrCreateInstance(els.newChordSettingsModal).show();
}

function populateChordPlayStyleSelect(select, selectedValue = DEFAULT_CHORD_PLAY_STYLE) {
  if (!select) {
    return;
  }

  const value = normalizeChordPlayStyle(selectedValue);
  select.replaceChildren(...chordPlayStyleOptions.map((playStyle) => {
    const option = new Option(playStyle.displayName, playStyle.id, false, playStyle.id === value);
    option.title = playStyle.description;
    return option;
  }));
  select.value = value;
}

function populateChordExtensionSelect(select, selectedSymbol = "C") {
  if (!select) {
    return;
  }

  const value = chordExtensionValueForSymbol(selectedSymbol);
  select.replaceChildren(...chordExtensionOptions.map((extension) => (
    new Option(extension.label, extension.value, false, extension.value === value)
  )));
  select.value = value;
}

function normalizeChordExtensionValue(value) {
  const candidate = String(value || "");
  return chordExtensionOptions.some((extension) => extension.value === candidate) ? candidate : "";
}

function chordExtensionValueForSymbol(symbol) {
  const suffix = Chord.tokenize(String(symbol || "C"))?.[1] || "";
  if (chordExtensionOptions.some((extension) => extension.value === suffix)) {
    return suffix;
  }

  const chord = Chord.get(`C${suffix}`);
  const matching = chordExtensionOptions.find((extension) => (
    Chord.get(`C${extension.value}`).chroma === chord.chroma
  ));
  return matching?.value || "";
}

function renderToolPanel() {
  const title = panelTitleForTrackType(state.activePanelTrackType);
  els.chordPalette.classList.toggle("is-empty", state.activePanelTrackType !== "chords");
  els.chordPalette.classList.toggle("stock-clip-palette", state.activePanelTrackType !== "chords");
  els.chordPalette.setAttribute("aria-label", state.activePanelTrackType === "chords" ? "Scale degree chord palette" : `${title} tools`);

  if (state.activePanelTrackType !== "chords") {
    renderStockPatternPanel(state.activePanelTrackType);
    return;
  }

  const tools = getScaleChords().map((chord) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "chord-button";
    button.style.setProperty("--chord-color", chord.color);
    button.innerHTML = `<strong>${escapeHtml(chord.roman)}</strong><span>${escapeHtml(chord.symbol)}</span>`;
    button.addEventListener("click", () => applyPaletteChord(chord.symbol));
    return button;
  });

  const settingsButton = document.createElement("button");
  settingsButton.type = "button";
  settingsButton.className = "new-chord-settings-button";
  settingsButton.setAttribute("aria-label", "New chord settings");
  settingsButton.setAttribute("title", "New chord settings");
  settingsButton.textContent = "\u2699";
  settingsButton.addEventListener("click", openNewChordSettingsModal);
  tools.push(settingsButton);

  els.chordPalette.replaceChildren(...tools);
}

function renderStockPatternPanel(trackType) {
  const patterns = stockPatterns[trackType] || [];
  const selected = getSelectedTimelineEvent();
  const canApplyPattern = Boolean(
    state.previewClip?.trackType === trackType
    || getActivePatternTrack()?.type === trackType
    || getSelectedEventsForTrackType(trackType).length
    || selected && selected.track.type === trackType && stockPatterns[trackType],
  );
  els.chordPalette.classList.toggle("is-empty", !patterns.length && !state.stockPatternsLoading);
  if (state.stockPatternsLoading && !patterns.length) {
    const status = document.createElement("span");
    status.className = "stock-clip-status";
    status.textContent = "Loading stock clips...";
    els.chordPalette.replaceChildren(status);
    return;
  }

  const buttons = patterns.map((pattern) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "stock-clip-button";
    button.style.setProperty("--clip-color", trackColors[trackType] || "#69727f");
    button.title = pattern.name;
    button.innerHTML = renderStockPatternButtonBody(trackType, pattern);
    button.disabled = !canApplyPattern;
    button.addEventListener("click", () => applyStockPatternClip(trackType, pattern.id));
    return button;
  });

  const searchButton = document.createElement("button");
  searchButton.type = "button";
  searchButton.className = "stock-clip-search-button";
  searchButton.setAttribute("aria-label", `Search ${panelTitleForTrackType(trackType)} clips`);
  searchButton.setAttribute("title", `Search ${panelTitleForTrackType(trackType)} clips`);
  searchButton.innerHTML = `
    <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
      <path d="M10.8 18.1a7.3 7.3 0 1 1 5.2-2.2l4 4a1.2 1.2 0 0 1-1.7 1.7l-4-4a7.2 7.2 0 0 1-3.5.9Zm0-2.4a4.9 4.9 0 1 0 0-9.8 4.9 4.9 0 0 0 0 9.8Z"></path>
    </svg>
  `;
  searchButton.addEventListener("click", () => showClipSearchModal(trackType));
  const controls = [...buttons, searchButton];
  if (state.stockPatternsError && !patterns.length) {
    const status = document.createElement("span");
    status.className = "stock-clip-status";
    status.textContent = state.stockPatternsError;
    controls.unshift(status);
  }
  els.chordPalette.replaceChildren(...controls);
}

function renderStockPatternButtonBody(trackType, pattern) {
  const event = stockPatternPreviewEvent(trackType, pattern);
  const track = { type: trackType };
  const preview = renderTimelineClipBody(track, event, {
    visibleStartTick: 0,
    durationTicks: event.durationTicks,
  }, true);

  return `
    <span class="stock-clip-preview-shell" aria-hidden="true">${preview}</span>
    <span class="stock-clip-caption">
      <strong>${escapeHtml(pattern.name)}</strong>
      <span>${escapeHtml(durationBarsLabel(pattern.bars))}</span>
    </span>
  `;
}

function stockPatternPreviewEvent(trackType, pattern) {
  return {
    id: `stock_preview_${trackType}_${pattern.id}`,
    eventType: `${trackType}-clip`,
    patternName: pattern.name,
    tick: 0,
    durationTicks: patternDurationTicks(pattern),
    events: (pattern.events || []).map((event) => ({
      ...event,
      offsetTicks: event.offsetTicks ?? beatsToTick(event.offsetBeats),
      durationTicks: event.durationTicks ?? beatsToTick(event.durationBeats),
    })),
  };
}

function panelTitleForTrackType(type) {
  return {
    chords: "Chords",
    melody: "Melody",
    bass: "Bass",
    drums: "Drums",
    lyrics: "Lyrics",
  }[type] || "Chords";
}

function renderTimeline() {
  const meter = getMeter();
  const rowTicks = ticksPerTimelineRow();
  const rowWidth = tickToPx(rowTicks);
  const tracks = visibleTimelineTracks();
  els.barRuler.hidden = true;
  els.trackRows.style.width = `calc(var(--track-label-width) + ${rowWidth}px)`;

  const groups = [];
  for (let rowIndex = 0; rowIndex < timelineRowCount(); rowIndex += 1) {
    const rowStartTick = rowIndex * rowTicks;
    const rowEndTick = Math.min(totalTicks(), rowStartTick + rowTicks);
    const group = document.createElement("div");
    group.className = "timeline-group";
    group.dataset.groupIndex = String(rowIndex);

    const ruler = document.createElement("div");
    ruler.className = "bar-ruler timeline-group-ruler";
    ruler.style.width = `${rowWidth}px`;
    ruler.dataset.rowStartTick = String(rowStartTick);
    ruler.addEventListener("pointerdown", startLoopRegionPointer);

    for (let barOffset = 0; barOffset < BARS_PER_TIMELINE_ROW; barOffset += 1) {
      const bar = (rowIndex * BARS_PER_TIMELINE_ROW) + barOffset;
      if (bar >= totalBars()) {
        break;
      }

      const marker = document.createElement("div");
      marker.className = "bar-marker";
      marker.style.left = `${tickToPx(barOffset * ticksPerBar(meter))}px`;
      marker.style.width = `${tickToPx(ticksPerBar(meter))}px`;
      marker.innerHTML = `<span>${bar + 1}</span>`;
      ruler.appendChild(marker);
    }
    renderLoopRegionOnRuler(ruler, rowStartTick, rowEndTick);

    const rows = document.createElement("div");
    rows.className = "timeline-group-rows";
    rows.replaceChildren(...tracks.map((track) => renderTrackRow(track, rowStartTick, rowEndTick, rowWidth)));
    group.append(ruler, rows);
    groups.push(group);
  }

  els.trackRows.replaceChildren(...groups);
  renderPlayhead();
}

function visibleTimelineTracks() {
  return state.song.tracks.filter((track) => !hiddenTimelineTrackTypes.has(track.type));
}

function renderLoopRegionOnRuler(ruler, rowStartTick, rowEndTick) {
  const region = normalizeLoopRegion(state.loopRegion);
  if (!region || region.startTick >= rowEndTick || region.endTick <= rowStartTick) {
    return;
  }

  const visibleStartTick = Math.max(region.startTick, rowStartTick);
  const visibleEndTick = Math.min(region.endTick, rowEndTick);
  const loopNode = document.createElement("div");
  loopNode.className = "loop-region";
  loopNode.style.left = `${tickToPx(visibleStartTick - rowStartTick)}px`;
  loopNode.style.width = `${Math.max(4, tickToPx(visibleEndTick - visibleStartTick))}px`;
  loopNode.innerHTML = `
    ${visibleStartTick === region.startTick ? '<span class="loop-region-handle is-start" data-loop-action="resize-start"></span>' : ""}
    ${visibleEndTick === region.endTick ? '<span class="loop-region-handle is-end" data-loop-action="resize-end"></span>' : ""}
  `;
  ruler.appendChild(loopNode);
}

function renderTrackRow(track, rowStartTick, rowEndTick, width) {
  const row = document.createElement("div");
  row.className = "timeline-row";
  row.classList.toggle("is-collapsed", isTrackCollapsed(track));
  row.classList.toggle("is-guitar-chord-row", track.type === "chords" && state.chordRenderMode === "guitar");
  const label = document.createElement("div");
  label.className = "row-label";
  label.innerHTML = `
    <span class="row-label-text">
      <strong>${escapeHtml(track.name)}</strong>
      <span>${escapeHtml(labelForTrackInstrument(track))}</span>
    </span>
  `;
  const settingsButton = document.createElement("button");
  settingsButton.className = "row-settings-button";
  settingsButton.type = "button";
  settingsButton.dataset.trackId = track.id;
  settingsButton.setAttribute("aria-label", `Choose sound bank for ${track.name}`);
  settingsButton.setAttribute("title", "Sound bank");
  settingsButton.textContent = "\u2699";
  settingsButton.addEventListener("click", () => openTrackSoundModal(track.id));
  const muteButton = document.createElement("button");
  const pendingUnmute = isTrackPendingUnmute(track.id);
  muteButton.className = "row-mute-button";
  muteButton.classList.toggle("is-muted", track.muted);
  muteButton.classList.toggle("is-pending", pendingUnmute);
  muteButton.type = "button";
  muteButton.dataset.trackId = track.id;
  muteButton.setAttribute("aria-label", pendingUnmute
    ? `Cancel unmute for ${track.name}`
    : `${track.muted ? "Unmute" : "Mute"} ${track.name}`);
  muteButton.setAttribute("aria-pressed", String(!track.muted));
  muteButton.setAttribute("title", pendingUnmute
    ? "Unmuting next measure"
    : (track.muted ? "Track muted" : "Track playing"));
  muteButton.textContent = track.muted ? "\uD83D\uDD07" : "\uD83D\uDD0A";
  muteButton.addEventListener("click", (event) => {
    event.stopPropagation();
    toggleTrackMuted(track.id);
  });
  const controlStack = document.createElement("span");
  controlStack.className = "row-control-stack";
  if (track.type !== "chords") {
    const collapseButton = document.createElement("button");
    const collapsed = isTrackCollapsed(track);
    collapseButton.className = "row-collapse-button";
    collapseButton.classList.toggle("is-collapsed", collapsed);
    collapseButton.type = "button";
    collapseButton.dataset.trackId = track.id;
    collapseButton.setAttribute("aria-label", `${collapsed ? "Expand" : "Collapse"} ${track.name}`);
    collapseButton.setAttribute("aria-expanded", String(!collapsed));
    collapseButton.setAttribute("title", collapsed ? "Expand row" : "Collapse row");
    collapseButton.addEventListener("click", (event) => {
      event.stopPropagation();
      toggleTrackCollapsed(track.id);
    });
    controlStack.appendChild(collapseButton);
  }
  if (!isTrackCollapsed(track)) {
    controlStack.appendChild(settingsButton);
    controlStack.appendChild(muteButton);
  }
  label.appendChild(controlStack);
  const grid = document.createElement("div");
  grid.className = `row-grid is-${track.type}`;
  grid.style.width = `${width}px`;
  grid.dataset.trackId = track.id;
  grid.dataset.rowStartTick = String(rowStartTick);
  renderPreviewCell(track, grid, rowStartTick, rowEndTick);

  if (track.type === "chords") {
    const chords = track.events
      .filter((event) => event.eventType === "chord")
      .sort((a, b) => a.tick - b.tick);
    chords.forEach((event) => {
      if (!eventIntersectsRange(event, rowStartTick, rowEndTick)) {
        return;
      }
      grid.appendChild(renderChordClip(event, clipLayoutForRange(event, rowStartTick, rowEndTick)));
    });
  } else {
    renderReadOnlyEvents(track, grid, rowStartTick, rowEndTick);
  }

  row.append(label, grid);
  return row;
}

function isTrackCollapsed(track) {
  return track.type !== "chords" && state.collapsedTrackIds.includes(track.id);
}

function toggleTrackCollapsed(trackId) {
  if (state.collapsedTrackIds.includes(trackId)) {
    state.collapsedTrackIds = state.collapsedTrackIds.filter((id) => id !== trackId);
  } else {
    state.collapsedTrackIds = [...state.collapsedTrackIds, trackId];
  }

  scheduleAppStateSave();
  renderTimeline();
}

function toggleTrackMuted(trackId) {
  const track = state.song.tracks.find((item) => item.id === trackId);
  if (!track) {
    return;
  }

  if (isTrackPendingUnmute(trackId)) {
    state.pendingTrackUnmutes.delete(trackId);
    renderTimeline();
    if (state.playing) {
      schedulePlayback();
    }
    return;
  }

  if (state.playing && track.muted) {
    state.pendingTrackUnmutes.set(trackId, nextMeasureTickForPlayback());
    renderTimeline();
    schedulePlayback();
    return;
  }

  state.pendingTrackUnmutes.delete(trackId);
  track.muted = !track.muted;
  touchSong();
  renderTimeline();
  if (state.playing) {
    schedulePlayback();
  }
}

function isTrackPendingUnmute(trackId) {
  return state.pendingTrackUnmutes.has(trackId);
}

function nextMeasureTickForPlayback() {
  const barTicks = ticksPerBar();
  const nextBarTick = (Math.floor(state.playheadTick / barTicks) + 1) * barTicks;
  const loopRegion = activeLoopRegion();
  if (loopRegion && nextBarTick >= loopRegion.endTick) {
    return loopRegion.endTick;
  }
  return Math.min(totalTicks(), nextBarTick);
}

function applyPendingTrackUnmute(trackId) {
  const track = state.song.tracks.find((item) => item.id === trackId);
  state.pendingTrackUnmutes.delete(trackId);
  if (!track || !track.muted) {
    return;
  }

  track.muted = false;
  touchSong();
  renderTimeline();
}

function eventIntersectsRange(event, rangeStartTick, rangeEndTick) {
  const eventStartTick = Number(event.tick) || 0;
  const eventEndTick = eventStartTick + (Number(event.durationTicks) || state.snapTicks);
  return eventStartTick < rangeEndTick && eventEndTick > rangeStartTick;
}

function clipLayoutForRange(event, rangeStartTick, rangeEndTick) {
  const eventStartTick = Number(event.tick) || 0;
  const eventDurationTicks = Number(event.durationTicks) || state.snapTicks;
  const visibleStartTick = Math.max(eventStartTick, rangeStartTick);
  const visibleEndTick = Math.min(eventStartTick + eventDurationTicks, rangeEndTick);

  return {
    leftTick: visibleStartTick - rangeStartTick,
    visibleStartTick,
    visibleEndTick,
    durationTicks: Math.max(state.snapTicks, visibleEndTick - visibleStartTick),
  };
}

function renderReadOnlyEvents(track, grid, rowStartTick, rowEndTick) {
  if (!track.events.length) {
    return;
  }

  track.events.forEach((event, index) => {
    if (!Number.isFinite(event.tick) || !eventIntersectsRange(event, rowStartTick, rowEndTick)) {
      return;
    }
    const layout = clipLayoutForRange(event, rowStartTick, rowEndTick);
    grid.appendChild(renderTimelineEventClip(track, event, index, layout));
  });
}

function renderPreviewCell(track, grid, rowStartTick, rowEndTick) {
  const preview = state.previewClip;
  if (!preview || preview.trackId !== track.id || !eventIntersectsRange(preview, rowStartTick, rowEndTick)) {
    return;
  }

  const layout = clipLayoutForRange(preview, rowStartTick, rowEndTick);
  const ghost = document.createElement("div");
  ghost.className = "clip clip-ghost";
  ghost.setAttribute("aria-hidden", "true");
  ghost.style.setProperty("--clip-color", trackColors[track.type] || "#69727f");
  ghost.style.left = `${tickToPx(layout.leftTick)}px`;
  ghost.style.width = `${Math.max(24, tickToPx(layout.durationTicks))}px`;
  grid.appendChild(ghost);
}

function renderTimelineEventClip(track, event, index, layout = {}) {
  const clip = document.createElement("div");
  const editable = stockClipTypes().includes(event.eventType);
  const previewable = isPreviewableTimelineClip(event);
  const expanded = !isTrackCollapsed(track);
  const width = Math.max(24, tickToPx(layout.durationTicks ?? event.durationTicks ?? state.snapTicks));
  const showSettingsButton = isTransformablePatternClip(event) && event.id === state.selectedEventId && width >= 92;
  clip.className = editable ? "clip" : "clip is-placeholder";
  clip.classList.toggle("has-note-preview", previewable && expanded);
  clip.tabIndex = editable ? 0 : -1;
  clip.dataset.eventId = event.id;
  clip.dataset.trackId = track.id;
  clip.style.setProperty("--clip-color", colorForTimelineEventClip(track, event));
  clip.style.left = `${tickToPx(layout.leftTick ?? event.tick)}px`;
  clip.style.width = `${width}px`;
  clip.classList.toggle("is-selected", selectedEventIdSet().has(event.id));
  const clipBody = previewable
    ? renderTimelineClipBody(track, event, layout, expanded)
    : `<span class="clip-symbol"><strong>${escapeHtml(eventLabel(event))}</strong><span>${index + 1}</span></span>`;
  clip.innerHTML = `
    ${editable ? '<span class="resize-handle resize-start" data-action="resize-start"></span>' : ""}
    ${clipBody}
    ${showSettingsButton ? `<button class="clip-settings-button" type="button" aria-label="${track.name} clip settings" title="${track.name} clip settings">\u2699</button>` : ""}
    ${editable ? '<span class="resize-handle resize-end" data-action="resize-end"></span>' : ""}
  `;
  const settingsButton = clip.querySelector(".clip-settings-button");
  settingsButton?.addEventListener("pointerdown", (event) => {
    event.stopPropagation();
  });
  settingsButton?.addEventListener("click", (event) => {
    event.stopPropagation();
    selectEventForAction(event.currentTarget.closest(".clip").dataset.eventId);
    openPatternClipModal();
  });
  if (editable) {
    clip.addEventListener("pointerdown", startClipPointer);
    clip.addEventListener("click", (clickEvent) => {
      if (state.ignoreNextClipClick) {
        state.ignoreNextClipClick = false;
        return;
      }
      setPlayheadToTimelineCellFromPointer(clickEvent);
      selectEvent(event.id, clickEvent.ctrlKey || clickEvent.metaKey, clickEvent.shiftKey);
      auditionTimelineEvent(event.id);
    });
  }
  return clip;
}

function isPreviewableTimelineClip(event) {
  return ["melody-clip", "bass-clip", "drums-clip"].includes(event.eventType);
}

function renderTimelineClipBody(track, event, layout, expanded) {
  if (!expanded) {
    return `<span class="clip-symbol"><strong>${escapeHtml(eventLabel(event))}</strong></span>`;
  }

  const previewNotes = timelineClipPreviewNotes(track, event, layout);
  const notesMarkup = previewNotes.map((note) => `
    <span
      class="clip-preview-note"
      style="left: ${note.leftPercent}%; width: ${note.widthPercent}%; top: ${note.topPercent}%; --note-color: ${note.color};"
    ></span>
  `).join("");

  return `
    <span class="clip-note-preview" aria-hidden="true">${notesMarkup}</span>
    <span class="visually-hidden">${escapeHtml(eventLabel(event))}</span>
  `;
}

function timelineClipPreviewNotes(track, event, layout = {}) {
  const eventStartTick = Number(event.tick) || 0;
  const visibleStartTick = Number.isFinite(Number(layout.visibleStartTick))
    ? Number(layout.visibleStartTick)
    : eventStartTick;
  const visibleDurationTicks = Number(layout.durationTicks) || Number(event.durationTicks) || state.snapTicks;
  const visibleEndTick = visibleStartTick + visibleDurationTicks;
  const noteEvents = Array.isArray(event.events) ? event.events : [];
  const isDrumClip = track.type === "drums" || event.eventType === "drums-clip";
  const chord = isDrumClip ? null : chordAtTick(event.tick) || lastSelectedChordEvent();
  const pitchValues = noteEvents.map((noteEvent) => previewPitchValue(track, event, noteEvent, chord));
  const finitePitches = pitchValues.filter((value) => Number.isFinite(value));
  const drumPitchPositions = isDrumClip ? drumPreviewPitchPositions(finitePitches) : new Map();
  const minPitch = finitePitches.length ? Math.min(...finitePitches) : 0;
  const maxPitch = finitePitches.length ? Math.max(...finitePitches) : minPitch;
  const pitchRange = Math.max(1, maxPitch - minPitch);

  return noteEvents.reduce((notes, noteEvent, noteIndex) => {
    const noteStartTick = eventStartTick + (Number(noteEvent.offsetTicks) || 0);
    const noteDurationTicks = Math.max(1, Number(noteEvent.durationTicks) || state.snapTicks / 2);
    const noteEndTick = noteStartTick + noteDurationTicks;

    if (noteStartTick >= visibleEndTick || noteEndTick <= visibleStartTick) {
      return notes;
    }

    const pitch = pitchValues[noteIndex];
    const normalizedPitch = Number.isFinite(pitch) ? (pitch - minPitch) / pitchRange : 0.5;
    const topPercent = isDrumClip
      ? drumPitchPositions.get(drumPreviewPitchKey(pitch)) ?? 45
      : 82 - (normalizedPitch * 72);
    const leftPercent = ((Math.max(noteStartTick, visibleStartTick) - visibleStartTick) / visibleDurationTicks) * 100;
    const widthPercent = ((Math.min(noteEndTick, visibleEndTick) - Math.max(noteStartTick, visibleStartTick)) / visibleDurationTicks) * 100;

    notes.push({
      leftPercent: clampValue(leftPercent, 0, 100, 0).toFixed(3),
      widthPercent: Math.max(1.2, clampValue(widthPercent, 0, 100, 0)).toFixed(3),
      topPercent: clampValue(topPercent, 8, 82, 45).toFixed(3),
      color: colorForClipPreviewNote(event, noteEvent, chord),
    });

    return notes;
  }, []);
}

function drumPreviewPitchPositions(pitches) {
  const pitchKeys = [...new Set(pitches.map(drumPreviewPitchKey).filter(Boolean))]
    .map(Number)
    .sort((a, b) => a - b);

  if (pitchKeys.length === 0) {
    return new Map();
  }

  if (pitchKeys.length === 1) {
    return new Map([[`${pitchKeys[0]}`, 45]]);
  }

  const maxIndex = pitchKeys.length - 1;
  return new Map(pitchKeys.map((pitch, index) => [
    `${pitch}`,
    8 + ((index / maxIndex) * 74),
  ]));
}

function drumPreviewPitchKey(pitch) {
  if (!Number.isFinite(pitch)) {
    return "";
  }

  const midi = Math.trunc(clampValue(pitch, DRUM_PREVIEW_TOP_MIDI, DRUM_PREVIEW_BOTTOM_MIDI, DRUM_PREVIEW_TOP_MIDI));
  return `${midi}`;
}

function clampValue(value, min, max, fallback) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return fallback;
  }

  return Math.min(max, Math.max(min, number));
}

function previewPitchValue(track, event, noteEvent, chord = null) {
  if (track.type === "drums" || event.eventType === "drums-clip") {
    return Number(noteEvent.midi);
  }

  return relativeSemitoneForChord(noteEvent.relativeSemitone, chord) + ((Number(noteEvent.octave) || 0) * 12);
}

function colorForClipPreviewNote(event, noteEvent, chord = null) {
  const semitone = event.eventType === "drums-clip"
    ? Number(noteEvent.semitone ?? noteEvent.midi)
    : relativeSemitoneForChord(noteEvent.relativeSemitone, chord);
  const degree = event.eventType === "drums-clip"
    ? semitoneDegree(semitone)
    : semitoneDegreeForChord(semitone, chord);
  return degree >= 0 ? chordColors[degree] : chromaticNoteColor;
}

function semitoneDegree(semitone) {
  if (!Number.isFinite(semitone)) {
    return -1;
  }

  const keySignature = getKeySignature();
  const scaleSemitones = keySignature.mode === "minor" ? minorSemitones : majorSemitones;
  const normalizedSemitone = ((Math.trunc(semitone) % 12) + 12) % 12;
  return scaleSemitones.indexOf(normalizedSemitone);
}

function semitoneDegreeForChord(semitone, chord) {
  if (!Number.isFinite(semitone)) {
    return -1;
  }

  return scaleSemitonesForChord(chord).indexOf(positiveMod(semitone, 12));
}

function scaleSemitonesForChord(chord) {
  if (isMinorChord(chord)) {
    return minorSemitones;
  }

  if (isDominantSeventhChord(chord)) {
    return dominantSemitones;
  }

  return majorSemitones;
}

function colorForTimelineEventClip(track, event) {
  if (["melody-clip", "bass-clip"].includes(event.eventType)) {
    const chord = chordAtTick(event.tick);
    if (chord) {
      return isDiatonicChordSymbol(chord.symbol) ? colorForChordSymbol(chord.symbol) : nonDiatonicChordColor;
    }
  }

  return trackColors[track.type] || "#69727f";
}

function eventLabel(event) {
  if (["melody-clip", "bass-clip", "drums-clip"].includes(event.eventType)) {
    return event.patternName || "Clip";
  }
  if (event.eventType === "note") {
    return `MIDI ${event.midi}`;
  }
  if (event.eventType === "drum") {
    return event.drumName || `Drum ${event.midi}`;
  }
  if (event.eventType === "lyric") {
    return event.text || "Lyric";
  }
  return event.eventType || "Event";
}

function guitarFingeringData() {
  return globalThis.GUITAR_FINGERING_DATA || null;
}

function normalizeGuitarRoot(root) {
  const chroma = Note.get(root || "").chroma;
  return Number.isFinite(chroma) ? GUITAR_PITCHES[chroma] : null;
}

function guitarChordToken(symbol) {
  const raw = String(symbol || "").trim();
  const rootMatch = raw.match(/^([A-G](?:#{1,2}|b{1,2})?)/);
  const suffix = rootMatch ? raw.slice(rootMatch[0].length).replace(/\s+/g, "") : raw;
  if (!suffix) {
    return "Major";
  }
  return {
    m: "Minor",
    min: "Minor",
    maj: "Major",
    "\u00b0": "dim",
  }[suffix] || suffix;
}

function guitarVoicingsForSymbol(symbol) {
  const data = guitarFingeringData();
  const rootMatch = String(symbol || "").trim().match(/^([A-G](?:#{1,2}|b{1,2})?)/);
  const root = normalizeGuitarRoot(rootMatch?.[1]);
  const chordToken = guitarChordToken(symbol);
  const rootIndex = data?.roots?.indexOf(root) ?? -1;
  const chordIndex = data?.chords?.indexOf(chordToken) ?? -1;
  const results = data?.voicings?.[String(rootIndex)]?.[String(chordIndex)]?.results;
  if (!Array.isArray(results)) {
    return [];
  }

  return results.map((result, resultIndex) => {
    const positions = [];
    const seen = new Set();
    (result.strings || []).forEach((stringSlots, stringIdx) => {
      const slot = Array.isArray(stringSlots) ? stringSlots[0] : null;
      if (!slot || slot.isMuted) {
        return;
      }

      const string = (result.strings.length - 1) - stringIdx;
      const fret = Number(slot.fretNo) || 0;
      const midi = GUITAR_TUNING_MIDI[string] + fret;
      const key = `${string}:${fret}`;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      positions.push({
        string,
        fret,
        midi,
        symbol: slot.symbol ?? "",
        isRootNote: Boolean(slot.isRootNote),
        bar: slot.bar ?? null,
      });
    });

    return {
      positions,
      firstFret: Number.isFinite(result.firstFret) ? result.firstFret : null,
      label: resultIndex === 0 ? "Primary" : `Variation ${resultIndex + 1}`,
      variation: result.variation ?? resultIndex + 1,
    };
  }).filter((voicing) => voicing.positions.length);
}

function selectedGuitarVoicingForEvent(event) {
  const voicings = guitarVoicingsForSymbol(event?.symbol);
  if (!voicings.length) {
    return { voicings, voicing: null, index: 0 };
  }
  const index = Math.max(0, Math.min(Number(event.guitarVoicingIndex) || 0, voicings.length - 1));
  return { voicings, voicing: voicings[index], index };
}

function guitarMidiNotesForEvent(event) {
  const { voicing } = selectedGuitarVoicingForEvent(event);
  return voicing?.positions
    ?.slice()
    .sort((a, b) => (a.string - b.string) || (a.fret - b.fret))
    .map((position) => position.midi) || [];
}

function setChordGuitarVoicing(eventId, delta) {
  const selectedChords = selectedEventIdSet().has(eventId)
    ? getSelectedChordEvents().map(({ event }) => event)
    : [getChordTrack()?.events.find((item) => item.id === eventId)].filter(Boolean);
  let auditionEvent = null;

  selectedChords.forEach((event) => {
    const { voicings, index } = selectedGuitarVoicingForEvent(event);
    if (voicings.length <= 1) {
      return;
    }
    event.guitarVoicingIndex = (index + delta + voicings.length) % voicings.length;
    auditionEvent = auditionEvent || event;
  });

  if (!auditionEvent) {
    return;
  }
  touchSong();
  renderTimeline();
  auditionChord(auditionEvent);
}

function setChordExtensionForEvents(events, extension) {
  const suffix = normalizeChordExtensionValue(extension);
  let auditionEvent = null;

  events.forEach((event) => {
    const root = event.root || Chord.tokenize(event.symbol)?.[0] || parseChord(event.symbol).root || "C";
    const details = parseChord(`${root}${suffix}`);
    event.symbol = details.symbol;
    event.root = details.root;
    delete event.bass;
    if (details.bass) {
      event.bass = details.bass;
    }
    event.quality = details.quality;
    event.extensions = details.extensions;
    event.voicing = details.voicing;
    event.guitarVoicingIndex = 0;
    auditionEvent = auditionEvent || event;
  });

  if (!auditionEvent) {
    return;
  }

  touchSong();
  renderTimeline();
  populateChordExtensionSelect(els.chordExtensionSelect, auditionEvent.symbol);
  auditionChord(auditionEvent);
}

function setChordPlayStyleByDelta(eventId, delta) {
  const selectedChords = selectedEventIdSet().has(eventId)
    ? getSelectedChordEvents().map(({ event }) => event)
    : [getChordTrack()?.events.find((item) => item.id === eventId)].filter(Boolean);
  if (!selectedChords.length) {
    return;
  }

  selectedChords.forEach((event) => {
    const current = normalizeChordPlayStyle(event.playStyle);
    const currentIndex = Math.max(0, chordPlayStyles.indexOf(current));
    const nextIndex = (currentIndex + delta + chordPlayStyles.length) % chordPlayStyles.length;
    event.playStyle = chordPlayStyles[nextIndex];
  });
  touchSong();
  renderTimeline();
  auditionChord(selectedChords[0]);
}

function renderMiniGuitarChord(event) {
  const { voicings, voicing, index } = selectedGuitarVoicingForEvent(event);
  const playStyleLabel = chordPlayStyleLabel(event.playStyle);
  const playStyleDescription = chordPlayStyleDescription(event.playStyle);
  if (!voicing) {
    return `<span class="clip-symbol"><strong>${escapeHtml(romanForSymbol(event.symbol) || event.symbol)}</strong><span>${escapeHtml(event.symbol)}</span></span>`;
  }

  const positions = voicing.positions || [];
  const minFret = positions.filter((position) => position.fret > 0).reduce((min, position) => Math.min(min, position.fret), Infinity);
  const firstFret = Math.max(1, Number.isFinite(minFret) ? minFret : (voicing.firstFret || 1));
  const visibleFrets = Array.from({ length: 4 }, (_unused, idx) => firstFret + idx);
  const positionMap = new Map(positions.map((position) => [`${position.string}:${position.fret}`, position]));
  const openStrings = new Set(positions.filter((position) => position.fret === 0).map((position) => position.string));
  const playedStrings = new Set(positions.map((position) => position.string));
  const strings = ["E", "A", "D", "G", "B", "E"];

  const headers = strings.map((name, string) => {
    const marker = openStrings.has(string) ? "O" : playedStrings.has(string) ? "" : "X";
    return `<span class="mini-guitar-header"><span>${name}</span><b>${marker}</b></span>`;
  }).join("");
  const fretRows = visibleFrets.map((fret) => `
    <span class="mini-guitar-fret">${fret}</span>
    ${strings.map((_name, string) => {
      const position = positionMap.get(`${string}:${fret}`);
      return `<span class="mini-guitar-cell">${position ? `<b class="${position.isRootNote ? "is-root" : ""}">${escapeHtml(position.symbol || "")}</b>` : ""}</span>`;
    }).join("")}
  `).join("");

  return `
    <div class="clip-guitar-chord" aria-label="${escapeHtml(event.symbol)} guitar fingering ${index + 1} of ${voicings.length}">
      <div class="clip-guitar-title"><strong>${escapeHtml(romanForSymbol(event.symbol) || event.symbol)}</strong><span>${escapeHtml(event.symbol)}</span></div>
      <div class="mini-guitar-board">
        <span class="mini-guitar-spacer"></span>
        ${headers}
        ${fretRows}
      </div>
      <span class="clip-guitar-playstyle" title="${escapeHtml(playStyleDescription)}">
        <button class="clip-playstyle-button is-prev" type="button" aria-label="Previous playstyle, currently ${escapeHtml(playStyleLabel)}" title="Previous playstyle">←</button>
        <span>${escapeHtml(playStyleLabel)}</span>
        <button class="clip-playstyle-button is-next" type="button" aria-label="Next playstyle, currently ${escapeHtml(playStyleLabel)}" title="Next playstyle">→</button>
      </span>
    </div>
  `;
}

function renderChordClip(event, layout = {}) {
  const clip = document.createElement("div");
  const width = Math.max(24, tickToPx(layout.durationTicks ?? event.durationTicks ?? state.snapTicks));
  const showSettingsButton = event.id === state.selectedEventId && width >= 92;
  const showGuitarChord = state.chordRenderMode === "guitar";
  const { voicings } = showGuitarChord ? selectedGuitarVoicingForEvent(event) : { voicings: [] };
  const diatonic = isDiatonicChordSymbol(event.symbol);
  clip.className = "clip";
  clip.classList.toggle("is-guitar-chord", showGuitarChord);
  clip.classList.toggle("is-non-diatonic", !diatonic);
  clip.tabIndex = 0;
  clip.dataset.eventId = event.id;
  clip.dataset.trackId = "track_chords";
  clip.style.setProperty("--clip-color", diatonic ? colorForChordSymbol(event.symbol) : nonDiatonicChordColor);
  clip.style.left = `${tickToPx(layout.leftTick ?? event.tick)}px`;
  clip.style.width = `${width}px`;
  clip.classList.toggle("is-selected", selectedEventIdSet().has(event.id));
  clip.innerHTML = `
    <span class="resize-handle resize-start" data-action="resize-start"></span>
    ${showGuitarChord ? `
      <span class="clip-control-pair is-voicing" aria-label="Fingering controls">
        <button class="clip-voicing-button is-prev" type="button" aria-label="Previous fingering" title="Previous fingering" ${voicings.length <= 1 ? "disabled" : ""}>↑</button>
        <button class="clip-voicing-button is-next" type="button" aria-label="Next fingering" title="Next fingering" ${voicings.length <= 1 ? "disabled" : ""}>↓</button>
      </span>
      ${renderMiniGuitarChord(event)}
    ` : `<span class="clip-symbol"><strong>${escapeHtml(romanForSymbol(event.symbol) || event.symbol)}</strong><span>${escapeHtml(event.symbol)}</span></span>`}
    ${showSettingsButton ? '<button class="clip-settings-button" type="button" aria-label="Chord clip settings" title="Chord clip settings">\u2699</button>' : ""}
    <span class="resize-handle resize-end" data-action="resize-end"></span>
  `;
  const bindClipButton = (selector, onClick) => {
    const button = clip.querySelector(selector);
    button?.addEventListener("pointerdown", (buttonEvent) => {
      buttonEvent.stopPropagation();
    });
    button?.addEventListener("click", (buttonEvent) => {
      buttonEvent.stopPropagation();
      onClick();
    });
  };
  bindClipButton(".clip-voicing-button.is-prev", () => setChordGuitarVoicing(event.id, -1));
  bindClipButton(".clip-voicing-button.is-next", () => setChordGuitarVoicing(event.id, 1));
  bindClipButton(".clip-playstyle-button.is-prev", () => setChordPlayStyleByDelta(event.id, -1));
  bindClipButton(".clip-playstyle-button.is-next", () => setChordPlayStyleByDelta(event.id, 1));
  const settingsButton = clip.querySelector(".clip-settings-button");
  settingsButton?.addEventListener("pointerdown", (event) => {
    event.stopPropagation();
  });
  settingsButton?.addEventListener("click", (event) => {
    event.stopPropagation();
    selectEventForAction(event.currentTarget.closest(".clip").dataset.eventId);
    openChordClipModal();
  });
  clip.addEventListener("pointerdown", startClipPointer);
  clip.addEventListener("click", (clickEvent) => {
    if (state.ignoreNextClipClick) {
      state.ignoreNextClipClick = false;
      return;
    }
    setPlayheadToTimelineCellFromPointer(clickEvent);
    selectEvent(event.id, clickEvent.ctrlKey || clickEvent.metaKey, clickEvent.shiftKey);
    auditionTimelineEvent(event.id);
  });
  return clip;
}

function romanForSymbol(symbol) {
  return getScaleChords().find((chord) => chord.symbol === symbol)?.roman || "";
}

function colorForChordSymbol(symbol) {
  return getScaleChords().find((chord) => chord.symbol === symbol)?.color || chordColors[0];
}

function currentScaleChromas() {
  const keySignature = getKeySignature();
  const mode = keySignature.mode === "minor" ? "minor" : "major";
  const scaleNotes = Scale.get(`${keySignature.key} ${mode}`).notes;
  const notes = scaleNotes.length ? scaleNotes : Scale.get(`${keySignature.key} major`).notes;
  return new Set(notes.map((note) => Note.chroma(note)).filter((chroma) => Number.isFinite(chroma)));
}

function isDiatonicChordSymbol(symbol) {
  const scaleChromas = currentScaleChromas();
  if (!scaleChromas.size) {
    return true;
  }

  const chord = Chord.get(String(symbol || ""));
  const chordNotes = chord.notes.length ? chord.notes : fallbackChordNotes(symbol);
  const bassChroma = chord.bass ? Note.chroma(chord.bass) : null;
  const chordChromas = chordNotes
    .map((note) => Note.chroma(note))
    .concat(Number.isFinite(bassChroma) ? [bassChroma] : [])
    .filter((chroma) => Number.isFinite(chroma));

  return chordChromas.length > 0 && chordChromas.every((chroma) => scaleChromas.has(chroma));
}

function isTransformablePatternClip(event) {
  return event?.eventType === "melody-clip" || event?.eventType === "bass-clip" || event?.eventType === "drums-clip";
}

function normalizePatternTransform(value) {
  return Math.max(-8, Math.min(8, Math.trunc(Number(value) || 0)));
}

function trackSupportsOctaveShift(track) {
  return ["chords", "melody", "bass"].includes(track?.type);
}

function renderPlayhead({ scrollIntoView = false, scrollBehavior = "auto" } = {}) {
  const rowTicks = ticksPerTimelineRow();
  const playheadTick = state.playbackPlayheadTick ?? state.playheadTick;
  const rowIndex = Math.min(timelineRowCount() - 1, Math.floor(playheadTick / rowTicks));
  const rowStartTick = rowIndex * rowTicks;
  const rowTick = Math.min(rowTicks, Math.max(0, playheadTick - rowStartTick));
  const playheadX = tickToPx(rowTick);
  const groupRows = els.trackRows.querySelector(`[data-group-index="${rowIndex}"] .timeline-group-rows`);

  if (!groupRows) {
    return;
  }

  els.playhead.style.top = `${groupRows.offsetTop}px`;
  els.playhead.style.height = `${groupRows.offsetHeight}px`;
  els.playhead.style.transform = `translateX(${playheadX}px)`;
  if (scrollIntoView) {
    scrollPlayheadIntoView(groupRows, playheadX, scrollBehavior);
  }
}

function scrollPlayheadIntoView(groupRows, playheadX, behavior = "auto") {
  const scroller = els.timelineScroller;
  const group = groupRows.closest(".timeline-group");
  const nextGroup = group?.nextElementSibling?.classList?.contains("timeline-group")
    ? group.nextElementSibling
    : null;
  const nextGroupPeek = nextGroup
    ? Math.min(nextGroup.offsetHeight, Math.max(56, scroller.clientHeight * 0.22))
    : 0;
  const labelWidth = els.trackRows.querySelector(".row-label")?.offsetWidth || 0;
  const playheadLeft = labelWidth + playheadX;
  const visibleLeft = scroller.scrollLeft + labelWidth;
  const visibleRight = scroller.scrollLeft + scroller.clientWidth - 16;
  const nextScrollLeft = playheadLeft > visibleRight
    ? playheadLeft - scroller.clientWidth + 32
    : playheadLeft < visibleLeft
      ? playheadLeft - labelWidth
      : scroller.scrollLeft;

  const visibleTop = scroller.scrollTop;
  const visibleBottom = visibleTop + scroller.clientHeight;
  const groupTop = groupRows.offsetTop;
  const groupBottom = groupTop + groupRows.offsetHeight;
  const groupBottomWithPeek = groupBottom + nextGroupPeek;
  const nextScrollTop = groupBottomWithPeek > visibleBottom
    ? groupBottomWithPeek - scroller.clientHeight
    : groupTop < visibleTop
      ? groupTop
      : scroller.scrollTop;

  if (nextScrollLeft !== scroller.scrollLeft || nextScrollTop !== scroller.scrollTop) {
    scroller.scrollTo({
      top: Math.max(0, nextScrollTop),
      left: Math.max(0, nextScrollLeft),
      behavior,
    });
  }
}

function timelineCellStartTickFromPointer(event) {
  const grid = event.target.closest(".row-grid");
  if (!grid) {
    return null;
  }

  const rect = grid.getBoundingClientRect();
  const rowStartTick = Number(grid.dataset.rowStartTick) || 0;
  const clickedTick = rowStartTick + pxToTick(event.clientX - rect.left);
  return Math.min(totalTicks(), barStartTick(clickedTick));
}

function setPlayheadToTimelineCellFromPointer(event) {
  const tick = timelineCellStartTickFromPointer(event);
  if (!Number.isFinite(tick)) {
    return false;
  }

  state.playheadTick = tick;
  return true;
}

function applyPaletteChord(symbol) {
  const preview = state.previewClip?.trackType === "chords" ? state.previewClip : null;
  clearPreviewClip();
  state.activePatternTrackId = null;
  state.activePanelTrackType = "chords";
  if (preview) {
    insertChordAtTick(symbol, preview.tick);
    return;
  }

  const track = getChordTrack();
  const selectedChordEvents = getSelectedChordEvents().map(({ event }) => event);
  if (selectedChordEvents.length) {
    selectedChordEvents.forEach((event) => replaceChordEvent(event, symbol));
    touchSong();
    render();
    auditionChord(selectedChordEvents[0]);
    return;
  }

  const nextEvent = track.events
    .filter((event) => event.eventType === "chord" && event.tick >= state.playheadTick)
    .sort((a, b) => a.tick - b.tick)[0];
  if (nextEvent) {
    replaceChordEvent(nextEvent, symbol);
    state.playheadTick = Math.min(totalTicks(), nextEvent.tick + (nextEvent.durationTicks || state.snapTicks));
    touchSong();
    render();
    auditionChord(nextEvent);
    return;
  }

  insertChordAtPlayhead(symbol);
}

function replaceChordEvent(event, symbol) {
  const replacement = createChordEvent(event.tick, event.durationTicks || state.snapTicks, symbol);
  Object.assign(event, {
    ...replacement,
    id: event.id,
    tick: event.tick,
    durationTicks: event.durationTicks || state.snapTicks,
    playStyle: normalizeChordPlayStyle(event.playStyle),
    guitarVoicingIndex: 0,
  });
}

function insertChordAtPlayhead(symbol) {
  insertChordAtTick(symbol, snapGrid(state.playheadTick));
}

function insertChordAtTick(symbol, tick) {
  const track = getChordTrack();
  const durationTicks = Math.max(state.snapTicks, ticksPerBar());
  ensureSongDuration(tick + durationTicks);
  const event = createChordEvent(tick, durationTicks, symbol);
  track.events.push(event);
  track.events.sort((a, b) => a.tick - b.tick);
  state.playheadTick = Math.min(totalTicks(), event.tick + event.durationTicks);
  clearSelection();
  touchSong();
  render();
  scrollEventStackIntoView(event.id);
  auditionChord(event);
}

function selectEventForAction(id) {
  if (!selectedEventIdSet().has(id)) {
    selectEvent(id);
    return;
  }

  const selected = findEventById(id);
  if (!selected) {
    return;
  }
  state.selectedEventId = id;
  state.activePatternTrackId = null;
  state.activePanelTrackType = selected.track.type;
  if (selected.event.eventType === "chord") {
    state.lastSelectedChordId = id;
  }
  scheduleAppStateSave();
  render();
}

function selectEvent(id, additive = false, range = false) {
  const selected = findEventById(id);
  if (!selected) {
    return;
  }

  clearPreviewClip();
  state.activePatternTrackId = null;
  state.activePanelTrackType = selected.track.type;
  if (range) {
    selectEventRange(id);
  } else if (additive) {
    const ids = selectedEventIdSet();
    if (ids.has(id) && ids.size > 1) {
      ids.delete(id);
      state.selectedEventIds = [...ids];
      state.selectedEventId = state.selectedEventIds.at(-1) || null;
    } else {
      ids.add(id);
      state.selectedEventIds = [...ids];
      state.selectedEventId = id;
    }
    state.selectionAnchorEventId = id;
  } else {
    state.selectedEventId = id;
    state.selectedEventIds = [id];
    state.selectionAnchorEventId = id;
  }
  if (selected.event.eventType === "chord") {
    state.lastSelectedChordId = id;
  }
  scheduleAppStateSave();
  render();
}

function selectEventRange(id) {
  const anchorId = state.selectionAnchorEventId || state.selectedEventId || state.selectedEventIds.at(-1);
  const anchor = timelineSelectionPointForEvent(anchorId);
  const target = timelineSelectionPointForEvent(id);

  if (!anchor || !target) {
    state.selectedEventId = id;
    state.selectedEventIds = [id];
    state.selectionAnchorEventId = id;
    return;
  }

  const rowStart = Math.min(anchor.rowTrackIndex, target.rowTrackIndex);
  const rowEnd = Math.max(anchor.rowTrackIndex, target.rowTrackIndex);
  const tickStart = Math.min(anchor.rowTick, target.rowTick);
  const tickEnd = Math.max(anchor.rowTick, target.rowTick) + state.snapTicks;

  state.selectedEventIds = getEditableTimelineEventsInOrder()
    .filter(({ track, event }) => timelineEventIntersectsSelectionRectangle(track, event, {
      rowStart,
      rowEnd,
      tickStart,
      tickEnd,
    }))
    .map(({ event }) => event.id);
  state.selectedEventId = id;
}

function timelineSelectionPointForEvent(eventId) {
  const selected = findEventById(eventId);
  if (!selected) {
    return null;
  }

  const rowTicks = ticksPerTimelineRow();
  const tracks = visibleTimelineTracks();
  const trackIndex = tracks.findIndex((track) => track.id === selected.track.id);
  if (trackIndex === -1) {
    return null;
  }

  const tick = Math.max(0, Number(selected.event.tick) || 0);
  const rowIndex = Math.min(timelineRowCount() - 1, Math.floor(tick / rowTicks));
  return {
    rowIndex,
    trackIndex,
    rowTrackIndex: (rowIndex * tracks.length) + trackIndex,
    rowTick: tick - (rowIndex * rowTicks),
  };
}

function timelineEventIntersectsSelectionRectangle(track, event, rectangle) {
  const rowTicks = ticksPerTimelineRow();
  const tracks = visibleTimelineTracks();
  const trackIndex = tracks.findIndex((item) => item.id === track.id);
  if (trackIndex === -1) {
    return false;
  }

  const eventStartTick = Math.max(0, Number(event.tick) || 0);
  const eventEndTick = eventStartTick + Math.max(state.snapTicks, Number(event.durationTicks) || state.snapTicks);
  const firstRow = Math.max(0, Math.floor(eventStartTick / rowTicks));
  const lastRow = Math.min(timelineRowCount() - 1, Math.floor(Math.max(eventStartTick, eventEndTick - 1) / rowTicks));

  for (let rowIndex = firstRow; rowIndex <= lastRow; rowIndex += 1) {
    const rowTrackIndex = (rowIndex * tracks.length) + trackIndex;
    if (rowTrackIndex < rectangle.rowStart || rowTrackIndex > rectangle.rowEnd) {
      continue;
    }

    const rowStartTick = rowIndex * rowTicks;
    const visibleStartTick = Math.max(eventStartTick, rowStartTick);
    const visibleEndTick = Math.min(eventEndTick, rowStartTick + rowTicks);
    const localStartTick = visibleStartTick - rowStartTick;
    const localEndTick = visibleEndTick - rowStartTick;
    if (localStartTick < rectangle.tickEnd && localEndTick > rectangle.tickStart) {
      return true;
    }
  }

  return false;
}

function scrollEventStackIntoView(eventId) {
  const clip = [...els.trackRows.querySelectorAll(".clip[data-event-id]")]
    .find((item) => item.dataset.eventId === eventId);
  const group = clip?.closest(".timeline-group");
  if (!clip || !group) {
    return;
  }

  const scroller = els.timelineScroller;
  const visibleTop = scroller.scrollTop;
  const visibleBottom = visibleTop + scroller.clientHeight;
  const groupTop = group.offsetTop;
  const groupBottom = groupTop + group.offsetHeight;
  const nextScrollTop = groupBottom > visibleBottom
    ? groupBottom - scroller.clientHeight
    : groupTop < visibleTop
      ? groupTop
      : visibleTop;

  const labelWidth = els.trackRows.querySelector(".row-label")?.offsetWidth || 0;
  const visibleLeft = scroller.scrollLeft + labelWidth;
  const visibleRight = scroller.scrollLeft + scroller.clientWidth;
  const clipLeft = clip.offsetLeft + labelWidth;
  const clipRight = clipLeft + clip.offsetWidth;
  const nextScrollLeft = clipRight > visibleRight
    ? clipRight - scroller.clientWidth
    : clipLeft < visibleLeft
      ? clipLeft - labelWidth
      : scroller.scrollLeft;

  scroller.scrollTo({
    top: Math.max(0, nextScrollTop),
    left: Math.max(0, nextScrollLeft),
    behavior: "smooth",
  });
}

function updateSelectedClipClasses() {
  const ids = selectedEventIdSet();
  els.trackRows.querySelectorAll(".clip[data-event-id]").forEach((clip) => {
    clip.classList.toggle("is-selected", ids.has(clip.dataset.eventId));
  });
}

function selectPreviewClip(track, tick) {
  setPreviewClip(track, tick);
  state.activePatternTrackId = track.type === "chords" ? null : track.id;
  state.activePanelTrackType = track.type;
  state.lastInsertedClipId = null;
  clearSelection();
  state.playheadTick = tick;
  scheduleAppStateSave();
  render();
}

function setPreviewClip(track, tick) {
  state.previewClip = {
    id: "preview_clip",
    eventType: `${track.type}-preview`,
    trackId: track.id,
    trackType: track.type,
    tick,
    durationTicks: ticksPerBar(),
  };
}

function getActivePatternTrack() {
  return state.song.tracks.find((track) => track.id === state.activePatternTrackId) || null;
}

function getPatternTargetTrack(trackType) {
  const preview = state.previewClip;
  const activeTrack = getActivePatternTrack();
  return state.song.tracks.find((item) => (
    item.id === (preview?.trackId || activeTrack?.id)
    && item.type === trackType
  )) || state.song.tracks.find((item) => item.type === trackType) || null;
}

function applyStockPatternClip(trackType, patternId) {
  const selectedClips = getSelectedEventsForTrackType(trackType);
  if (shouldInsertAtPreviewTarget(trackType, selectedClips)) {
    insertStockPatternClip(trackType, patternId);
    return;
  }

  if (selectedClips.length) {
    selectedClips.forEach(({ track, event }) => replaceStockPatternClip(track, event, patternId, false));
    touchSong();
    render();
    auditionTimelineEvent(selectedClips[0].event.id);
    return;
  }

  const selected = getSelectedTimelineEvent();
  if (selected?.track.type === trackType && stockPatterns[trackType]) {
    replaceStockPatternClip(selected.track, selected.event, patternId);
    return;
  }

  insertStockPatternClip(trackType, patternId);
}

function shouldInsertAtPreviewTarget(trackType, selectedClips) {
  return Boolean(
    state.previewClip?.trackType === trackType
    && selectedClips.length === 1
    && selectedClips[0].event.id === state.lastInsertedClipId
  );
}

function replaceStockPatternClip(track, event, patternId, shouldRender = true) {
  const pattern = (stockPatterns[track.type] || []).find((item) => item.id === patternId);
  if (!pattern) {
    return;
  }

  const octaveShift = normalizePatternTransform(event.octaveShift);
  delete event.inversion;
  Object.assign(event, createStockPatternEvent(track.type, pattern, event.tick, event.durationTicks || ticksPerBar(), track), {
    id: event.id,
    octaveShift,
  });
  state.activePanelTrackType = track.type;
  if (!shouldRender) {
    return;
  }
  touchSong();
  render();
  auditionTimelineEvent(event.id);
}

function insertStockPatternClip(trackType, patternId) {
  const preview = state.previewClip;
  const activeTrack = getActivePatternTrack();
  const track = state.song.tracks.find((item) => (
    item.id === (preview?.trackId || activeTrack?.id)
    && item.type === trackType
  ));
  const pattern = (stockPatterns[trackType] || []).find((item) => item.id === patternId);
  if (!track || !pattern || (!preview && activeTrack?.id !== track.id)) {
    return;
  }

  const event = createStockPatternEvent(trackType, pattern, preview?.tick ?? barStartTick(state.playheadTick), undefined, track);
  track.events.push(event);
  track.events.sort((a, b) => a.tick - b.tick);
  state.playheadTick = Math.min(totalTicks(), event.tick + event.durationTicks);
  ensureSongDuration(state.playheadTick + ticksPerBar());
  setPreviewClip(track, state.playheadTick);
  state.activePatternTrackId = track.id;
  state.activePanelTrackType = trackType;
  state.selectedEventId = event.id;
  state.selectedEventIds = [event.id];
  state.selectionAnchorEventId = event.id;
  state.lastInsertedClipId = event.id;
  touchSong();
  render();
  auditionTimelineEvent(event.id);
}

function createStockPatternEvent(trackType, pattern, tick, durationTicks = patternDurationTicks(pattern), track = null) {
  return {
    id: makeId(trackType),
    eventType: `${trackType}-clip`,
    patternId: pattern.id,
    serverClipId: pattern.serverClipId || pattern.id,
    patternName: pattern.name,
    tick,
    durationTicks,
    rootMode: trackType === "drums" ? "fixed" : "chord-root",
    octaveShift: trackType === "drums"
      ? 0
      : normalizePatternTransform(normalizePatternTransform(pattern?.octaveShift) - normalizePatternTransform(track?.octaveShift)),
    bars: validClipBars(pattern?.bars),
    stepsPerBar: validClipStepsPerBar(pattern?.stepsPerBar, DEFAULT_CLIP_STEPS_PER_BAR),
    gridOctaves: validClipGridOctaves(pattern?.gridOctaves),
    events: pattern.events.map((event) => ({
      ...event,
      offsetTicks: event.offsetTicks ?? beatsToTick(event.offsetBeats),
      durationTicks: event.durationTicks ?? beatsToTick(event.durationBeats),
    })),
  };
}

function patternDurationTicks(pattern) {
  const durationTicks = Number(pattern?.durationTicks);
  return Number.isFinite(durationTicks) && durationTicks > 0 ? durationTicks : ticksPerBar();
}

function clearPreviewClip() {
  state.previewClip = null;
}

function clearPreviewOnOutsidePointer(event) {
  if (!state.previewClip || pointerStartedInPreviewArea(event)) {
    return;
  }

  clearPreviewClip();
  state.activePatternTrackId = null;
  state.activePanelTrackType = "chords";
  render();
}

function pointerStartedInPreviewArea(event) {
  return event.composedPath().some((node) => (
    node?.classList?.contains("timeline-wrap") || node?.classList?.contains("transport-tool-panel")
  ));
}

function deleteSelectedEvent() {
  const selectedEvents = getSelectedTimelineEvents();
  if (!selectedEvents.length) {
    return;
  }

  const removedIds = new Set(selectedEvents.map(({ event }) => event.id));
  const previousEvent = selectedEvents
    .slice()
    .sort((a, b) => a.event.tick - b.event.tick)[0];
  const deletionTrack = previousEvent?.track || null;
  const deletionTick = previousEvent?.event.tick ?? 0;
  state.song.tracks.forEach((track) => {
    track.events = track.events.filter((event) => !removedIds.has(event.id));
  });
  cancelClipAudition();
  clearSelection();
  if (deletionTrack && deletionTrack.type !== "chords") {
    setPreviewClip(deletionTrack, barStartTick(deletionTick));
  }
  const nextSelection = deletionTrack ? nextClipDeletionTarget(deletionTrack, deletionTick) : null;
  if (nextSelection) {
    state.selectedEventId = nextSelection.event.id;
    state.selectedEventIds = [nextSelection.event.id];
    state.selectionAnchorEventId = nextSelection.event.id;
    state.activePatternTrackId = deletionTrack.id;
    state.activePanelTrackType = deletionTrack.type;
    state.playheadTick = Math.min(totalTicks(), nextSelection.event.tick);
  } else if (deletionTrack && deletionTrack.type !== "chords") {
    state.activePatternTrackId = deletionTrack.id;
    state.activePanelTrackType = deletionTrack.type;
    state.playheadTick = Math.min(totalTicks(), barStartTick(deletionTick));
  } else {
    state.playheadTick = previousEvent ? Math.min(totalTicks(), previousEvent.event.tick) : 0;
  }
  state.lastInsertedClipId = null;
  touchSong();
  render();
  if (state.playing) {
    schedulePlayback();
  }
}

function nextClipDeletionTarget(track, tick) {
  const clips = track.events
    .filter((event) => stockClipTypes().includes(event.eventType))
    .sort((a, b) => a.tick - b.tick);
  const event = clips.filter((item) => item.tick < tick).at(-1)
    || clips.find((item) => item.tick >= tick)
    || null;
  return event ? { track, event } : null;
}

function isEditableTimelineClip(event) {
  return event?.eventType === "chord" || stockClipTypes().includes(event?.eventType);
}

function nextDragLayouts(drag, requestedDelta) {
  const items = (drag.items || []).filter((item) => findEventById(item.eventId));
  if (!items.length) {
    return [];
  }

  const targetDelta = clampDragDelta(drag, requestedDelta, items);
  return layoutsForDragDelta(drag, targetDelta, items);
}

function clampDragDelta(drag, requestedDelta, items) {
  const snappedDelta = snapDelta(requestedDelta);
  if (snappedDelta === 0) {
    return 0;
  }
  if (areDragLayoutsValid(layoutsForDragDelta(drag, snappedDelta, items))) {
    return snappedDelta;
  }

  const direction = snappedDelta > 0 ? 1 : -1;
  const step = Math.max(1, state.snapTicks) * direction;
  let acceptedDelta = 0;
  for (let candidate = step; direction > 0 ? candidate <= snappedDelta : candidate >= snappedDelta; candidate += step) {
    const layouts = layoutsForDragDelta(drag, candidate, items);
    if (!areDragLayoutsValid(layouts)) {
      break;
    }
    acceptedDelta = candidate;
  }
  return acceptedDelta;
}

function layoutsForDragDelta(drag, delta, items = drag.items || []) {
  return items.map((item) => {
    if (drag.action === "resize-start") {
      return {
        ...item,
        tick: item.tick + delta,
        durationTicks: item.durationTicks - delta,
      };
    }
    if (drag.action === "resize-end") {
      return {
        ...item,
        tick: item.tick,
        durationTicks: item.durationTicks + delta,
      };
    }
    return {
      ...item,
      tick: item.tick + delta,
      durationTicks: item.durationTicks,
    };
  });
}

function areDragLayoutsValid(layouts) {
  const layoutById = new Map(layouts.map((layout) => [layout.eventId, layout]));
  if (layouts.some((layout) => layout.tick < 0 || layout.durationTicks < state.snapTicks)) {
    return false;
  }

  return state.song.tracks.every((track) => {
    const ranges = track.events
      .filter(isEditableTimelineClip)
      .map((event) => ({
        id: event.id,
        tick: layoutById.get(event.id)?.tick ?? event.tick,
        durationTicks: layoutById.get(event.id)?.durationTicks ?? (event.durationTicks || state.snapTicks),
      }))
      .sort((a, b) => a.tick - b.tick);

    return ranges.every((range, index) => {
      const next = ranges[index + 1];
      return !next || range.tick + range.durationTicks <= next.tick;
    });
  });
}

function startClipPointer(event) {
  if (event.button !== 0) {
    return;
  }

  const hadPreviewClip = Boolean(state.previewClip);
  clearPreviewClip();
  state.activePatternTrackId = null;
  if (hadPreviewClip) {
    renderToolPanel();
  }

  const node = event.currentTarget;
  const selected = findEventById(node.dataset.eventId);
  if (!selected) {
    return;
  }
  const { track, event: clip } = selected;

  if (event.shiftKey || event.ctrlKey || event.metaKey) {
    selectEvent(clip.id, event.ctrlKey || event.metaKey, event.shiftKey);
    state.ignoreNextClipClick = true;
    event.preventDefault();
    return;
  }

  const action = event.target.dataset.action || "move";
  if (!selectedEventIdSet().has(clip.id)) {
    state.selectedEventId = clip.id;
    state.selectedEventIds = [clip.id];
    state.selectionAnchorEventId = clip.id;
  } else {
    state.selectedEventId = clip.id;
  }
  const dragItems = getSelectedTimelineEvents().map(({ track: itemTrack, event: itemEvent }) => ({
    eventId: itemEvent.id,
    trackId: itemTrack.id,
    tick: itemEvent.tick,
    durationTicks: itemEvent.durationTicks || state.snapTicks,
  }));
  state.drag = {
    action,
    eventId: clip.id,
    trackId: track.id,
    startX: event.clientX,
    songSnapshot: songHistorySnapshot(),
    tick: clip.tick,
    durationTicks: clip.durationTicks || state.snapTicks,
    items: dragItems.length ? dragItems : [{
      eventId: clip.id,
      trackId: track.id,
      tick: clip.tick,
      durationTicks: clip.durationTicks || state.snapTicks,
    }],
  };
  state.activePanelTrackType = track.type;
  if (clip.eventType === "chord") {
    state.lastSelectedChordId = clip.id;
  }
  scheduleAppStateSave();
  node.setPointerCapture(event.pointerId);
  updateSelectedClipClasses();
  event.preventDefault();
}

function moveClipPointer(event) {
  if (!state.drag) {
    return;
  }

  const delta = snapDelta(pxToTick(event.clientX - state.drag.startX));
  if (delta === 0) {
    return;
  }
  const nextLayouts = nextDragLayouts(state.drag, delta);
  if (!nextLayouts.length) {
    return;
  }

  nextLayouts.forEach((layout) => {
    const selected = findEventById(layout.eventId);
    if (!selected) {
      return;
    }
    selected.event.tick = layout.tick;
    selected.event.durationTicks = layout.durationTicks;
    ensureSongDuration(layout.tick + layout.durationTicks);
  });

  touchSong({ history: false, updateBaseline: false });
  renderTimeline();
}

function endClipPointer() {
  if (state.drag?.songSnapshot) {
    commitSongHistoryChange(state.drag.songSnapshot);
  }
  state.drag = null;
}

async function ensureAudio() {
  if (state.audio.error) {
    throw new Error(state.audio.error);
  }

  if (state.audio.ready) {
    await Tone.start();
    syncToneTempo();
    return;
  }

  if (state.audio.loading) {
    await state.audioLoadPromise;
    return;
  }

  state.audio.loading = true;
  state.audioLoadPromise = initializeToneAudio();

  try {
    await state.audioLoadPromise;
    state.audio.ready = true;
    state.audio.loading = false;
    state.audio.error = "";
    state.audioLoadPromise = null;
  } catch (error) {
    state.audioLoadPromise = null;
    state.audio.ready = false;
    state.audio.loading = false;
    state.audio.error = error instanceof Error ? error.message : "SoundFont failed to load";
    renderControls();
    throw error;
  }
}

async function initializeToneAudio() {
  await Tone.start();
  Tone.Transport.PPQ = PPQ;
  syncToneTempo();
  await ensureSamplerForSoundfont(soundfontIdForProgram(state.selectedProgram));
  await ensureSamplerForSoundfont("drums");
}

function syncToneTempo() {
  Tone.Transport.bpm.value = getTempo();
}

function soundfontConfig(id) {
  return FATBOY_SOUNDFONTS.find((config) => config.id === id) || FATBOY_SOUNDFONTS[0];
}

function soundfontIdForProgram(program) {
  return normalizeSoundfontId(program?.instrumentName || program?.value);
}

function soundfontIdForTrack(track) {
  if (track?.type === "drums") {
    return normalizeSoundfontId(track.instrumentName, "drums");
  }
  if (track?.type === "chords") {
    return soundfontIdForProgram(state.selectedProgram);
  }
  return normalizeSoundfontId(track?.instrumentName, defaultInstrumentForTrackType(track?.type));
}

function globalSoundfont(globalName) {
  return window.MIDI?.Soundfont?.[globalName] || null;
}

function soundfontScriptSrc(config) {
  if (config.id === "drums") {
    return "/sounds/drums/drums-mp3.js";
  }
  if (config.id === "casio_vl_1") {
    return "/sounds/drums/casio/vl-1-ogg.js";
  }
  if (config.id === "linn_linndrum") {
    return "/sounds/drums/linn/linndrum-ogg.js";
  }
  if (config.id.startsWith("roland_")) {
    return `/sounds/drums/roland/${config.id.replace("roland_", "")}-ogg.js`;
  }
  return `/sounds/fatboy/${config.globalName}-ogg.js`;
}

async function ensureSoundfontData(config) {
  if (globalSoundfont(config.globalName)) {
    return;
  }

  const src = soundfontScriptSrc(config);
  const existing = soundfontScriptPromises.get(src);
  if (existing) {
    await existing;
    return;
  }

  const loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => {
      if (globalSoundfont(config.globalName)) {
        resolve();
        return;
      }
      reject(new Error(`SoundFont loaded without data: ${config.label}`));
    };
    script.onerror = () => reject(new Error(`Could not load SoundFont: ${config.label}`));
    document.head.append(script);
  });
  soundfontScriptPromises.set(src, loadPromise);
  await loadPromise;
}

async function ensureSamplerForTrack(track) {
  await ensureTrackSampler(track);
}

async function ensureSamplerForSoundfont(id) {
  const existing = state.audio.samplers.get(id);
  if (existing?.ready) {
    return existing;
  }
  if (existing?.loadPromise) {
    await existing.loadPromise;
    return existing;
  }

  const config = soundfontConfig(id);
  await ensureSoundfontData(config);
  const soundfont = globalSoundfont(config.globalName);
  if (!soundfont) {
    throw new Error(`FatBoy soundfont is not loaded: ${config.globalName}`);
  }

  const output = new Tone.Gain(config.gain).toDestination();
  const samplerState = {
    config,
    output,
    sampler: null,
    ready: false,
    loadPromise: null,
  };

  samplerState.sampler = new Tone.Sampler({
    urls: { ...soundfont },
    attack: 0,
    release: config.release,
    onload() {
      samplerState.ready = true;
    },
  }).connect(output);
  samplerState.loadPromise = Tone.loaded().then(() => {
    samplerState.ready = true;
    return samplerState;
  });
  state.audio.samplers.set(config.id, samplerState);
  await samplerState.loadPromise;
  return samplerState;
}

async function ensureTrackSampler(track) {
  if (!track) {
    return ensureSamplerForSoundfont(soundfontIdForTrack(track));
  }

  const key = samplerKeyForTrack(track);
  const existing = state.audio.samplers.get(key);
  if (existing?.ready) {
    updateSamplerForTrack(track);
    return existing;
  }
  if (existing?.loadPromise) {
    await existing.loadPromise;
    updateSamplerForTrack(track);
    return existing;
  }

  const config = soundfontConfig(soundfontIdForTrack(track));
  await ensureSoundfontData(config);
  const soundfont = globalSoundfont(config.globalName);
  if (!soundfont) {
    throw new Error(`FatBoy soundfont is not loaded: ${config.globalName}`);
  }

  const output = new Tone.Gain(trackOutputGain(track, config)).toDestination();
  const envelope = new Tone.AmplitudeEnvelope(trackAdsrEnvelope(track)).connect(output);
  const samplerState = {
    config,
    output,
    envelope,
    sampler: null,
    ready: false,
    loadPromise: null,
  };

  samplerState.sampler = new Tone.Sampler({
    urls: { ...soundfont },
    attack: 0,
    release: config.release,
    onload() {
      samplerState.ready = true;
    },
  }).connect(envelope);
  samplerState.loadPromise = Tone.loaded().then(() => {
    samplerState.ready = true;
    return samplerState;
  });
  state.audio.samplers.set(key, samplerState);
  await samplerState.loadPromise;
  updateSamplerForTrack(track);
  return samplerState;
}

function samplerKeyForTrack(track) {
  return `track:${track?.id || "unknown"}:${soundfontIdForTrack(track)}`;
}

function trackAdsrEnvelope(track) {
  const adsr = normalizeTrackAdsr(track?.adsr, track?.sustain);
  return {
    attack: adsr.attack,
    decay: adsr.decay,
    sustain: adsr.sustain,
    release: adsr.release,
  };
}

function trackOutputGain(track, config = soundfontConfig(soundfontIdForTrack(track))) {
  return config.gain * clampValue(track?.volume, 0, 1.25, DEFAULT_TRACK_VOLUME);
}

function updateSamplerForTrack(track) {
  const samplerState = state.audio.samplers.get(samplerKeyForTrack(track));
  if (!samplerState) {
    return;
  }

  samplerState.output.gain.value = trackOutputGain(track, samplerState.config);
  if (samplerState.envelope) {
    const adsr = trackAdsrEnvelope(track);
    samplerState.envelope.attack = adsr.attack;
    samplerState.envelope.decay = adsr.decay;
    samplerState.envelope.sustain = adsr.sustain;
    samplerState.envelope.release = adsr.release;
  }
}

async function preparePlaybackInstruments() {
  const ids = new Set(["drums", soundfontIdForProgram(state.selectedProgram)]);
  const playableTracks = state.song.tracks.filter((track) => track.type !== "lyrics");
  await Promise.all([
    ...[...ids].map((id) => ensureSamplerForSoundfont(id)),
    ...playableTracks.map((track) => ensureSamplerForTrack(track)),
  ]);
}

async function auditionChord(event) {
  const auditionRequestId = beginClipAudition();
  try {
    await ensureAudio();
    await ensureSamplerForTrack(getChordTrack());
  } catch {
    return;
  }

  if (!isCurrentClipAudition(auditionRequestId)) {
    return;
  }

  playChordEvent(event, chordAuditionDurationMs(event), getChordTrack());
}

function auditionChordPlayStyle(playStyle) {
  const value = normalizeChordPlayStyle(playStyle);
  const selectedChords = getSelectedChordEvents();
  const selected = selectedChords.find(({ event }) => event.id === state.selectedEventId)?.event
    || selectedChords[0]?.event
    || null;
  const event = selected
    ? { ...selected, playStyle: value }
    : c3AuditionChordEvent(value);
  auditionChord(event);
}

function c3AuditionChordEvent(playStyle) {
  const details = parseChord("C");
  return {
    id: "audition_c3_chord",
    eventType: "chord",
    tick: 0,
    durationTicks: ticksPerBar(),
    symbol: "C",
    root: "C",
    quality: details.quality,
    extensions: details.extensions,
    voicing: details.voicing.map((midi) => midi - 12),
    playStyle: normalizeChordPlayStyle(playStyle),
    useVoicingForAudition: true,
  };
}

function eventMidiNotes(event) {
  if (event?.useVoicingForAudition && Array.isArray(event.voicing) && event.voicing.length) {
    return event.voicing.map(Number).filter(Number.isFinite);
  }

  if (state.chordRenderMode === "guitar") {
    const guitarNotes = guitarMidiNotesForEvent(event);
    if (guitarNotes.length) {
      return guitarNotes;
    }
  }

  if (Array.isArray(event.voicing) && event.voicing.length) {
    return event.voicing.map(Number).filter(Number.isFinite);
  }

  const parsed = parseChord(event.symbol);
  event.root = parsed.root;
  event.quality = parsed.quality;
  event.extensions = parsed.extensions;
  event.voicing = parsed.voicing;
  return parsed.voicing;
}

function chordAuditionDurationMs(event) {
  return Math.max(650, ticksToSeconds(event?.durationTicks || ticksPerBar()) * 1000);
}

function playChordEvent(event, durationMs, track = getChordTrack()) {
  scheduleChordPlayback(event, 0, durationMs, track, scheduleToneNote);
}

function playStockPatternEvent(event, durationMs, track) {
  if (event.eventType === "drums-clip") {
    playDrumPatternEvent(event, track);
    return;
  }

  const chord = chordAtTick(event.tick) || lastSelectedChordEvent();
  const rootMidi = rootMidiForPatternEvent(event, chord);
  patternRelativeSemitones(event, chord, track).forEach((relativeSemitone, index) => {
    const noteEvent = event.events[index];
    const midi = rootMidi + relativeSemitone;
    const startMs = ticksToSeconds(noteEvent.offsetTicks || 0) * 1000;
    const noteDurationMs = ticksToSeconds(noteEvent.durationTicks || state.snapTicks) * 1000;
    scheduleToneNote(midi, Math.min(durationMs, noteDurationMs), track, 88, startMs);
  });
}

function playDrumPatternEvent(event, track) {
  (event.events || []).forEach((drumEvent) => {
    const midi = Number(drumEvent.midi);
    if (!Number.isFinite(midi)) {
      return;
    }
    const startMs = ticksToSeconds(drumEvent.offsetTicks || 0) * 1000;
    const noteDurationMs = ticksToSeconds(drumEvent.durationTicks || 120) * 1000;
    scheduleToneNote(midi, noteDurationMs, track, drumVelocityForEvent(drumEvent), startMs);
  });
}

function drumVelocityForEvent(event) {
  const velocity = Number(event?.velocity);
  if (Number.isFinite(velocity)) {
    return Math.max(6, Math.min(127, velocity <= 1 ? Math.round(velocity * 127) : velocity));
  }

  return DRUM_ACCENT_VELOCITIES[normalizeDrumAccent(event?.accent)] || DRUM_ACCENT_VELOCITIES.normal;
}

function rootMidiForPatternEvent(event, chord = chordAtTick(event.tick) || lastSelectedChordEvent()) {
  const root = chord?.root || getKeySignature().key || "C";
  const baseOctave = event.eventType === "bass-clip" ? 3 : 4;
  return Note.midi(`${root}${baseOctave}`) || (event.eventType === "bass-clip" ? 48 : 60);
}

function relativeSemitoneForChord(relativeSemitone, chord) {
  const semitone = Number(relativeSemitone) || 0;
  const degree = positiveMod(semitone, 12);

  if (isMinorChord(chord) && [4, 9, 11].includes(degree)) {
    return semitone - 1;
  }

  if (isDominantSeventhChord(chord) && degree === 11) {
    return semitone - 1;
  }

  return semitone;
}

function patternRelativeSemitones(event, chord, track) {
  const notes = (event.events || []).map((noteEvent) => (
    relativeSemitoneForChord(noteEvent.relativeSemitone, chord)
    + ((Number(noteEvent.octave) || 0) * 12)
  ));
  const octaveOffset = (
    normalizePatternTransform(track?.octaveShift)
    + normalizePatternTransform(event.octaveShift)
  ) * 12;
  return notes.map((relativeSemitone) => relativeSemitone + octaveOffset);
}

function isMinorChord(chord) {
  return Boolean(chord && (
    String(chord.quality || "").toLowerCase().includes("minor")
    || /m(?!aj)/.test(String(chord.symbol || ""))
  ));
}

function isDominantSeventhChord(chord) {
  if (!chord || isMinorChord(chord)) {
    return false;
  }

  const quality = String(chord.quality || "").toLowerCase();
  const extensions = Array.isArray(chord.extensions)
    ? chord.extensions.map((extension) => String(extension).toLowerCase())
    : [];
  const suffix = String(chord.symbol || "").replace(/^[A-G](?:#|b)?/, "").replace(/\/.*$/, "");

  return (
    quality.includes("dom")
    || extensions.some((extension) => extension.includes("dom"))
    || /^(?:7|9|11|13)(?:sus|b|#|add|\d|$)/i.test(suffix)
  );
}

function chordAtTick(tick) {
  return getChordTrack().events
    .filter((event) => event.eventType === "chord" && event.tick <= tick)
    .sort((a, b) => b.tick - a.tick)[0] || null;
}

function lastSelectedChordEvent() {
  return getChordTrack().events.find((event) => event.id === state.lastSelectedChordId) || null;
}

function scheduleChordPlayback(event, offsetMs, durationMs, track, scheduler) {
  const trackOctaveOffset = normalizePatternTransform(track?.octaveShift) * 12;
  const notes = eventMidiNotes(event).map((midi) => midi + trackOctaveOffset).sort((a, b) => a - b);
  if (!notes.length) {
    return;
  }

  const playStyle = normalizeChordPlayStyle(event.playStyle);
  const playStyleConfig = getChordPlayStyle(playStyle);
  const beatMs = 60000 / getTempo();
  const strumGapSlowMs = Math.min(45, Math.max(12, beatMs / 18));
  const strumGapFastMs = Math.min(22, Math.max(6, beatMs / 36));

  const scheduleSingle = (midi, noteOffsetMs, noteVelocity = 92, noteDurationMs = durationMs) => {
    scheduler(
      midi,
      Math.max(50, noteDurationMs),
      track,
      Math.max(1, Math.min(127, noteVelocity)),
      offsetMs + Math.max(0, noteOffsetMs),
    );
  };

  const scheduleStroke = (direction, strokeOffsetMs, velocityScale = 1, strokeGapMs = strumGapSlowMs) => {
    const strokeNotes = direction === "up" ? [...notes].reverse() : notes;
    strokeNotes.forEach((midi, index) => {
      const noteOffsetMs = strokeOffsetMs + (index * strokeGapMs);
      scheduleSingle(midi, noteOffsetMs, 92 * velocityScale, Math.max(80, durationMs - noteOffsetMs));
    });
  };

  if (playStyleConfig?.type === "block") {
    notes.forEach((midi) => scheduleSingle(midi, 0));
    return;
  }

  if (playStyleConfig?.type === "arpeggio") {
    const stepMs = beatMs * (4 / (Number(playStyleConfig.subdivision) || 8));
    (playStyleConfig.notes || []).forEach((noteIndex, step) => {
      const resolvedIndex = noteIndexForPlayStyle(noteIndex, notes.length);
      if (resolvedIndex === null) {
        return;
      }
      scheduleSingle(notes[resolvedIndex], step * stepMs, 92, Math.max(70, stepMs * 0.86));
    });
    return;
  }

  if (playStyleConfig?.type === "strum") {
    (playStyleConfig.strokes || []).forEach((strokeConfig) => {
      const strokeOffsetMs = stepToBeatOffset(strokeConfig.step, playStyleConfig.subdivision) * beatMs;
      if (strokeOffsetMs < durationMs) {
        scheduleStroke(strokeConfig.direction, strokeOffsetMs, strokeConfig.velocity, strumGapFastMs);
      }
    });
    return;
  }

  scheduleStroke("down", 0);
}

function scheduleNoteTimer(callback, delayMs) {
  const id = window.setTimeout(() => {
    state.noteTimerIds.delete(id);
    callback();
  }, Math.max(0, delayMs));
  state.noteTimerIds.add(id);
  return id;
}

function scheduleToneNote(midi, durationMs, track, velocity = 92, delayMs = 0) {
  return scheduleNoteTimer(() => {
    triggerToneNote(midi, durationMs, track, velocity);
  }, delayMs);
}

function schedulePlaybackNote(midi, durationMs, track, velocity, offsetMs) {
  const id = Tone.Transport.schedule((time) => {
    triggerToneNote(midi, durationMs, track, velocity, time);
  }, Math.max(0, offsetMs) / 1000);
  state.scheduledIds.push(id);
  return id;
}

function triggerToneNote(midi, durationMs, track, velocity = 92, time = Tone.now()) {
  if (track?.muted) {
    return;
  }

  const midiNumber = Math.round(Number(midi));
  if (!Number.isFinite(midiNumber)) {
    return;
  }

  const samplerState = state.audio.samplers.get(samplerKeyForTrack(track));
  if (!samplerState?.sampler) {
    return;
  }

  const noteName = midiToToneNoteName(midiNumber);
  const config = samplerState.config;
  const safeVelocity = Math.max(0.05, Math.min(1, (Number(velocity) || 92) / 127));
  const adsr = trackAdsrEnvelope(track);
  const durationSeconds = Math.max(0.03, (Number(durationMs) || (config.duration * 1000)) / 1000);

  try {
    samplerState.sampler.triggerAttackRelease(noteName, durationSeconds, time, safeVelocity * config.defaultVelocity);
    samplerState.envelope?.triggerAttackRelease?.(Math.max(0.01, durationSeconds - adsr.release), time);
  } catch {
    // Ignore requests made while a browser is still settling decoded sample buffers.
  }
}

function midiToToneNoteName(midi) {
  return Tone.Frequency(midi, "midi").toNote();
}

function clearNoteTimersAndNotes() {
  state.noteTimerIds.forEach((id) => window.clearTimeout(id));
  state.noteTimerIds.clear();
  releaseAllSynthNotes();
}

function beginClipAudition() {
  state.auditionRequestId += 1;
  clearNoteTimersAndNotes();
  return state.auditionRequestId;
}

function isCurrentClipAudition(auditionRequestId) {
  return state.auditionRequestId === auditionRequestId;
}

function cancelClipAudition() {
  state.auditionRequestId += 1;
  clearNoteTimersAndNotes();
}

function releaseAllSynthNotes() {
  state.audio.samplers.forEach((samplerState) => {
    try {
      samplerState.envelope?.triggerRelease?.(Tone.now());
      samplerState.sampler?.releaseAll?.(Tone.now());
    } catch {
      // Some sampler states can reject release calls while still loading.
    }
  });
}

async function auditionTimelineEvent(eventId) {
  const selected = findEventById(eventId);
  if (!selected) {
    return;
  }

  const auditionRequestId = beginClipAudition();
  const { track, event } = selected;
  try {
    await ensureAudio();
    await ensureSamplerForTrack(track);
  } catch {
    return;
  }

  if (!isCurrentClipAudition(auditionRequestId)) {
    return;
  }

  if (event.eventType === "chord") {
    playChordEvent(event, chordAuditionDurationMs(event), track);
  } else if (stockClipTypes().includes(event.eventType)) {
    playStockPatternEvent(event, ticksToSeconds(event.durationTicks || ticksPerBar()) * 1000, track);
  }
}

async function togglePlayback() {
  if (state.preparingPlayback) {
    return;
  }

  if (state.playing) {
    stopPlayback(false, true);
    return;
  }

  if (!normalizeLoopRegion(state.loopRegion)) {
    const region = contentLoopRegion();
    if (region) {
      state.loopRegion = region;
      scheduleAppStateSave();
      renderTimeline();
    }
  }

  state.preparingPlayback = true;
  renderControls();
  try {
    await ensureAudio();
    await preparePlaybackInstruments();
  } catch {
    state.preparingPlayback = false;
    renderControls();
    return;
  }

  state.preparingPlayback = false;
  const loopRegion = activeLoopRegion();
  if (loopRegion && (state.playheadTick < loopRegion.startTick || state.playheadTick >= loopRegion.endTick)) {
    state.playheadTick = loopRegion.startTick;
  }
  state.playing = true;
  renderControls();
  schedulePlayback();
}

function schedulePlayback() {
  cancelClipAudition();
  clearScheduledPlayback();

  const loopRegion = activeLoopRegion();
  const segmentStartTick = loopRegion?.startTick ?? state.playheadTick;
  const playbackOffsetTick = loopRegion
    ? Math.max(0, Math.min(loopRegion.endTick - segmentStartTick, state.playheadTick - segmentStartTick))
    : 0;
  const startTick = loopRegion ? segmentStartTick : state.playheadTick;
  const playbackEndTick = loopRegion?.endTick ?? totalTicks();
  const playbackDurationTick = playbackEndTick - startTick;
  if (startTick >= playbackEndTick) {
    state.playheadTick = loopRegion?.startTick ?? 0;
    schedulePlayback();
    return;
  }
  const playbackEvents = state.song.tracks.flatMap((track) => (
    track.events
      .filter((event) => (
        playableEventTypes().includes(event.eventType)
        && event.tick < playbackEndTick
        && event.tick + (event.durationTicks || 0) > startTick
      ))
      .map((event) => ({ track, event }))
  ))
    .sort((a, b) => a.event.tick - b.event.tick);
  const startTime = performance.now() + (PLAYBACK_START_DELAY_SECONDS * 1000);

  Tone.Transport.position = 0;
  Tone.Transport.loop = Boolean(state.loop && playbackDurationTick > 0);
  Tone.Transport.loopStart = 0;
  Tone.Transport.loopEnd = ticksToSeconds(playbackDurationTick);
  syncToneTempo();
  schedulePendingTrackUnmutes(startTick, playbackEndTick);
  playbackEvents.forEach(({ track, event }) => {
    schedulePlaybackEvent(track, event, startTick, playbackEndTick);
  });
  Tone.Transport.start(`+${PLAYBACK_START_DELAY_SECONDS}`, ticksToSeconds(playbackOffsetTick));

  const updatePlaybackPlayhead = () => {
    const elapsedTicks = secondsToRawTicks(Math.max(0, (performance.now() - startTime) / 1000));
    const segmentElapsedTick = state.loop && playbackDurationTick > 0
      ? (playbackOffsetTick + elapsedTicks) % playbackDurationTick
      : elapsedTicks;
    const playbackTick = Math.min(playbackEndTick, startTick + segmentElapsedTick);
    state.playbackPlayheadTick = playbackTick;
    state.playheadTick = snapTick(playbackTick);
    if (!state.loop && startTick + elapsedTicks >= playbackEndTick) {
      applyPendingTrackUnmutesAtTick(playbackEndTick);
      stopPlayback();
      return;
    }
    if (state.loop && startTick + elapsedTicks >= playbackEndTick) {
      applyPendingTrackUnmutesAtTick(playbackEndTick);
    }
    const playbackMeasure = Math.floor(playbackTick / ticksPerBar());
    const shouldScrollMeasureIntoView = playbackMeasure !== state.lastPlaybackScrollMeasure;
    state.lastPlaybackScrollMeasure = playbackMeasure;
    renderPlayhead({
      scrollIntoView: shouldScrollMeasureIntoView,
      scrollBehavior: "smooth",
    });
    state.playheadAnimationFrame = window.requestAnimationFrame(updatePlaybackPlayhead);
  };
  state.playheadAnimationFrame = window.requestAnimationFrame(updatePlaybackPlayhead);
}

function activeLoopRegion() {
  return state.loop ? normalizeLoopRegion(state.loopRegion) : null;
}

function schedulePendingTrackUnmutes(startTick, playbackEndTick) {
  state.pendingTrackUnmutes.forEach((targetTick, trackId) => {
    const normalizedTargetTick = Number(targetTick) || 0;
    const shouldApplyNow = normalizedTargetTick <= startTick;
    const shouldApplyDuringSegment = normalizedTargetTick > startTick && normalizedTargetTick <= playbackEndTick;
    if (!shouldApplyNow && !shouldApplyDuringSegment) {
      return;
    }

    if (shouldApplyNow) {
      applyPendingTrackUnmute(trackId);
      return;
    }

    const id = Tone.Transport.scheduleOnce(() => {
      applyPendingTrackUnmute(trackId);
    }, ticksToSeconds(normalizedTargetTick - startTick));
    state.scheduledIds.push(id);
  });
}

function applyPendingTrackUnmutesAtTick(tick) {
  [...state.pendingTrackUnmutes].forEach(([trackId, targetTick]) => {
    if ((Number(targetTick) || 0) <= tick) {
      applyPendingTrackUnmute(trackId);
    }
  });
}

function schedulePlaybackEvent(track, event, startTick, playbackEndTick = totalTicks()) {
  const offsetMs = ticksToSeconds(Math.max(0, event.tick - startTick)) * 1000;
  const eventEndTick = event.tick + (event.durationTicks || state.snapTicks);
  const durationMs = ticksToSeconds(Math.max(0, Math.min(eventEndTick, playbackEndTick) - Math.max(event.tick, startTick))) * 1000;
  if (event.eventType === "chord") {
    scheduleChordNotes(event, offsetMs, durationMs, track);
  } else {
    scheduleStockPatternNotes(event, offsetMs, durationMs, track);
  }
}

function scheduleChordNotes(event, offsetMs, durationMs, track) {
  scheduleChordPlayback(event, offsetMs, durationMs, track, schedulePlaybackNote);
}

function scheduleStockPatternNotes(event, offsetMs, durationMs, track) {
  if (event.eventType === "drums-clip") {
    (event.events || []).forEach((drumEvent) => {
      const midi = Number(drumEvent.midi);
      if (!Number.isFinite(midi)) {
        return;
      }
      const startMs = offsetMs + (ticksToSeconds(drumEvent.offsetTicks || 0) * 1000);
      const noteDurationMs = ticksToSeconds(drumEvent.durationTicks || 120) * 1000;
      schedulePlaybackNote(midi, noteDurationMs, track, drumVelocityForEvent(drumEvent), startMs);
    });
    return;
  }

  const chord = chordAtTick(event.tick) || lastSelectedChordEvent();
  const rootMidi = rootMidiForPatternEvent(event, chord);
  patternRelativeSemitones(event, chord, track).forEach((relativeSemitone, index) => {
    const noteEvent = event.events[index];
    const midi = rootMidi + relativeSemitone;
    const startMs = offsetMs + (ticksToSeconds(noteEvent.offsetTicks || 0) * 1000);
    const noteDurationMs = ticksToSeconds(noteEvent.durationTicks || state.snapTicks) * 1000;
    schedulePlaybackNote(midi, Math.min(durationMs, noteDurationMs), track, 88, startMs);
  });
}

function playableEventTypes() {
  return ["chord", ...stockClipTypes()];
}

function showMidiExportDialog() {
  if (!shouldShowMidiExportNotice()) {
    exportMidi();
    return;
  }

  window.bootstrap.Modal.getOrCreateInstance(els.midiExportModal).show();
}

function confirmMidiExport(options = {}) {
  if (options.remember) {
    rememberMidiExportNoticeDismissed();
  }
  window.bootstrap.Modal.getOrCreateInstance(els.midiExportModal).hide();
  exportMidi();
}

function exportMidi() {
  const meter = getMeter();
  const keySignature = getKeySignature();
  const midi = createMidiDocument({
    name: state.song.title,
    ppq: PPQ,
    bpm: getTempo(),
    timeSignature: [meter.numerator, meter.denominator],
    keySignature: {
      key: keySignature.key,
      scale: keySignature.mode === "minor" ? "minor" : "major",
    },
  });

  state.song.tracks
    .filter((track) => track.type !== "lyrics")
    .forEach((songTrack, index) => {
      const track = addMidiTrack(midi, {
        name: songTrack.name || labelForTrackInstrument(songTrack),
        channel: midiChannelForTrack(songTrack, index),
        instrumentNumber: midiInstrumentForTrack(songTrack),
      });

      midiNotesForSongTrack(songTrack).forEach((note) => {
        addMidiNote(track, note);
      });
    });

  downloadMidi(midi, state.song.title || DEFAULT_SONG_TITLE);
}

function midiNotesForSongTrack(track) {
  return (track.events || [])
    .filter((event) => playableEventTypes().includes(event.eventType))
    .flatMap((event) => midiNotesForSongEvent(track, event))
    .sort((a, b) => a.ticks - b.ticks || a.midi - b.midi);
}

function midiNotesForSongEvent(track, event) {
  if (event.eventType === "chord") {
    return midiNotesForChordEvent(track, event);
  }
  if (event.eventType === "drums-clip") {
    return midiNotesForDrumPatternEvent(event);
  }
  return midiNotesForPatternEvent(track, event);
}

function midiNotesForChordEvent(track, event) {
  const notes = [];
  const durationMs = ticksToSeconds(event.durationTicks || ticksPerBar()) * 1000;
  scheduleChordPlayback(event, 0, durationMs, track, (midi, noteDurationMs, _track, velocity, delayMs) => {
    notes.push({
      midi,
      ticks: (event.tick || 0) + Math.round(secondsToRawTicks((delayMs || 0) / 1000)),
      durationTicks: Math.max(1, Math.round(secondsToRawTicks((noteDurationMs || 0) / 1000))),
      velocity,
    });
  });
  return notes;
}

function midiNotesForDrumPatternEvent(event) {
  return (event.events || []).flatMap((drumEvent) => {
    const midi = Number(drumEvent.midi);
    if (!Number.isFinite(midi)) {
      return [];
    }
    return [{
      midi,
      ticks: (event.tick || 0) + Math.round(Number(drumEvent.offsetTicks) || 0),
      durationTicks: Math.max(1, Math.round(Number(drumEvent.durationTicks) || 120)),
      velocity: drumVelocityForEvent(drumEvent),
    }];
  });
}

function midiNotesForPatternEvent(track, event) {
  const chord = chordAtTick(event.tick) || lastSelectedChordEvent();
  const rootMidi = rootMidiForPatternEvent(event, chord);
  return patternRelativeSemitones(event, chord, track).flatMap((relativeSemitone, index) => {
    const noteEvent = event.events?.[index];
    if (!noteEvent) {
      return [];
    }
    return [{
      midi: rootMidi + relativeSemitone,
      ticks: (event.tick || 0) + Math.round(Number(noteEvent.offsetTicks) || 0),
      durationTicks: Math.max(1, Math.round(Number(noteEvent.durationTicks) || state.snapTicks)),
      velocity: 88,
    }];
  });
}

function midiChannelForTrack(track, index) {
  if (track.type === "drums") {
    return MIDI_DRUM_CHANNEL;
  }

  const storedChannel = Number(track.midiChannel);
  if (Number.isFinite(storedChannel)) {
    const zeroBasedChannel = storedChannel >= 1 ? storedChannel - 1 : storedChannel;
    return Math.max(0, Math.min(15, Math.round(zeroBasedChannel)));
  }

  const channels = [0, 1, 2, 3, 4, 5, 6, 7, 8, 10, 11, 12, 13, 14, 15];
  return channels[index % channels.length];
}

function midiInstrumentForTrack(track) {
  const id = soundfontIdForTrack(track);
  if (track.type === "bass" || id.includes("bass")) {
    return 32;
  }
  if (track.type === "chords" || id.includes("guitar")) {
    return 24;
  }
  if (id.includes("organ")) {
    return 16;
  }
  if (id.includes("strings") || id.includes("violin") || id.includes("cello") || id.includes("viola")) {
    return 48;
  }
  return 0;
}

function stockClipTypes() {
  return ["melody-clip", "bass-clip", "drums-clip"];
}

function clearScheduledPlayback() {
  state.scheduledIds.forEach((id) => Tone.Transport.clear(id));
  state.scheduledIds = [];
  Tone.Transport.stop();
  Tone.Transport.cancel();
  Tone.Transport.loop = false;
  Tone.Transport.loopStart = 0;
  Tone.Transport.loopEnd = 0;
  if (state.playheadTimer) {
    window.clearInterval(state.playheadTimer);
    state.playheadTimer = null;
  }
  if (state.playheadAnimationFrame) {
    window.cancelAnimationFrame(state.playheadAnimationFrame);
    state.playheadAnimationFrame = null;
  }
  state.playbackPlayheadTick = null;
  state.lastPlaybackScrollMeasure = null;
}

function stopPlayback(resetPlayhead = true, alignToCurrentMeasure = false) {
  const stoppedTick = state.playbackPlayheadTick ?? state.playheadTick;
  state.playing = false;
  clearScheduledPlayback();
  cancelClipAudition();
  state.pendingTrackUnmutes.clear();
  if (alignToCurrentMeasure) {
    state.playheadTick = Math.min(totalTicks(), barStartTick(stoppedTick));
  } else if (resetPlayhead) {
    state.playheadTick = 0;
  }
  if (resetPlayhead) {
    clearSelection();
  }
  scheduleAppStateSave();
  renderControls();
  renderTimeline();
}

function ticksToSeconds(ticks) {
  return (ticks / PPQ) * (60 / getTempo());
}

function secondsToRawTicks(seconds) {
  return Math.max(0, (seconds / (60 / getTempo())) * PPQ);
}

function setTempo(bpm) {
  const nextBpm = Math.max(40, Math.min(240, Math.round(Number(bpm) || getTempo())));
  state.song.timeline.defaultTempoBpm = nextBpm;
  state.song.timeline.tempoEvents = [{ tick: 0, bpm: nextBpm }];
  updateTrackSourceTempo(nextBpm);
  if (state.audio.ready) {
    syncToneTempo();
  }
  touchSong();
  renderTempo();
}

function setKeySignatureValue(updates) {
  const current = getKeySignature();
  const key = keys.includes(updates.key) ? updates.key : current.key;
  const mode = updates.mode === "minor" ? "minor" : updates.mode === "major" ? "major" : current.mode === "minor" ? "minor" : "major";
  state.song.timeline.keySignatureEvents = [
    { tick: 0, key, mode },
  ];
  touchSong();
  renderKeySignature();
  renderToolPanel();
  renderTimeline();
}

function tapTempo() {
  const now = performance.now();
  const previousTap = state.tapTempoTimes.at(-1) || 0;
  if (now - previousTap > 2500) {
    state.tapTempoTimes = [];
  }

  state.tapTempoTimes.push(now);
  state.tapTempoTimes = state.tapTempoTimes.slice(-5);
  if (state.tapTempoTimes.length < 2) {
    return;
  }

  const intervals = state.tapTempoTimes.slice(1).map((tapTime, index) => tapTime - state.tapTempoTimes[index]);
  const averageInterval = intervals.reduce((total, interval) => total + interval, 0) / intervals.length;
  setTempo(60000 / averageInterval);
}

function movePlayhead(deltaTicks) {
  clearPreviewClip();
  state.activePanelTrackType = "chords";
  state.playheadTick = snapTick(state.playheadTick + deltaTicks);
  clearSelection();
  scheduleAppStateSave();
  renderTimeline();
}

function createNewSong() {
  state.song = createDefaultSong();
  state.serverSongId = null;
  state.serverSongSignature = null;
  state.serverSongLocked = false;
  state.loadedOriginBardName = "";
  clearSelection();
  clearPreviewClip();
  state.activePanelTrackType = "chords";
  state.collapsedTrackIds = [];
  state.playheadTick = 0;
  state.loopRegion = null;
  state.loopSelectionAnchorTick = null;
  state.loopDrag = null;
  state.selectedProgram = programFromChordTrack() || programs[0];
  touchSong();
  render();
}

function adjustSelectedPatternTransform(property, delta) {
  const selectedEvents = getSelectedTimelineEvents().filter(({ event }) => isTransformablePatternClip(event));
  if (!selectedEvents.length) {
    return;
  }

  selectedEvents.forEach(({ event }) => {
    event[property] = normalizePatternTransform((Number(event[property]) || 0) + delta);
  });
  renderPatternClipModal(selectedEvents[0].track, selectedEvents[0].event);
  touchSong();
  renderTimeline();
  auditionTimelineEvent(selectedEvents[0].event.id);
}

function setSelectedPatternGridOctaves(value) {
  const selectedEvents = getSelectedTimelineEvents().filter(({ event }) => isTransformablePatternClip(event));
  const gridOctaves = validClipGridOctaves(value);
  if (!selectedEvents.length) {
    return;
  }

  selectedEvents.forEach(({ event }) => {
    event.gridOctaves = gridOctaves;
  });
  touchSong();
  renderPatternClipModal(selectedEvents[0].track, selectedEvents[0].event);
}

function setSelectedPatternGridResolution(value) {
  const selectedEvents = getSelectedTimelineEvents().filter(({ event }) => isTransformablePatternClip(event));
  const nextStepsPerBar = validClipStepsPerBar(value, DEFAULT_CLIP_STEPS_PER_BAR);
  if (!selectedEvents.length) {
    return;
  }

  selectedEvents.forEach(({ event }) => {
    const previousStepsPerBar = patternStepsPerBar(event);
    if (nextStepsPerBar === previousStepsPerBar) {
      return;
    }

    event.stepsPerBar = nextStepsPerBar;
    event.events = (event.events || []).map((noteEvent) => ({
      ...noteEvent,
      offsetTicks: Math.round((Math.round(((Number(noteEvent.offsetTicks) || 0) / ticksPerBar()) * nextStepsPerBar) / nextStepsPerBar) * ticksPerBar()),
      durationTicks: Math.max(1, Math.round((Math.round(((Number(noteEvent.durationTicks) || state.snapTicks) / ticksPerBar()) * nextStepsPerBar) / nextStepsPerBar) * ticksPerBar())),
    }));
    sortPatternEvents(event);
  });
  touchSong();
  renderTimeline();
  renderPatternClipModal(selectedEvents[0].track, selectedEvents[0].event);
}

function startLoopRegionPointer(event) {
  if (event.button !== 0) {
    return;
  }

  const ruler = event.currentTarget;
  const action = event.target.dataset.loopAction || "create";
  const tick = tickFromRulerPointer(event, ruler);
  const region = normalizeLoopRegion(state.loopRegion);
  if (event.shiftKey && region) {
    extendLoopRegionToTick(tick);
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  const anchorTick = action === "create" ? tick : null;
  state.loopDrag = {
    action,
    anchorTick,
    startTick: region?.startTick ?? tick,
    endTick: region?.endTick ?? Math.min(totalTicks(), tick + state.snapTicks),
  };

  if (action === "create") {
    state.loopRegion = {
      startTick: tick,
      endTick: Math.min(totalTicks(), tick + state.snapTicks),
    };
    state.loopSelectionAnchorTick = tick;
  } else if (action === "resize-start") {
    state.loopSelectionAnchorTick = region?.endTick ?? state.loopSelectionAnchorTick;
  } else if (action === "resize-end") {
    state.loopSelectionAnchorTick = region?.startTick ?? state.loopSelectionAnchorTick;
  }

  ruler.setPointerCapture(event.pointerId);
  event.preventDefault();
  event.stopPropagation();
  renderTimeline();
}

function moveLoopRegionPointer(event) {
  if (!state.loopDrag) {
    return;
  }

  const tick = tickFromTimelinePointer(event);
  const minDurationTicks = state.snapTicks;
  let startTick = state.loopDrag.startTick;
  let endTick = state.loopDrag.endTick;

  if (state.loopDrag.action === "resize-start") {
    startTick = Math.min(tick, endTick - minDurationTicks);
  } else if (state.loopDrag.action === "resize-end") {
    endTick = Math.max(tick, startTick + minDurationTicks);
  } else {
    startTick = Math.min(state.loopDrag.anchorTick, tick);
    endTick = Math.max(state.loopDrag.anchorTick, tick);
    if (endTick - startTick < minDurationTicks) {
      endTick = Math.min(totalTicks(), startTick + minDurationTicks);
    }
  }

  state.loopRegion = normalizeLoopRegion({ startTick, endTick });
  scheduleAppStateSave();
  renderTimeline();
}

function endLoopRegionPointer() {
  if (!state.loopDrag) {
    return;
  }

  state.loopRegion = normalizeLoopRegion(state.loopRegion);
  state.loopDrag = null;
  scheduleAppStateSave();
  renderTimeline();
}

function tickFromRulerPointer(event, ruler) {
  const rect = ruler.getBoundingClientRect();
  const rowStartTick = Number(ruler.dataset.rowStartTick) || 0;
  const x = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
  return snapTick(Math.max(0, Math.min(totalTicks(), rowStartTick + pxToTick(x))));
}

function tickFromTimelinePointer(event) {
  const ruler = timelineRulerFromPointer(event);
  return ruler
    ? tickFromRulerPointer(event, ruler)
    : snapTick(state.loopDrag?.anchorTick ?? state.loopDrag?.endTick ?? 0);
}

function timelineRulerFromPointer(event) {
  const groups = [...els.trackRows.querySelectorAll(".timeline-group")];
  let nearest = null;
  let nearestDistance = Infinity;

  groups.forEach((group) => {
    const rect = group.getBoundingClientRect();
    if (event.clientY >= rect.top && event.clientY <= rect.bottom) {
      nearest = group;
      nearestDistance = 0;
      return;
    }

    const distance = Math.min(Math.abs(event.clientY - rect.top), Math.abs(event.clientY - rect.bottom));
    if (distance < nearestDistance) {
      nearest = group;
      nearestDistance = distance;
    }
  });

  return nearest?.querySelector(".timeline-group-ruler") || null;
}

function extendLoopRegionToTick(tick) {
  const region = normalizeLoopRegion(state.loopRegion);
  if (!region) {
    return;
  }

  const fallbackAnchor = tick <= region.startTick ? region.endTick : region.startTick;
  const anchorTick = Number.isFinite(state.loopSelectionAnchorTick)
    ? state.loopSelectionAnchorTick
    : fallbackAnchor;
  let startTick = Math.min(anchorTick, tick);
  let endTick = Math.max(anchorTick, tick);
  if (endTick - startTick < state.snapTicks) {
    if (tick < anchorTick) {
      startTick = Math.max(0, endTick - state.snapTicks);
    } else {
      endTick = Math.min(totalTicks(), startTick + state.snapTicks);
    }
  }

  state.loopRegion = normalizeLoopRegion({ startTick, endTick });
  state.loopSelectionAnchorTick = anchorTick;
  scheduleAppStateSave();
  renderTimeline();
}

function wireEvents() {
  els.playButton.addEventListener("click", togglePlayback);
  els.stopButton.addEventListener("click", () => stopPlayback(true, state.playing));
  els.loopButton.addEventListener("click", () => {
    state.loop = !state.loop;
    scheduleAppStateSave();
    renderControls();
  });
  els.transportSettingsButton.addEventListener("click", () => {
    window.bootstrap.Modal.getOrCreateInstance(els.transportModal).show();
  });
  els.chordExtensionSelect.addEventListener("change", () => {
    const events = getSelectedChordEvents().map(({ event }) => event);
    setChordExtensionForEvents(events, els.chordExtensionSelect.value);
  });
  els.chordPlayStyleSelect.addEventListener("change", () => {
    const events = getSelectedChordEvents().map(({ event }) => event);
    const playStyle = normalizeChordPlayStyle(els.chordPlayStyleSelect.value);
    events.forEach((event) => {
      event.playStyle = playStyle;
    });
    if (events.length) {
      touchSong();
      renderTimeline();
    }
    auditionChordPlayStyle(playStyle);
  });
  els.chordPlayStylePreviewButton.addEventListener("click", () => {
    auditionChordPlayStyle(els.chordPlayStyleSelect.value);
  });
  els.defaultChordPlayStyleSelect.addEventListener("change", () => {
    const playStyle = normalizeChordPlayStyle(els.defaultChordPlayStyleSelect.value);
    state.defaultChordPlayStyle = playStyle;
    scheduleAppStateSave();
    auditionChordPlayStyle(playStyle);
  });
  els.defaultChordPlayStylePreviewButton.addEventListener("click", () => {
    auditionChordPlayStyle(els.defaultChordPlayStyleSelect.value);
  });
  els.patternOctaveDownButton.addEventListener("click", () => adjustSelectedPatternTransform("octaveShift", -1));
  els.patternOctaveUpButton.addEventListener("click", () => adjustSelectedPatternTransform("octaveShift", 1));
  els.patternPlayButton.addEventListener("click", togglePatternEditorPlayback);
  els.patternOpenFullEditorButton.addEventListener("click", openSelectedPatternInFullClipEditor);
  els.patternFullEditorTextLink.addEventListener("click", openSelectedPatternInFullClipEditor);
  els.patternGridOctaves1Radio.addEventListener("change", () => setSelectedPatternGridOctaves(1));
  els.patternGridOctaves2Radio.addEventListener("change", () => setSelectedPatternGridOctaves(2));
  els.patternGridOctaves3Radio.addEventListener("change", () => setSelectedPatternGridOctaves(3));
  els.patternGridResolution4Radio.addEventListener("change", () => setSelectedPatternGridResolution(4));
  els.patternGridResolution8Radio.addEventListener("change", () => setSelectedPatternGridResolution(8));
  els.patternGridResolution12Radio.addEventListener("change", () => setSelectedPatternGridResolution(12));
  els.patternGridResolution16Radio.addEventListener("change", () => setSelectedPatternGridResolution(16));
  els.trackOctaveDownButton.addEventListener("click", () => adjustEditingTrackOctave(-1));
  els.trackOctaveUpButton.addEventListener("click", () => adjustEditingTrackOctave(1));
  els.fileNewButton.addEventListener("click", createNewSong);
  els.fileLoadButton.addEventListener("click", showLoadSongModal);
  els.fileSaveButton.addEventListener("click", saveSongToServer);
  els.fileSaveAsButton.addEventListener("click", showSaveSongModal);
  els.fileShareButton.addEventListener("click", showShareModal);
  els.copyShareLinkButton.addEventListener("click", copyShareLink);
  els.fileExportMidiButton.addEventListener("click", showMidiExportDialog);
  els.confirmMidiExportButton.addEventListener("click", () => confirmMidiExport());
  els.midiExportNeverShowButton.addEventListener("click", () => confirmMidiExport({ remember: true }));
  els.confirmSaveSongButton.addEventListener("click", saveSongFromModal);
  els.saveSongNameInput.addEventListener("input", () => {
    clearSaveSongModalSelectionIfEdited();
    renderSaveSongDuplicateState();
  });
  els.saveSongSharedCheckbox.addEventListener("change", renderSaveSongSharedInfo);
  document.querySelectorAll("[data-load-mode]").forEach((button) => {
    button.addEventListener("click", () => {
      state.loadModalMode = button.dataset.loadMode === "shared" ? "shared" : "owned";
      renderLoadSongList(state.loadModalSongs);
    });
  });
  document.querySelectorAll("[data-save-song-sort]").forEach((button) => {
    button.addEventListener("click", () => {
      const key = button.dataset.saveSongSort || "updatedAt";
      if (state.saveModalSortKey === key) {
        state.saveModalSortDirection = state.saveModalSortDirection === "asc" ? "desc" : "asc";
      } else {
        state.saveModalSortKey = key;
        state.saveModalSortDirection = key === "updatedAt" ? "desc" : "asc";
      }
      renderSaveSongList(state.saveModalSongs);
    });
  });
  document.querySelectorAll("[data-load-song-sort]").forEach((button) => {
    button.addEventListener("click", () => {
      const key = button.dataset.loadSongSort || "updatedAt";
      if (state.loadModalSortKey === key) {
        state.loadModalSortDirection = state.loadModalSortDirection === "asc" ? "desc" : "asc";
      } else {
        state.loadModalSortKey = key;
        state.loadModalSortDirection = key === "updatedAt" ? "desc" : "asc";
      }
      renderLoadSongList(state.loadModalSongs);
    });
  });
  els.clipSearchMyButton.addEventListener("click", () => setClipSearchMode("my"));
  els.clipSearchSharedButton.addEventListener("click", () => setClipSearchMode("shared"));
  els.clipSearchBardInput.addEventListener("input", loadClipSearchResults);
  els.clipSearchTitleInput.addEventListener("input", loadClipSearchResults);
  els.clipSearchDurationSelect.addEventListener("change", loadClipSearchResults);
  els.confirmClipSearchButton?.addEventListener("click", importSelectedClipSearchResult);
  els.clipSearchModal.addEventListener("hidden.bs.modal", cancelClipAudition);
  els.trackSoundSelect.addEventListener("change", previewTrackSoundSelection);
  els.saveTrackSoundButton.addEventListener("click", saveTrackSound);
  els.tempoButton.addEventListener("click", () => {
    state.tapTempoTimes = [];
    renderTempo();
    window.bootstrap.Modal.getOrCreateInstance(els.tempoModal).show();
  });
  els.tapTempoButton.addEventListener("click", tapTempo);
  els.tempoModal.querySelectorAll("[data-tempo-preset]").forEach((button) => {
    button.addEventListener("click", () => {
      state.tapTempoTimes = [];
      setTempo(button.dataset.tempoPreset);
    });
  });
  els.tempoModal.querySelectorAll("[data-tempo-step]").forEach((button) => {
    button.addEventListener("click", () => {
      state.tapTempoTimes = [];
      setTempo(getTempo() + Number(button.dataset.tempoStep));
    });
  });
  els.transposeButton.addEventListener("click", () => window.bootstrap.Modal.getOrCreateInstance(els.transposeModal).show());
  els.transposeModal.querySelectorAll("[data-transpose]").forEach((button) => {
    button.addEventListener("click", () => transposeSong(Number(button.dataset.transpose) || 0));
  });
  els.keyButton.addEventListener("click", () => {
    renderKeySignature();
    window.bootstrap.Modal.getOrCreateInstance(els.keyModal).show();
  });
  els.keyModal.querySelectorAll("[data-key-value]").forEach((button) => {
    button.addEventListener("click", () => {
      setKeySignatureValue({ key: button.dataset.keyValue });
    });
  });
  els.keyModal.querySelectorAll("[data-quality-value]").forEach((button) => {
    button.addEventListener("click", () => {
      setKeySignatureValue({ mode: button.dataset.qualityValue });
    });
  });
  els.meterSelect.addEventListener("change", updateMeter);
  els.snapSelect.addEventListener("change", () => {
    state.snapTicks = Number(els.snapSelect.value) || 480;
    scheduleAppStateSave();
    renderTimeline();
  });
  els.chordRenderModeSelect.addEventListener("change", () => {
    state.chordRenderMode = normalizeChordRenderMode(els.chordRenderModeSelect.value);
    scheduleAppStateSave();
    renderTimeline();
  });
  els.timelineScroller.addEventListener("pointerdown", (event) => {
    if (event.target.closest(".clip")) {
      return;
    }

    const grid = event.target.closest(".row-grid");
    if (!grid) {
      if (state.previewClip) {
        clearPreviewClip();
        state.activePatternTrackId = null;
        state.activePanelTrackType = "chords";
        render();
      }
      return;
    }

    const track = state.song.tracks.find((item) => item.id === grid.dataset.trackId);
    if (!track) {
      return;
    }

    const nextTick = timelineCellStartTickFromPointer(event);
    if (!Number.isFinite(nextTick)) {
      return;
    }

    selectPreviewClip(track, nextTick);
  });
  window.addEventListener("pointerdown", clearPreviewOnOutsidePointer);
  window.addEventListener("pointermove", moveLoopRegionPointer);
  window.addEventListener("pointerup", endLoopRegionPointer);
  window.addEventListener("pointermove", moveClipPointer);
  window.addEventListener("pointerup", endClipPointer);
  window.addEventListener("keydown", handleKeyDown);
  window.addEventListener("resize", renderTimeline);
  window.addEventListener("focus", applyReturnedClipEditorHandoff);
  window.addEventListener("beforeunload", saveAppState);
  els.patternClipModal.addEventListener("hidden.bs.modal", stopPatternEditorPlayback);
}

function updateMeter() {
  const [numerator, denominator] = els.meterSelect.value.split("/").map(Number);
  state.song.timeline.timeSignatureEvents = [{ tick: 0, numerator, denominator }];
  ensureSongDuration(Math.max(totalTicks(), state.playheadTick));
  touchSong();
  render();
}

function transposeSong(semitones) {
  getChordTrack()?.events
    .filter((event) => event.eventType === "chord")
    .forEach((event) => {
      const token = Chord.tokenize(event.symbol);
      const root = token?.[0] || parseChord(event.symbol).root || "C";
      const suffix = String(event.symbol).slice(String(root).length);
      const nextRoot = chromaticRoots[(Note.chroma(root) + semitones + 1200) % 12];
      const details = parseChord(`${nextRoot}${suffix}`);
      event.symbol = details.symbol;
      event.root = details.root;
      delete event.bass;
      if (details.bass) {
        event.bass = details.bass;
      }
      event.quality = details.quality;
      event.extensions = details.extensions;
      event.voicing = details.voicing;
      event.guitarVoicingIndex = 0;
    });

  const currentKey = getKeySignature();
  const keyChroma = Note.chroma(currentKey.key);
  if (Number.isFinite(keyChroma)) {
    state.song.timeline.keySignatureEvents = [{
      tick: 0,
      key: keys.find((key) => Note.chroma(key) === (keyChroma + semitones + 1200) % 12) || currentKey.key,
      mode: currentKey.mode,
    }];
  }

  touchSong();
  render();
}

function updateTrackSourceTempo(bpm) {
  state.song.tracks.forEach((track) => {
    track.source = {
      ...(track.source || {}),
      originalTempoBpm: track.source?.originalTempoBpm || bpm,
      originalPpq: track.source?.originalPpq || PPQ,
      importedFrom: track.source?.importedFrom || "user",
    };
  });
}

function copySelectedEventsToClipboard() {
  const selectedEvents = getSelectedTimelineEvents().filter(({ event }) => isEditableTimelineClip(event));
  if (!selectedEvents.length) {
    return false;
  }

  const startTick = Math.min(...selectedEvents.map(({ event }) => event.tick));
  state.clipboard = {
    items: selectedEvents.map(({ track, event }) => ({
      trackId: track.id,
      trackType: track.type,
      offsetTick: event.tick - startTick,
      event: JSON.parse(JSON.stringify(event)),
    })),
  };
  return true;
}

function cutSelectedEventsToClipboard() {
  if (!copySelectedEventsToClipboard()) {
    return false;
  }
  deleteSelectedEvent();
  return true;
}

function pasteClipboardEvents() {
  const items = state.clipboard?.items || [];
  if (!items.length) {
    return false;
  }

  const pasteTick = findPasteTick(items, snapGrid(state.playheadTick));
  if (!Number.isFinite(pasteTick)) {
    return false;
  }

  const pastedIds = [];
  items.forEach((item) => {
    const track = state.song.tracks.find((candidate) => candidate.id === item.trackId || candidate.type === item.trackType);
    if (!track) {
      return;
    }

    const event = {
      ...JSON.parse(JSON.stringify(item.event)),
      id: makeId(item.trackType || "clip"),
      tick: pasteTick + item.offsetTick,
    };
    track.events.push(event);
    track.events.sort((a, b) => a.tick - b.tick);
    pastedIds.push(event.id);
    ensureSongDuration(event.tick + (event.durationTicks || state.snapTicks));
  });

  if (!pastedIds.length) {
    return false;
  }

  state.selectedEventIds = pastedIds;
  state.selectedEventId = pastedIds.at(-1);
  state.selectionAnchorEventId = pastedIds[0];
  state.playheadTick = Math.min(totalTicks(), Math.max(...getSelectedTimelineEvents().map(({ event }) => event.tick + (event.durationTicks || 0))));
  touchSong();
  render();
  return true;
}

function findPasteTick(items, baseTick) {
  const maxSearchTick = baseTick + (ticksPerBar() * 128);
  for (let tick = baseTick; tick <= maxSearchTick; tick += state.snapTicks) {
    if (canPasteItemsAtTick(items, tick)) {
      return tick;
    }
  }
  return null;
}

function canPasteItemsAtTick(items, tick) {
  return state.song.tracks.every((track) => {
    const pasteRanges = items
      .filter((item) => item.trackId === track.id || item.trackType === track.type)
      .map((item) => ({
        tick: tick + item.offsetTick,
        durationTicks: item.event.durationTicks || state.snapTicks,
      }));
    const ranges = track.events
      .filter(isEditableTimelineClip)
      .map((event) => ({
        tick: event.tick,
        durationTicks: event.durationTicks || state.snapTicks,
      }))
      .concat(pasteRanges)
      .sort((a, b) => a.tick - b.tick);

    return ranges.every((range, index) => {
      const next = ranges[index + 1];
      return range.tick >= 0 && (!next || range.tick + range.durationTicks <= next.tick);
    });
  });
}

function handleKeyDown(event) {
  const activeTag = document.activeElement?.tagName;
  if (["INPUT", "TEXTAREA", "SELECT"].includes(activeTag)) {
    return;
  }

  const commandKey = event.ctrlKey || event.metaKey;
  const key = event.key.toLowerCase();
  if (commandKey && key === "z") {
    const handled = event.shiftKey ? redoSongChange() : undoSongChange();
    if (handled) {
      event.preventDefault();
    }
  } else if (commandKey && key === "y") {
    if (redoSongChange()) {
      event.preventDefault();
    }
  } else if (commandKey && key === "c") {
    if (copySelectedEventsToClipboard()) {
      event.preventDefault();
    }
  } else if (commandKey && key === "x") {
    if (cutSelectedEventsToClipboard()) {
      event.preventDefault();
    }
  } else if (commandKey && key === "v") {
    if (pasteClipboardEvents()) {
      event.preventDefault();
    }
  } else if (event.code === "Space") {
    event.preventDefault();
    togglePlayback();
  } else if (event.key === "Delete" || event.key === "Backspace") {
    event.preventDefault();
    deleteSelectedEvent();
  } else if (event.key === "ArrowRight") {
    movePlayhead(state.snapTicks);
  } else if (event.key === "ArrowLeft") {
    movePlayhead(-state.snapTicks);
  } else if (event.key === "Home") {
    state.playheadTick = 0;
    clearSelection();
    scheduleAppStateSave();
    renderTimeline();
  } else if (event.key === "End") {
    state.playheadTick = totalTicks();
    clearSelection();
    scheduleAppStateSave();
    renderTimeline();
  }
}

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;")
    .replaceAll("'", "&#039;");
}

loadStoredAppState();
initializeSongHistory();
applyReturnedClipEditorHandoff();
wireEvents();
render();
loadLeaderboardSongFromUrl();
loadStockPatterns();
